<template>
  <div class="wmh-wrap">
    <v-alert v-if="!weldMappingEnabled" type="warning" text prominent class="ma-4">
      {{ $t('message.weldMapping.featureDisabled') }}
    </v-alert>

    <template v-else>
      <!-- ── header bar ───────────────────────────────────────── -->
      <div class="top">
        <button class="ib" :aria-label="$t('message.login.back')" @click="goBack">
          <v-icon size="20">mdi-chevron-left</v-icon>
        </button>

        <div class="grow">
          <h1 class="mono">{{ drawing.number || '—' }}</h1>
          <div class="sub">
            <!-- Parent number is plain text here by design; it becomes a link
                 once browse navigation exists.
                 TODO(revision): "· Rev. X" goes here. project_drawing has no
                 revision column and `tag` must not stand in for it — it is free
                 text with no ordering and cannot express "superseded". Needs a
                 drawing_revision table with welds carrying drawing_revision_id.
                 testPackage is likewise omitted: no such relation on drawings. -->
            <span v-if="drawing.parent" class="mono parent">{{ drawing.parent.number }}</span>
            <span v-if="drawing.parent"> › </span>
            <span>{{ drawing.name }}</span>
          </div>
        </div>

        <!-- Star omitted: there is no user_pin table, and pins must not be
             faked in localStorage. It returns when that table exists. -->

        <div v-if="hasPager" class="pgr">
          <button
            :aria-label="prevLabel"
            :title="siblings.prevNumber || ''"
            :disabled="siblings.index === 1"
            @click="step(siblings.prevId)"
          >
            <v-icon size="19">mdi-chevron-left</v-icon>
          </button>
          <span class="lbl">
            {{ siblings.index }}/{{ siblings.total }}
            <small>{{ $t('message.weldMapping.drawingsShort') }}</small>
          </span>
          <button
            :aria-label="nextLabel"
            :title="siblings.nextNumber || ''"
            :disabled="siblings.index === siblings.total"
            @click="step(siblings.nextId)"
          >
            <v-icon size="19">mdi-chevron-right</v-icon>
          </button>
        </div>

        <span class="pill" :class="isOnline ? 'ok' : 'off'">
          <span class="pdot" :class="isOnline ? 'g' : 'o'"></span>
          {{ isOnline ? $t('message.common.online') : $t('message.common.offline') }}
        </span>
      </div>

      <!-- WIT-IMPL-CERT-MATCH-003: «Bekreft sertifikat» / begrunnelse at
           «Ferdig sveist» (spec §2.3 steps 4–5). One dialog, two modes. -->
      <v-dialog :value="certDialog.show" max-width="520" persistent>
        <v-card>
          <v-card-title class="text-h6">
            {{ certDialog.mode === 'confirm' ? $t('message.weldMapping.certConfirmTitle') : $t('message.weldMapping.certOverrideTitle') }}
          </v-card-title>
          <v-card-text>
            <p v-if="certDialog.message" class="mb-2">{{ certDialog.message }}</p>
            <!-- WIT-FIX-RETEST-1008-001 P-C: only when a «Kan ikke verifiseres»
                 choice is on offer — the pick is stored as «Bekreftet manuelt». -->
            <p v-if="certDialog.mode === 'confirm' && certDialog.note" class="mb-2 cert-confirm-note"><small>{{ certDialog.note }}</small></p>
            <template v-if="certDialog.mode === 'confirm'">
              <v-radio-group v-model="certDialog.pick" class="mt-0">
                <v-radio v-for="o in certDialog.options" :key="o.value" :value="o.value">
                  <template #label>
                    <span>{{ o.label }} <small v-if="o.why" class="pfsel-why">{{ o.why }}</small></span>
                  </template>
                </v-radio>
              </v-radio-group>
            </template>
            <template v-else>
              <!-- WIT-FIX-WELDMAP-002 3.4: the welder's certificates, best first,
                   each with the server's own reason. -->
              <template v-if="certDialog.options.length">
                <p class="mb-1"><small>{{ $t('message.weldMapping.certRankedTitle') }}</small></p>
                <ul class="cert-ranked-list mb-2">
                  <li v-for="o in certDialog.options" :key="o.value">
                    {{ o.mark }} {{ o.label }}
                    <small v-if="o.summary" class="pfsel-why">({{ o.summary }})</small>
                    <small v-if="o.why" class="pfsel-why">— {{ o.why }}</small>
                  </li>
                </ul>
              </template>
              <!-- WIT-FIX-CERT-5355-001: the welder's short dialog has its own line. -->
              <p class="mb-2">{{ certDialog.note || (certDialog.required ? $t('message.weldMapping.certOverrideBlock') : $t('message.weldMapping.certOverrideWarn')) }}</p>
              <v-textarea
                v-model="certDialog.reason"
                :label="$t('message.weldMapping.certReasonLabel')"
                :error-messages="certDialog.error ? [certDialog.error] : []"
                rows="2"
                auto-grow
                maxlength="500"
                outlined
                dense
              />
            </template>
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn text @click="certDialogDone(false)">{{ $t('message.common.cancel') }}</v-btn>
            <v-btn color="primary" depressed @click="certDialogDone(true)">
              {{ certDialog.mode === 'confirm' ? $t('message.weldMapping.certConfirmBtn') : $t('message.weldMapping.certSignBtn') }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- lightGallery renders into this hidden node (dynamic mode). -->
      <div ref="lgContainer" style="display:none"></div>

      <!-- PDF weld documents (VT reports etc.) preview here rather than in
           lightGallery — see openPhotos() for why an iframe cannot show them. -->
      <v-dialog v-model="showPdf" scrollable fullscreen>
        <v-card>
          <v-card-title class="pa-0">
            <v-toolbar dense dark color="primary">
              <v-toolbar-title>{{ pdfTitle }}</v-toolbar-title>
              <v-spacer></v-spacer>
              <!-- Weld documents stay downloadable; the WPS sheet is
                   preview-only, so the button is omitted for it. -->
              <v-btn v-if="pdfDownloadable" icon dark :href="pdfLink" target="_blank" rel="noopener">
                <v-icon>mdi-download</v-icon>
              </v-btn>
              <!-- Close sits last, at the trailing edge: the thumb reaches it
                   without crossing the sheet, and it matches where every other
                   dismiss control in the app sits. -->
              <v-btn icon dark :aria-label="$t('message.common.close')" @click="showPdf = false; pdfLink = ''">
                <v-icon>mdi-close</v-icon>
              </v-btn>
            </v-toolbar>
          </v-card-title>
          <v-divider></v-divider>
          <v-card-text class="pa-0">
            <pdf-viewer v-if="showPdf" :fileUrl="pdfLink" />
          </v-card-text>
        </v-card>
      </v-dialog>

      <!-- ── content area: intentionally empty in this task ────── -->
      <div v-if="loadError" class="wmh-state">
        <v-alert type="error" text prominent class="ma-4">
          {{ loadError }}
          <div class="mt-2">
            <v-btn small outlined @click="load">{{ $t('message.common.retry') }}</v-btn>
          </div>
        </v-alert>
      </div>
      <!-- ── joints list ──────────────────────────────────────── -->
      <div v-else class="wmh-body">
        <!-- ── production series (WIT-PROTO-PRODSERIES-PWA-001) ─────
             Only on a series drawing; flag off / no series: absent, and the
             screen is today's. First the whole series as numbers (never a
             drawing — there is no aggregate view), then the ONE unit the
             canvas and list below are loaded for. -->
        <template v-if="series">
          <div class="ps-sum" role="group" :aria-label="$t('message.productionSeries.seriesOf') + ' ' + series.name">
            <span class="ps-sum-l">
              {{ $t('message.productionSeries.seriesOf') }}
              <b>{{ $t('message.productionSeries.nWelds', { n: seriesSummary ? seriesSummary.total_welds : 0 }) }}</b>
            </span>
            <!-- Dot + count only; the legend under the canvas names the colours. -->
            <span
              v-for="(s, k) in STATUS" :key="k"
              v-show="seriesSummaryCounts[k]"
              class="pstat"
              :style="{ background: s.color + '18', color: s.color }"
              :title="statusLabel(k)"
              :aria-label="seriesSummaryCounts[k] + ' ' + statusLabel(k)"
            >
              <i class="sdot" :class="'s-' + k">{{ s.badge }}</i>{{ seriesSummaryCounts[k] }}
            </span>
          </div>
          <div class="ps-pick">
            <span class="ps-ico"><v-icon size="19">mdi-layers-triple-outline</v-icon></span>
            <div class="ps-pick-g">
              <label for="psUnitSelect" class="ps-pick-l">
                {{ seriesPickLabel }}
                <v-icon
                  v-if="series.has_production_data" size="13" class="ps-lock"
                  :title="$t('message.productionSeries.hasProductionData')"
                  :aria-label="$t('message.productionSeries.hasProductionData')"
                >mdi-lock-outline</v-icon>
              </label>
              <!-- Native select on purpose: the OS picker opens on a phone,
                   works with gloves and scales to a 36-unit series. -->
              <div class="ps-pick-sel">
                <select id="psUnitSelect" :value="String(unitId)" @change="onUnitChange($event.target.value)">
                  <option v-for="u in seriesUnits" :key="u.id" :value="String(u.id)">{{ unitOptionText(u) }}</option>
                </select>
                <v-icon class="ps-chev" size="22">mdi-chevron-down</v-icon>
              </div>
              <span class="ps-meta">{{ $t('message.productionSeries.unitsWelds', { units: series.unit_count, welds: series.weld_count }) }}</span>
            </div>
          </div>
        </template>

        <!-- Role switch: the three-stage workshop relay. Which markers pulse
             and which action the sheet offers both follow from this. -->
        <div class="rolewrap">
          <div class="role-switch">
            <button
              v-for="(r, k) in roles" :key="k"
              class="role-btn" :class="{ active: role === k }"
              @click="setRole(k)"
            >
              <!-- Welder gets the same welding-mask mark the web app uses on
                   its drawing tree, so both surfaces read the same; the other
                   roles stay on emoji. -->
              <svg
                v-if="k === 'welder'" class="role-icon" viewBox="4 0 32 32"
                aria-hidden="true" focusable="false"
              >
                <path fill="currentColor" d="M5,15H4c-0.552,0-1-0.448-1-1v-3c0-0.552,0.448-1,1-1h1V15z M21,11H11v3h10V11z M23,10v5c0,0.551-0.449,1-1,1H10c-0.551,0-1-0.449-1-1v-5c0-0.551,0.449-1,1-1h12C22.551,9,23,9.449,23,10z M22,10H10v5h12V10z M26,18.02c0,1.317-0.13,2.631-0.388,3.922l-1.129,5.646C24.202,28.991,22.971,30,21.541,30H10.459c-1.43,0-2.661-1.009-2.942-2.412l-1.129-5.646C6.13,20.65,6,19.337,6,18.02V7c0-2.761,2.239-5,5-5h10c2.761,0,5,2.239,5,5V18.02z M24,10c0-1.103-0.897-2-2-2H10c-1.103,0-2,0.897-2,2v5c0,1.103,0.897,2,2,2h12c1.103,0,2-0.897,2-2V10z M28,10h-1v5h1c0.552,0,1-0.448,1-1v-3C29,10.448,28.552,10,28,10z"/>
              </svg>
              <span v-else class="role-emoji">{{ r.emoji }}</span>{{ $t('message.weldMapping.roles.' + k) }}
            </button>
          </div>
        </div>

        <!-- Progress strip: one pill per status that actually occurs, plus the
             joints-list toggle. The pills scroll horizontally when there are
             more than fit, so the toggle sits outside that scroller to stay
             pinned to the right edge. -->
        <div class="progress-strip">
          <div class="pstats">
            <span
              v-for="(s, k) in STATUS" :key="k"
              v-show="visibleCounts[k]"
              class="pstat"
              :style="{ background: s.color + '18', color: s.color }"
            >
              <i class="sdot" :class="'s-' + k">{{ s.badge }}</i>{{ counts[k] }} {{ statusLabel(k) }}
            </span>
            <!-- Field-registered pre-status (violet, dashed) and rejected
                 (red) — the office's backlog, always visible (spec §3). -->
            <span v-if="fieldCounts.pending" class="pstat pstat-field">
              <i class="sdot s-FIELD">F</i>{{ fieldCounts.pending }} {{ $t('message.weldMapping.field.status.pending') }}
            </span>
            <span v-if="fieldCounts.rejected" class="pstat" :style="{ background: STATUS.REPAIR.color + '18', color: STATUS.REPAIR.color }">
              <i class="sdot s-FIELD-REJ">×</i>{{ fieldCounts.rejected }} {{ $t('message.weldMapping.field.status.rejected') }}
            </span>
          </div>

          <!-- VT only. Approving a batch of welds is one inspector reading one
               drawing, so signing them together matches how the check is actually
               done. Assembly and welding stay per weld: each carries its own batch
               and certificate, and a shared signature would flatten exactly the
               per-weld traceability those steps exist to record.

               Sits at the right of the progress strip, next to the counts it acts
               on, rather than as a full-width bar above the drawing. -->
          <!-- Add weld in field: compact, in the strip before the VT bulk button. -->
          <button
            v-if="fieldPerm.can_register && !placing"
            class="fw-addbtn fw-addbtn-sm"
            :title="$t('message.weldMapping.field.addBtn')"
            @click="startPlacing"
          >
            <span class="fw-plus">+</span>{{ $t('message.weldMapping.field.addBtn') }}
          </button>
          <button
            v-if="role === 'vt' && bulkReady.length > 1"
            class="bulk-btn"
            @click="openBulk"
          >
            {{ $t('message.weldMapping.bulkSignVt', { n: bulkReady.length }) }}
          </button>

          <!-- Collapses the joints list so the drawing gets the whole screen.
               A real switch rather than a chevron: on a mounted tablet this is
               a setting the welder leaves alone, not a per-glance disclosure. -->
          <button
            class="jt-toggle"
            :class="{ on: showJoints }"
            role="switch"
            :aria-checked="String(showJoints)"
            :title="$t('message.weldMapping.toggleJoints')"
            @click="toggleJoints"
          >
            <span class="jt-label">{{ $t('message.weldMapping.joints') }}</span>
            <span class="jt-track"><span class="jt-knob"></span></span>
          </button>
        </div>

        <!-- Task hint: what this role should do next, from real counts. -->
        <div v-if="!placing" class="task-hint" :class="taskHint.tone">{{ taskHint.text }}</div>
        <!-- Placement banner replaces the hint while the welder picks a spot. -->
        <div v-else class="task-hint fw-place">
          <span>{{ $t('message.weldMapping.field.placeHint') }}</span>
          <button class="fw-place-cancel" @click="cancelPlacing">{{ $t('message.weldMapping.field.cancel') }}</button>
        </div>
        <!-- Registrations made offline, queued until the connection returns. -->
        <div v-if="outbox.length" class="task-hint fw-outbox">
          <v-icon size="16" class="ic-inherit">mdi-cloud-upload-outline</v-icon>
          {{ $t('message.weldMapping.field.queued', { n: outbox.length }) }}
          <button v-if="isOnline" class="fw-place-cancel" :disabled="syncing" @click="flushOutbox">{{ $t('message.weldMapping.field.syncNow') }}</button>
        </div>

        <!-- The whole joints panel — header, states and rows — is what the
             strip's toggle collapses, so the drawing can have the full screen
             on a small tablet. The map below is never hidden. -->
        <template v-if="showJoints">
        <div class="jlh">
          <span class="t">{{ $t('message.weldMapping.joints') }}</span>
          <span v-if="joints.length" class="t n">{{ joints.length }}</span>
        </div>

        <div v-if="jointsLoading" class="jls">
          <v-skeleton-loader v-for="n in 4" :key="n" type="list-item-two-line"></v-skeleton-loader>
        </div>

        <div v-else-if="jointsError" class="jl-state">
          <v-alert type="error" text class="ma-3">{{ jointsError }}</v-alert>
        </div>

        <div v-else-if="!unitJoints.length" class="jl-state">
          {{ $t('message.weldMapping.noJoints') }}
        </div>

        <div v-else class="jls">
          <!-- 5183 / S4: which unit the list is scoped to, and the way back to
               the whole drawing. Without it a filtered list looks like a drawing
               that has lost most of its welds. -->
          <div v-if="activeUnitId" class="jl-unit">
            <span class="jl-unit-no">{{ $t('message.weldMapping.unitScoped', { unit: activeUnitLabel }) }}</span>
            <button class="jl-unit-all" @click="clearUnitScope">
              {{ $t('message.weldMapping.unitShowAll') }}
            </button>
          </div>
          <button
            v-for="j in unitJoints"
            :key="j.id"
            class="jr"
            :class="{ sel: selected === j.id }"
            @click="openWeld(j.id)"
          >
            <span class="sdot" :class="'s-' + jointDotKey(j)" :title="jointStatusLabel(j)">
              {{ jointDotKey(j) === 'FIELD-REJ' ? '×' : jointDotKey(j) === 'FIELD' ? 'F' : (STATUS[j.status] && STATUS[j.status].badge) }}
            </span>
            <span class="jr-g">
              <span class="id">
                {{ j.display_number || j.weld_nr }}
                <!-- Decision B: a row that belongs to no unit is listed in every
                     unit view, tagged so it is never taken for this unit's. -->
                <span v-if="series && j.unit_id === null" class="ps-nounit">{{ $t('message.productionSeries.noUnit') }}</span>
                <span v-if="j.field && j.field.warnings && j.field.warnings.length" class="fw-warnflag" :title="j.field.warnings.join(' ')">&#9888; {{ $t('message.weldMapping.field.wpsDeviation') }}</span>
                <!-- WIT-IMPL-M101-PWA-001: hold welding on this weld's WPS or
                     welder, as the server reported it. Information only. -->
                <span v-if="hasHold(j)" class="fw-warnflag" :title="holdLinesFor(j).join(' ')">&#9888; {{ $t('message.weldMapping.weldingHold.flag') }}</span>
              </span>
              <span v-if="j.meta && j.meta.length" class="sm">{{ j.meta.join(' · ') }}</span>
            </span>
            <span class="jr-st" :style="{ color: jointColor(j) }">
              {{ jointStatusLabel(j) }}
              <span v-if="j.field && j.field.review !== 'approved'" class="jr-st2">{{ fieldSubLabel(j) }}</span>
            </span>
          </button>
        </div>
        </template>

        <!-- ── drawing canvas ─────────────────────────────────── -->
        <div class="dwrap" ref="dwrap">
          <!-- The canvas itself lives in WeldMapCanvas so the drawing cards on
               the overview screen can render the very same map as a static
               preview. Interaction and state stay here. -->
          <!-- canvasJoints, ikke joints: en reparert sveis og reparasjonen
               ligger på samme punkt, så begge markørene overlapper. Lista over
               viser fortsatt begge. -->
          <weld-map-canvas
            ref="canvas"
            :joints="canvasJoints"
            :drawing-file="drawingFile"
            :overlay="overlay"
            :selected="selected"
            :acts-on="roles[role].actsOn"
            :locked-ids="certLockedIds"
            :placing="placing"
            :ghost="ghost"
            movable-labels
            :drawing-id="$route.params.drawing_id"
            @select="onCanvasSelect"
            @label-move="saveLabelPosition"
            @place="onPlace"
          >
            <!-- `failed` separates a PDF we could not open from a file type
                 that genuinely has no preview — the two need different words. -->
            <template #empty="slot">
              <div class="dwrap-empty">
                <!-- Fix 06b: the opened drawing's error state carries its retry
                     too; the canvas's 60 s timeout lands here as well. -->
                <template v-if="slot && slot.failed">
                  <div>{{ $t('message.weldMapping.imageFailed') }}</div>
                  <v-btn small outlined color="primary" class="mt-3" @click="slot.retry()">
                    <v-icon left small>mdi-refresh</v-icon>{{ $t('message.retry') }}
                  </v-btn>
                </template>
                <template v-else>
                  {{ drawingFile ? $t('message.weldMapping.noPreview') : $t('message.weldMapping.noDrawingFile') }}
                </template>
              </div>
            </template>
            <template #image-error>{{ $t('message.weldMapping.imageFailed') }}</template>
          </weld-map-canvas>
        </div>

        <!-- ── legend ─────────────────────────────────────────── -->
        <div class="legend">
          <span v-for="(s, k) in STATUS" :key="k" class="legend-item">
            <i class="sdot" :class="'s-' + k">{{ s.badge }}</i>{{ statusLabel(k) }}
          </span>
          <!-- Sixth item: a queued write that has not reached the server yet.
               The marker carries the same orange dot. Nothing can be pending
               until the write path exists, so no marker shows it today — the
               key still belongs here so the legend explains the whole scheme. -->
          <span class="legend-item">
            <i class="sdot s-FIELD">F</i>{{ $t('message.weldMapping.field.legend') }}
          </span>
          <span class="legend-item">
            <i class="sdot s-FIELD-REJ">×</i>{{ $t('message.weldMapping.field.status.rejected') }}
          </span>
          <span class="legend-item">
            <i class="syncdot"></i>{{ $t('message.weldMapping.pendingSync') }}
          </span>
          <span v-if="hasMissingSymbol" class="legend-item">
            <i class="nosymdot">?</i>{{ $t('message.weldMapping.noSymbol') }}
          </span>
        </div>
      </div>

      <!-- ── joint sheet ──────────────────────────────────────── -->
      <template v-if="selectedJoint">
        <div class="scrim" @click="scrimClose"></div>
        <aside class="sheet" role="dialog" :aria-label="selectedJoint.display_number || selectedJoint.weld_nr">
          <div class="sheet-handle"></div>
          <div class="sheet-head">
            <span class="wn">{{ selectedJoint.display_number || selectedJoint.weld_nr }}</span>
            <!-- The unit is half of the weld's identity through production, so
                 it sits in the head next to the number. -->
            <span v-if="series && selectedJoint.unit_label" class="sheet-unit">{{ selectedJoint.unit_label }}</span>
            <span v-else-if="series && selectedJoint.unit_id === null" class="sheet-unit ps-nounit">{{ $t('message.productionSeries.noUnit') }}</span>
            <span class="sheet-status"
                  :style="{ background: jointColor(selectedJoint) + '18', color: jointColor(selectedJoint) }">
              {{ jointStatusLabel(selectedJoint) }}
            </span>
            <span v-if="selectedJoint.field && selectedJoint.field.review !== 'approved'" class="sheet-sub">{{ fieldSubLabel(selectedJoint) }}</span>
          </div>
          <!-- Production series (prototype v1.12): a field-added weld can be
               corrected at any time, from a row of its own under the head.
               Deletion lives in the edit sheet and only while Kartlagt. -->
          <div v-if="series && selectedJoint.field && selectedJoint.field.can_edit && !selectedJoint.field.local" class="ps-actrow">
            <button class="primary-btn btn-outline" @click="openFieldEdit">
              <v-icon size="18" class="ic-inherit">mdi-pencil-outline</v-icon> {{ $t('message.weldMapping.field.edit') }}
            </button>
          </div>

          <!-- ── field-registered weld: registration facts + office gate ── -->
          <!-- Only while pending / rejected: an approved field weld is an
               ordinary weld everywhere (spec key principle), so no field block. -->
          <div v-if="selectedJoint.field && selectedJoint.field.review !== 'approved'" class="sb fw-sec" :class="'fw-' + selectedJoint.field.review">
            <h3>
              <i class="sdot s-FIELD"></i>
              {{ $t('message.weldMapping.field.title') }}
            </h3>
            <dl class="fw-dl">
              <dt>{{ $t('message.weldMapping.field.after') }}</dt><dd>{{ selectedJoint.field.after || '—' }}</dd>
              <dt>{{ $t('message.weldMapping.field.reason') }}</dt><dd>{{ reasonLabel(selectedJoint.field.reason) }}</dd>
              <dt>{{ $t('message.weldMapping.field.joint') }}</dt><dd>{{ jointLabel(selectedJoint.field.joint) }}</dd>
              <dt>{{ $t('message.weldMapping.field.groove') }}</dt><dd>{{ grooveLabel(selectedJoint.field.groove) }}</dd>
              <dt>{{ $t('message.weldMapping.field.material') }}</dt><dd>{{ selectedJoint.field.material || '—' }}</dd>
              <dt>{{ $t('message.weldMapping.field.thickness') }}</dt><dd>{{ selectedJoint.field.thickness ? selectedJoint.field.thickness + ' mm' : '—' }}</dd>
              <dt>{{ $t('message.weldMapping.field.registered') }}</dt>
              <dd>{{ selectedJoint.field.registered_by || '—' }}<template v-if="selectedJoint.field.registered_at"> · {{ selectedJoint.field.registered_at | dt }}</template></dd>
              <template v-if="selectedJoint.field.note">
                <dt>{{ $t('message.weldMapping.comment') }}</dt><dd>{{ selectedJoint.field.note }}</dd>
              </template>
            </dl>
            <div v-if="selectedJoint.field.warnings && selectedJoint.field.warnings.length" class="fw-warn">
              <div v-for="(w, wi) in selectedJoint.field.warnings" :key="wi">&#9888;&#65039; {{ w }}</div>
            </div>
            <!-- The office's decision: who, when and their comment, in its own box. -->
            <div v-if="selectedJoint.field.review !== 'pending'" class="fw-decision" :class="'fw-decision-' + selectedJoint.field.review">
              <div class="fw-decision-head">
                <v-icon size="16" class="ic-inherit">{{ selectedJoint.field.review === 'rejected' ? 'mdi-close-circle' : 'mdi-check-circle' }}</v-icon>
                <span>{{ $t('message.weldMapping.field.decision.' + selectedJoint.field.review) }}</span>
              </div>
              <div class="fw-decision-meta">
                <b>{{ selectedJoint.field.reviewed_by || '—' }}</b>
                <template v-if="selectedJoint.field.reviewed_at"> · {{ selectedJoint.field.reviewed_at | dt }}</template>
              </div>
              <div v-if="selectedJoint.field.review_comment" class="fw-decision-comment">
                <span class="fw-decision-lbl">{{ $t('message.weldMapping.field.reviewComment') }}</span>
                {{ selectedJoint.field.review_comment }}
              </div>
            </div>
            <div v-if="selectedJoint.field.local" class="fw-warn fw-local">
              <v-icon size="14" class="ic-inherit">mdi-cloud-upload-outline</v-icon> {{ $t('message.weldMapping.field.localPending') }}
            </div>

            <v-alert v-if="fieldActError" type="error" text dense class="mt-2">{{ fieldActError }}</v-alert>

            <!-- Office review (approve / reject / remove) is done on the web
                 canvas, not in the PWA. -->
            <!-- On a series drawing edit sits in the row under the head and
                 server-side delete in the edit sheet; only a still-queued
                 offline registration is removed from here. -->
            <button v-if="!series && selectedJoint.field.can_edit && selectedJoint.field.review !== 'rejected' && !selectedJoint.field.local" class="primary-btn btn-outline fw-editbtn" @click="openFieldEdit">
              {{ $t('message.weldMapping.field.edit') }}
            </button>
            <button v-if="series ? selectedJoint.field.local : (fieldPending(selectedJoint) && (selectedJoint.field.can_edit || selectedJoint.field.local))" class="primary-btn btn-red-outline fw-editbtn" :disabled="fieldActing" @click="deleteField">
              {{ $t('message.weldMapping.field.delete') }}
            </button>
            <button v-if="fieldPending(selectedJoint)" class="primary-btn btn-outline fw-editbtn" @click="closeWeld">
              {{ $t('message.weldMapping.close') }}
            </button>
          </div>

          <!-- Until the office has approved a field weld, nothing else is
               offered on it: no WPS/trace/material/photo/weld actions. -->
          <template v-if="!fieldPending(selectedJoint)">
          <div class="kv">
            <div>
              <!-- The preview button sits on the LABEL row, right-aligned, so
                   the value line stays clean monospace like the other cells. -->
              <span class="kv-lbl">
                {{ $t('message.weldMapping.wps') }}
                <button
                  v-if="selectedJoint.wps_url"
                  class="kv-eye"
                  :title="$t('message.weldMapping.previewWps')"
                  :aria-label="$t('message.weldMapping.previewWps')"
                  @click="previewWps"
                >
                  <v-icon size="20">mdi-eye-outline</v-icon>
                </button>
              </span>
              <b>{{ selectedJoint.wps || '—' }}</b>
            </div>
            <div><span>{{ $t('message.weldMapping.process') }}</span><b>{{ selectedJoint.process || '—' }}</b></div>
            <div><span>{{ $t('message.weldMapping.filler') }}</span><b>{{ selectedJoint.filler || '—' }}</b></div>
            <div><span>{{ $t('message.weldMapping.vtLevel') }}</span><b>{{ selectedJoint.vt_level || '—' }}</b></div>
          </div>

          <!-- WIT-IMPL-M101-PWA-001: hold welding on this weld's WPS / welder,
               server-derived. The office already knows; the welder is told. -->
          <div v-if="selectedHoldLines.length" class="fw-warn">
            <div v-for="(w, wi) in selectedHoldLines" :key="'hold' + wi">&#9888;&#65039; {{ w }}</div>
            <div class="fw-warn-sub">{{ $t('message.weldMapping.weldingHold.notBlocking') }}</div>
          </div>

          <!-- Read-only NDT context (category, configuration, s, hold time,
               earliest NDT) exactly as the server resolved it; nothing is
               derived here. Absent on an older API / a non-profiled project. -->
          <div v-if="ndtRows.length" class="sb">
            <h3>{{ $t('message.weldMapping.ndtContext.title') }}</h3>
            <div class="kv">
              <div v-for="r in ndtRows" :key="r.key"><span>{{ r.label }}</span><b>{{ r.value }}</b></div>
            </div>
          </div>

          <!-- Traceability, real values only; absent stages are not invented. -->
          <div class="sb">
            <h3>{{ $t('message.weldMapping.trace') }}</h3>

            <!-- Fit-up: who, PF batch, when. The chain is what ISO 3834
                 traceability is for, so every part the row actually has is
                 shown — a bare name is not traceability. -->
            <!-- With assembly off no fit-up is ever registered, so this line is
                 driven by real fit-up work only: `filler_batch` alone would
                 otherwise print "Fitted by" for a batch the welder entered.
                 A drawing that ran assembly before the flag was turned off
                 still shows its recorded fit-up — that is as-built history. -->
            <div v-if="selectedJoint.fitter || selectedJoint.fit_up" class="mini">
              <v-icon size="14" color="#0891b2">mdi-wrench-outline</v-icon>
              <span>
                {{ $t('message.weldMapping.fittedBy') }}
                <b v-if="selectedJoint.fitter">{{ selectedJoint.fitter }}</b>
                <!-- Date sits with the name, not at the end: who-and-when is the
                     traceability fact, and trailing it after the batch pushed it
                     onto a second line where it read as unrelated. -->
                <template v-if="selectedJoint.fit_up"> · {{ selectedJoint.fit_up | dt }}</template>
                <template v-if="selectedJoint.filler_batch">
                  · {{ $t('message.weldMapping.batch') }} <b>{{ selectedJoint.filler_batch }}</b>
                </template>
              </span>
            </div>

            <!-- Weld: who, filler wire, charge/batch, when. -->
            <div v-if="selectedJoint.welder || selectedJoint.weld_end" class="mini">
              <!-- The welding-mask mark, same path as the role switch above, so
                   the welder reads as the welder on every surface. A flame said
                   "heat", not "who did this". -->
              <svg
                class="trace-icon" viewBox="4 0 32 32"
                aria-hidden="true" focusable="false"
              >
                <path fill="currentColor" d="M5,15H4c-0.552,0-1-0.448-1-1v-3c0-0.552,0.448-1,1-1h1V15z M21,11H11v3h10V11z M23,10v5c0,0.551-0.449,1-1,1H10c-0.551,0-1-0.449-1-1v-5c0-0.551,0.449-1,1-1h12C22.551,9,23,9.449,23,10z M22,10H10v5h12V10z M26,18.02c0,1.317-0.13,2.631-0.388,3.922l-1.129,5.646C24.202,28.991,22.971,30,21.541,30H10.459c-1.43,0-2.661-1.009-2.942-2.412l-1.129-5.646C6.13,20.65,6,19.337,6,18.02V7c0-2.761,2.239-5,5-5h10c2.761,0,5,2.239,5,5V18.02z M24,10c0-1.103-0.897-2-2-2H10c-1.103,0-2,0.897-2,2v5c0,1.103,0.897,2,2,2h12c1.103,0,2-0.897,2-2V10z M28,10h-1v5h1c0.552,0,1-0.448,1-1v-3C29,10.448,28.552,10,28,10z"/>
              </svg>
              <span>
                {{ $t('message.weldMapping.weldedBy') }}
                <!-- 5183 / R-3.2: the welder ID (EE-84), not the initials the
                     signature column happens to hold (EE) — the canvas and every
                     PDF name the welder by ID, and a traceability record must
                     not identify one person two ways. Falls back to the
                     signature against an older server. -->
                <b v-if="selectedJoint.welder_id || selectedJoint.welder">{{ selectedJoint.welder_id || selectedJoint.welder }}</b>
                <!-- WIT-FIX-WELDMAP-002 3.5: signed, and nobody's welder id on it. -->
                <span
                  v-if="selectedJoint.weld_end && (selectedJoint.welder_id_missing || !selectedJoint.welder_id)"
                  class="cert-verdict-chip is-warn"
                  :title="$t('message.weldMapping.welderIdMissingHint')"
                >{{ $t('message.weldMapping.welderIdMissing') }}</span>
                <!-- Same ordering as the fit-up row: who, when, then consumables. -->
                <template v-if="selectedJoint.weld_end"> · {{ selectedJoint.weld_end | dt }}</template>
                <template v-if="selectedJoint.filler">
                  · {{ $t('message.weldMapping.wire') }} <b>{{ selectedJoint.filler }}</b>
                </template>
                <template v-if="selectedJoint.batch">
                  {{ $t('message.weldMapping.batch') }} <b>{{ selectedJoint.batch }}</b>
                </template>
              </span>
            </div>

            <!-- WIT-IMPL-CERT-001 P3 (figure 5-2): the certificate the weld was
                 signed under, read-only, with the stored verdict. -->
            <div
              v-if="selectedJoint.weld_end && (csvIds(selectedJoint.certificate_refs).length || selectedJoint.cert_verdict)"
              class="cert-result mt-1"
            >
              <span class="cert-result-label">{{ certShortLabelsFor(selectedJoint) || '—' }}</span>
              <span v-if="selectedJoint.cert_verdict" class="cert-verdict-chip" :class="'is-' + certVerdictClass(selectedJoint.cert_verdict)">
                {{ certVerdictMark(selectedJoint.cert_verdict) }}
              </span>
            </div>

            <!-- VT: approved or rejected, by whom, at which level. -->
            <div v-if="selectedJoint.vt" class="mini">
              <v-icon size="14" :color="selectedJoint.status === 'REPAIR' ? '#dc2626' : '#16a34a'">
                mdi-eye-outline
              </v-icon>
              <span>
                {{ selectedJoint.status === 'REPAIR'
                   ? $t('message.weldMapping.vtRejectedBy')
                   : $t('message.weldMapping.vtBy') }}
                <b>{{ selectedJoint.vt }}</b>
                <template v-if="selectedJoint.vt_level && selectedJoint.status !== 'REPAIR'">
                  · {{ $t('message.weldMapping.level') }} <b>{{ selectedJoint.vt_level }}</b>
                </template>
              </span>
            </div>

            <!-- A weld can be WELDED with the VT step simply not done yet;
                 saying so is more useful than an empty row. -->
            <div v-else-if="selectedJoint.status === 'WELDED'" class="mini muted">
              {{ $t('message.weldMapping.vtPending') }}
            </div>

            <!-- Repair: the rejection that caused it lives on the ORIGINAL
                 weld, not on this fresh copy, so read it from repair_of.
                 Without this the chain on a repair looks empty. -->
            <template v-if="origin">
              <div class="mini">
                <v-icon size="14" color="#dc2626">mdi-eye-outline</v-icon>
                <span>
                  {{ $t('message.weldMapping.vtRejectedOn') }}
                  <b>{{ origin.weld_nr }}</b>
                  <template v-if="origin.vt_sign">
                    · {{ $t('message.weldMapping.by') }} <b>{{ origin.vt_sign }}</b>
                  </template>
                  <template v-if="origin.vt_level">
                    · {{ $t('message.weldMapping.level') }} <b>{{ origin.vt_level }}</b>
                  </template>
                </span>
              </div>
              <div v-if="originReason" class="mini">
                <v-icon size="14" color="#dc2626">mdi-alert-circle-outline</v-icon>
                <span>{{ $t('message.weldMapping.reason') }} <b>{{ originReason }}</b></span>
              </div>
            </template>

            <div v-if="!hasTrace && !origin" class="mini muted">
              {{ $t('message.weldMapping.noExecution') }}
            </div>
          </div>

          <!-- Material traceability: the plan ("fasit") from the PC weld log.
               Confirming against the physical steel is a write action and is
               not wired yet, so these are shown read-only. -->
          <div class="sb">
            <h3>
              {{ $t('message.weldMapping.materials') }}
              <!-- Same gate as Copy and row editing: materials are only
                   editable in the task mode that owns this weld's status. -->
              <button v-if="canEditMaterials" class="mat-add" @click="openMaterial(-1)">
                <v-icon size="15" class="ic-inherit">mdi-plus</v-icon>
                {{ $t('message.weldMapping.addMaterial') }}
              </button>
            </h3>
            <!-- A row is a div, not a button: it carries its own Copy button and
                 nesting a button inside a button is invalid HTML. -->
            <div
              v-for="(m, i) in (selectedJoint.materials || [])"
              :key="i"
              class="mat-row" :class="{ ro: !canEditMaterialRow(m) }"
            >
              <button
                class="mat-main" :disabled="!canEditMaterialRow(m)"
                @click="canEditMaterialRow(m) && openMaterial(i)"
              >
                <span class="mat-pos">{{ m.pos_nr || '—' }}</span>
                <span class="mat-g">
                  <span class="mat-t">{{ m.type || '—' }} · {{ m.thick || '?' }} mm</span>
                  <!-- Pos nr. and heat are both required to close the row, so
                       pos gets the same line treatment as heat: labelled value
                       when set, warning text when it is not. -->
                      <span class="mat-s" :class="{ warn: !m.heat }">
                        <template v-if="m.heat">{{ $t('message.weldMapping.heat') }} <b>{{ m.heat }}</b></template>
                        <template v-else>{{ $t('message.weldMapping.heatMissing') }}</template>
                      </span>
                  <!-- 5324-003 O3: no «Mangler pos.nr.» where the drawing's
                       material list does not use position numbers. -->
                  <span v-if="m.pos_nr || usesPosNr" class="mat-s" :class="{ warn: !m.pos_nr }">
                    <template v-if="m.pos_nr">{{ $t('message.weldMapping.posLabel') }} <b>{{ m.pos_nr }}</b></template>
                    <template v-else>{{ $t('message.weldMapping.posNrMissing') }}</template>
                  </span>
                  <!-- WPS-avvik (5170): denne siden er utenfor WPS-ens
                       gyldighetsområde. Sperrer ingenting — raden er allerede
                       registrert — men sier hvilken av dem det gjelder. -->
                  <span v-if="wpsThicknessWarning(m.thick)" class="mat-s warn">
                    &#9888;&#65039; {{ wpsThicknessWarning(m.thick) }}
                  </span>
                </span>
                <span class="mat-dot" :style="{ background: (m.heat && (m.pos_nr || !usesPosNr)) ? '#ca8a04' : '#c2410c' }"></span>
              </button>
              <!-- Duplicates the row so a repeated plate/heat can be added
                   without retyping it. Appends a new entry — the original is
                   untouched. -->
              <button
                v-if="canEditMaterials"
                class="mat-copy" :disabled="matSaving"
                :title="$t('message.weldMapping.copyMaterial')"
                :aria-label="$t('message.weldMapping.copyMaterial')"
                @click="copyMaterial(i)"
              >
                <v-icon size="16">mdi-content-copy</v-icon>
              </button>
              <v-icon v-if="canEditMaterialRow(m)" size="16" class="ic-chev">mdi-chevron-right</v-icon>
            </div>
            <div v-if="!(selectedJoint.materials || []).length" class="mini muted">
              {{ $t('message.weldMapping.noMaterials') }}
            </div>

          </div>

          <!-- Change history: every after-the-fact correction on this joint —
               material rows, filler batch, certificates, VT level — in one
               list, newest first. Its own section rather than nested under the
               materials, because it now covers more than the materials.
               Collapsed by default and hidden entirely when nothing has
               happened yet, so it costs the sheet no height on an untouched
               joint. -->
          <div v-if="weldAudit.length" class="sb">
            <button class="aud-toggle" @click="auditOpen = !auditOpen">
              <v-icon size="16" class="ic-inherit">
                {{ auditOpen ? 'mdi-chevron-down' : 'mdi-chevron-right' }}
              </v-icon>
              {{ $t('message.weldMapping.changeHistory') }}
              <span class="aud-count">{{ weldAudit.length }}</span>
            </button>

            <div v-if="auditOpen" class="aud-list">
              <div v-for="e in weldAudit" :key="e.id" class="aud-entry">
                <div class="aud-head">
                  <span class="aud-act" :class="'act-' + e.action">
                    {{ auditActionLabel(e.action) }}
                  </span>
                  <!-- What the entry was about: a material row is named by its
                       pos, a weld-log field by the field's own label. -->
                  <span class="aud-pos">{{ auditSubjectLabel(e) }}</span>
                  <span class="aud-meta">
                    {{ e.at | dt }}<template v-if="e.user"> · <b>{{ e.user }}</b></template>
                  </span>
                </div>
                <!-- One block per field that actually moved. Short values sit
                     inline as "label  old → new"; long ones (certificate lists,
                     mainly) stack instead, each side on its own labelled row —
                     inline, a wrapped certificate name left the arrow stranded
                     mid-sentence and old and new ran together. -->
                <div
                  v-for="(c, ci) in e.changes" :key="ci"
                  class="aud-chg" :class="{ stack: auditStacked(c) }"
                >
                  <span class="aud-f">{{ auditFieldLabel(c.field) }}</span>
                  <template v-if="c.from !== null && c.to !== null">
                    <span class="aud-v">
                      <span class="aud-side">
                        <span class="aud-tag">{{ $t('message.weldMapping.auditFrom') }}</span>
                        <span class="aud-vals">
                          <span
                            v-for="(pv, pi) in auditValueParts(c.field, c.from)" :key="pi"
                            class="aud-from"
                          >{{ pv }}</span>
                        </span>
                      </span>
                      <v-icon size="13" class="aud-arrow">mdi-arrow-right</v-icon>
                      <span class="aud-side">
                        <span class="aud-tag">{{ $t('message.weldMapping.auditTo') }}</span>
                        <span class="aud-vals">
                          <span
                            v-for="(pv, pi) in auditValueParts(c.field, c.to)" :key="pi"
                            class="aud-to"
                          >{{ pv }}</span>
                        </span>
                      </span>
                    </span>
                  </template>
                  <span v-else class="aud-v">
                    <span class="aud-vals">
                      <span
                        v-for="(pv, pi) in auditValueParts(c.field, c.to !== null ? c.to : c.from)"
                        :key="pi" class="aud-to"
                      >{{ pv }}</span>
                    </span>
                  </span>
                </div>
                <div v-if="!e.changes.length" class="aud-chg muted">
                  {{ $t('message.weldMapping.auditNoChange') }}
                </div>
              </div>
            </div>
          </div>

          <!-- Repair banner: VT rejected this weld; it must be re-worked and
               registered again. -->
          <div v-if="selectedJoint.status === 'REPAIR'" class="repair-note">
            <b>{{ statusLabel('REPAIR') }}<template v-if="origin && origin.revision"> · {{ origin.revision }}</template>:</b>
            <!-- The relation comes from copy_reference_id, not the "-R1"
                 suffix on the weld number. -->
            <template v-if="origin">
              {{ $t('message.weldMapping.repairOf') }} <b>{{ origin.weld_nr }}</b>.
            </template>
            <template v-if="originReason">
              {{ $t('message.weldMapping.reason') }} <b>{{ originReason }}</b>.
            </template>
            {{ $t('message.weldMapping.repairNote') }}
          </div>

          <!-- Action area, driven by role × status — the same relay the
               prototype defines. The buttons are disabled: writing to
               project_weld_log is the next task, and a button that looks live
               but silently does nothing is worse than one that says so. -->
          <div class="sb actionarea">
            <!-- Superseded original: the rework was registered as a revision, so
                 this weld is history. It reads REPAIR (the canvas does the same)
                 but the action belongs to the revision — canAct is false here,
                 so this must sit OUTSIDE that branch to be shown at all. -->
            <div v-if="superseded" class="act-note">
              {{ $t('message.weldMapping.supersededBy', { weld: superseded.weld_nr }) }}
            </div>
            <div v-else-if="fieldRejected(selectedJoint)" class="repair-note">
              <b>{{ $t('message.weldMapping.field.status.rejected') }}:</b> {{ $t('message.weldMapping.field.rejectedNote') }}
            </div>
            <template v-else-if="canAct || editMode">
              <!-- Photo capture: the shared CameraUploadModal hands back a
                   File, which we post straight to the weld row. The VT
                   inspector's photo is filed as the VT report; every other
                   role's is a plain weld-log attachment, because the vt
                   bucket is inspection evidence and feeds the as-built. -->
              <camera-upload-modal
                v-if="role !== 'fitter'"
                ref="cam"
                class="wm-cam"
                :label="$t('message.weldMapping.takePhoto')"
                file-prefix="weld"
                @upload-success="uploadPhoto"
              />
              <!-- Two ways in, same destination: the camera for a weld shot
                   taken on the spot, and the file picker for evidence that
                   already exists (a photo from the phone's gallery, a scanned
                   VT sheet). Both post through uploadPhoto. -->
              <div v-if="role !== 'fitter'" class="photo-actions">
                <button
                  class="photo-btn" :disabled="uploading" @click="$refs.cam.openModal()"
                >
                  <v-icon size="20" class="ic-inherit">mdi-camera-outline</v-icon>
                  {{ uploading ? $t('message.weldMapping.uploading') : $t('message.weldMapping.takePhoto') }}
                </button>
                <!-- Hidden input rather than a styled one: the native control
                     cannot be made to match .photo-btn, and the button is the
                     only accessible label we want announced. -->
                <input
                  ref="file"
                  type="file"
                  accept="image/*"
                  multiple
                  class="photo-file-input"
                  @change="pickPhotos"
                >
                <button
                  class="photo-btn" :disabled="uploading" @click="$refs.file.click()"
                >
                  <v-icon size="20" class="ic-inherit">mdi-image-multiple-outline</v-icon>
                  {{ uploading ? $t('message.weldMapping.uploading') : $t('message.weldMapping.uploadPhoto') }}
                </button>
              </div>

              <div v-if="visiblePhotos.length" class="photo-strip">
                <button
                  v-for="(ph, i) in visiblePhotos" :key="ph.id"
                  type="button" class="photo-thumb" :title="ph.name"
                  @click="openPhotos(i)"
                >
                  <img v-if="isImageDoc(ph)" :src="ph.downloadUrl" :alt="ph.name">
                  <span v-else class="photo-file">{{ (ph.extention || '').toUpperCase() }}</span>
                  <span v-if="ph.type !== 'weldlog'" class="photo-tag">{{ ph.type.toUpperCase() }}</span>
                </button>
              </div>

              <template v-if="role === 'fitter'">
                <!-- The filler-material batch is required: a fit-up with no
                     batch is not traceable to a lot. The certificate is
                     optional, as on the canvas, which leaves it blank when
                     unknown. Begge skriver nå de samme kolonnene som
                     nettsidens Produksjon-gruppe viser: Batch (fillerBatch /
                     charge_nr) og Sveisesertifikat (certificateRefs /
                     weldlog_expertise_id). -->
                <div class="fld">
                  <label>
                    {{ $t('message.weldMapping.batchPf') }}
                    <span class="req">· {{ $t('message.weldMapping.required') }}</span>
                  </label>
                  <!-- Nedtrekk med sveisens WPS-batcher (batchGroups: registrerte
                       + allerede brukt på samme WPS), pluss den lagrede verdien
                       og «Annen…» for en ny batch. Fritekst bare når WPS-en ikke
                       har noen batch ennå, eller etter «Annen…». Samme
                       felt-regel som Batch under sveiseren. -->
                  <select
                    v-if="batchGroups.all.length && !pfBatchTyping"
                    :value="pfBatch"
                    @change="pickBatch('pf', $event.target.value)"
                  >
                    <option value="">{{ $t('message.weldMapping.batchChargeHint') }}</option>
                    <optgroup v-if="batchGroups.wps.length" :label="$t('message.weldMapping.batchGroupWps')">
                      <option v-for="b in batchGroups.wps" :key="'w' + b" :value="b">{{ b }}</option>
                    </optgroup>
                    <option v-if="pfBatch && !batchGroups.all.includes(pfBatch)" :value="pfBatch">{{ pfBatch }}</option>
                    <option :value="batchNew">{{ $t('message.weldMapping.batchTypeNew') }}</option>
                  </select>
                  <template v-else>
                    <input
                      v-model.trim="pfBatch"
                      type="text"
                      :placeholder="$t('message.weldMapping.batchPfHint')"
                      @change="commitTypedBatch('pf')"
                    >
                    <button
                      v-if="batchGroups.all.length"
                      type="button"
                      class="batch-back"
                      @click="pfBatchTyping = false"
                    >{{ $t('message.weldMapping.batchPickFromList') }}</button>
                  </template>
                </div>
                <!-- Sertifikat (certificateRefs): samme flervalg som på nettsiden. Verdien er
                     expertise-id-er, ikke fritekst, ellers får kartleggings-
                     verktøyet en verdi det ikke kan slå opp. Lista kan være tom
                     hvis prosjektet ikke har gyldige sertifikater — da vises
                     den lagrede verdien i stedet for et tomt valg. -->
                <div class="fld">
                  <label>{{ $t('message.weldMapping.assemblyPf') }}</label>
                  <!-- Samme nedtrekk som fSelect på nettsiden: lukket felt med
                       «N valgt» og søk. Alle valg kan hakes av og på, også de
                       som allerede var lagret fra nettsiden. -->
                  <div v-if="certificates.length" ref="pfsel" class="pfsel" :class="{ open: pfOpen }">
                    <button type="button" class="pfsel-trigger" @click="togglePfOpen">
                      <span :class="{ ph: !assemblyPfIds.length }">{{ pfTriggerText }}</span>
                      <i class="pfsel-caret"></i>
                    </button>
                    <div v-if="pfOpen" class="pfsel-drop">
                      <input
                        v-model="pfSearch"
                        type="search"
                        class="pfsel-search"
                        :placeholder="$t('message.weldMapping.search')"
                      >
                      <div class="pfsel-list">
                        <div
                          v-for="c in pfFiltered" :key="c.value"
                          class="pfsel-opt"
                          :class="{ selected: assemblyPfIds.includes(c.value) }"
                          @click="pickPfCert(c.value)"
                        >
                          <span class="pfsel-box"><i></i></span>
                          <span class="pfsel-label">{{ c.label }}</span>
                        </div>
                        <div v-if="!pfFiltered.length" class="pfsel-empty">
                          {{ $t('message.weldMapping.assemblyPfNone') }}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div v-else class="fld-hint">
                    {{ assemblyPfLabels || $t('message.weldMapping.assemblyPfNone') }}
                  </div>
                </div>

                <v-alert v-if="actError" type="error" text dense class="mt-2">{{ actError }}</v-alert>

                <button
                  class="primary-btn btn-cyan"
                  :disabled="acting || patchSaving || !pfBatch"
                  @click="editMode ? savePatch() : withGapCheck(registerFitUp)"
                >
                  {{ (acting || patchSaving) ? $t('message.weldMapping.saving')
                     : (editMode ? $t('message.weldMapping.saveChanges')
                                 : $t('message.weldMapping.actFitUp')) }}
                </button>
                <div v-if="!pfBatch" class="fld-hint">
                  {{ $t('message.weldMapping.batchPfRequired') }}
                </div>
              </template>
              <template v-if="role === 'welder'">
                <!-- Welding wire + batch. The batch is what makes the weld
                     traceable to a consumable lot, so it is editable here. -->
                <div class="wirecard">
                  <div class="wire-g">
                    <div class="wire-h">{{ $t('message.weldMapping.wireBatchTitle') }}</div>
                    <div class="wire-t">{{ selectedJoint.filler || '—' }}</div>
                    <!-- "from WPS" only when the value actually came from there.
                         With no batch registered it was claiming a source that
                         does not exist and hiding that the field needs filling. -->
                    <div class="wire-s" :class="{ warn: !fillerBatch }">
                      <template v-if="fillerBatch">
                        {{ $t('message.weldMapping.batch') }} <b>{{ fillerBatch }}</b>
                        <template v-if="!batchEdit"> · {{ $t('message.weldMapping.fromWps') }}</template>
                      </template>
                      <template v-else>{{ $t('message.weldMapping.batchMissing') }}</template>
                    </div>
                  </div>
                  <button v-if="!batchEdit" class="wire-btn" @click="batchEdit = true">
                    {{ $t('message.weldMapping.change') }}
                  </button>
                </div>
                <!-- Batch: nedtrekk når WPS-en har minst én registrert tilsett-
                     batch, ellers fritekst — samme liste som kartleggings-
                     verktøyet, slik at begge viser samme valg for samme sveis. -->
                <div v-if="batchEdit" class="fld">
                  <label>{{ $t('message.weldMapping.batchCharge') }}</label>
                  <!-- Vanlig select, ikke v-select: .fld select gir allerede
                       samme ramme som tekstfeltet, mens Vuetify tegner sin egen
                       inne i den og gir boks-i-boks. -->
                  <select
                    v-if="batchGroups.all.length && !fillerBatchTyping"
                    :value="fillerBatch"
                    @change="pickBatch('filler', $event.target.value)"
                  >
                    <option value="">{{ $t('message.weldMapping.batchChargeHint') }}</option>
                    <optgroup v-if="batchGroups.wps.length" :label="$t('message.weldMapping.batchGroupWps')">
                      <option v-for="b in batchGroups.wps" :key="'w' + b" :value="b">{{ b }}</option>
                    </optgroup>
                    <option v-if="fillerBatch && !batchGroups.all.includes(fillerBatch)" :value="fillerBatch">{{ fillerBatch }}</option>
                    <option :value="batchNew">{{ $t('message.weldMapping.batchTypeNew') }}</option>
                  </select>
                  <template v-else>
                    <input
                      v-model.trim="fillerBatch"
                      type="text"
                      placeholder="T31-2205"
                      @change="commitTypedBatch('filler')"
                    >
                    <button
                      v-if="batchGroups.all.length"
                      type="button"
                      class="batch-back"
                      @click="fillerBatchTyping = false"
                    >{{ $t('message.weldMapping.batchPickFromList') }}</button>
                  </template>
                </div>

                <!-- 5183 / S18: one batch/heat per welding method. A weld welded
                     138 root + 136 fill uses TWO consumables with two heat
                     numbers, and the single field above cannot hold both — the
                     weld's filler traceability was lossy the moment it had more
                     than one process. Only shown when there IS more than one;
                     a single-method weld keeps exactly the field above. -->
                <div v-if="batchEdit && weldMethods.length > 1" class="fld method-batches">
                  <label>{{ $t('message.weldMapping.methodBatchesLabel') }}</label>
                  <div class="mb-hint">{{ $t('message.weldMapping.methodBatchesHint') }}</div>
                  <div v-for="m in weldMethods" :key="m" class="mb-row">
                    <span class="mb-method">{{ m }}</span>
                    <input
                      v-model.trim="methodBatches[m]"
                      type="text"
                      :placeholder="$t('message.weldMapping.batchCharge')"
                      :aria-label="m"
                    >
                  </div>
                </div>

                <!-- Sveisesertifikat (weldlog_expertise_id): sveiserens eget,
                     atskilt fra sammenstillingens Sammenstilling (PF). Verdien
                     er expertise-id-er, ikke fritekst. -->
                <div class="fld">
                  <label>{{ $t('message.weldMapping.weldCertificate') }}</label>
                  <!-- WIT-IMPL-CERT-MATCH-003 / WIT-FIX-WELDMAP-001 F1 (D-WELDMAP-3):
                       nobody picks a certificate in the app — the server chooses
                       among the signer's own valid certificates at «Ferdig sveist».
                       Read-only for every role; coordinators override on the web.
                       With matching off for the tenant nothing is chosen, so the
                       hint would be untrue: show the stored value or «—». -->
                  <!-- WIT-IMPL-CERT-001 P4 (D-CERT-1/11): the office — project
                       certificate scope from the server — picks as on the web:
                       grouped per welder, the verdict in each line, «Ikke
                       kvalifisert» hidden unless already chosen, and «Vis alle
                       sveisere i firmaet». Welders never get a picker (P1). -->
                  <div v-if="certOffice && !editMode && selectedJoint && !selectedJoint.weld_end" ref="certsel" class="pfsel" :class="{ open: certOpen }">
                    <button type="button" class="pfsel-trigger" @click="toggleCertOpen">
                      <span :class="{ ph: !weldCertIds.length }">{{ certTriggerText }}</span>
                      <i class="pfsel-caret"></i>
                    </button>
                    <div v-if="certOpen" class="pfsel-drop">
                      <input
                        v-model="certSearch"
                        type="search"
                        class="pfsel-search"
                        :placeholder="$t('message.weldMapping.search')"
                      >
                      <label class="pfsel-allwelders">
                        <input v-model="certShowAll" type="checkbox" @change="onShowAllWelders">
                        {{ $t('message.weldMapping.certShowAllWelders') }}
                      </label>
                      <div class="pfsel-list">
                        <div v-if="certMatchLoading" class="pfsel-empty">
                          {{ $t('message.weldMapping.certMatchChecking') }}
                        </div>
                        <template v-for="c in certOfficeOptions">
                          <div v-if="c.groupHead" :key="'h' + c.value" class="pfsel-group">{{ c.groupHead }}</div>
                          <div
                            :key="c.value"
                            class="pfsel-opt"
                            :class="{ selected: weldCertIds.includes(c.value), 'is-unverified': c.verdict && c.verdict !== 'qualified' }"
                            @click="pickWeldCert(c.value)"
                          >
                            <span class="pfsel-box"><i></i></span>
                            <span class="pfsel-label">
                              {{ c.text }}
                              <small v-if="c.why" class="pfsel-why">{{ c.why }}</small>
                            </span>
                          </div>
                        </template>
                        <div v-if="!certMatchLoading && !certOfficeOptions.length" class="pfsel-empty">
                          {{ certMatches ? $t('message.weldMapping.certNoneQualified') : $t('message.weldMapping.assemblyPfNone') }}
                        </div>
                      </div>
                    </div>
                  </div>
                  <!-- WIT-FIX-PWA-WELDERCERT-001 (Erik 09.10.2026): the welder's
                       OWN picker — only their own certificates the server did
                       not rule out for this weld, each with verdict and reason;
                       «Velges automatisk · n aktuelle» until they pick one. The
                       server built the list; nothing here can widen it. -->
                  <div v-else-if="certOwnPickerVisible" ref="certsel" class="pfsel" :class="{ open: certOpen }">
                    <button type="button" class="pfsel-trigger" @click="toggleCertOpen">
                      <span :class="{ ph: !weldCertIds.length }">{{ certOwnTriggerText }}</span>
                      <i class="pfsel-caret"></i>
                    </button>
                    <div v-if="certOpen" class="pfsel-drop">
                      <div class="pfsel-list">
                        <div class="pfsel-group">
                          {{ $t('message.weldMapping.certOwnHeading') }}<template v-if="certOwnWelderIds"> ({{ certOwnWelderIds }})</template>
                        </div>
                        <div v-if="certMatchLoading" class="pfsel-empty">
                          {{ $t('message.weldMapping.certMatchChecking') }}
                        </div>
                        <div
                          v-for="c in certOwnOptions"
                          :key="c.value"
                          class="pfsel-opt"
                          :class="{ selected: weldCertIds.includes(c.value), 'is-unverified': c.verdict && c.verdict !== 'qualified' }"
                          @click="pickWeldCert(c.value)"
                        >
                          <span class="pfsel-box"><i></i></span>
                          <span class="pfsel-label">
                            {{ c.label }}
                            <small v-if="c.summary" class="pfsel-why">{{ c.summary }}</small>
                            <span v-if="c.verdict" class="cert-verdict-chip" :class="'is-' + certVerdictClass(c.verdict)">{{ certVerdictMark(c.verdict) }}</span>
                            <small v-if="c.why" class="pfsel-why">{{ c.why }}</small>
                          </span>
                        </div>
                        <div v-if="!certMatchLoading && !certOwnOptions.length" class="pfsel-empty">
                          {{ certOwnEmptyText }}
                        </div>
                        <div v-if="!certMatchLoading && certOwnHiddenCount" class="pfsel-empty">
                          {{ $t('message.weldMapping.certOwnHidden', { n: certOwnHiddenCount }) }}
                        </div>
                      </div>
                    </div>
                  </div>
                  <!-- WIT-IMPL-CERT-001 P3 (figure 5-2): once signed, the chosen
                       certificate as one read-only card — short label and the
                       stored verdict. Before signing only the hint (5-1). -->
                  <div
                    v-else-if="selectedJoint && selectedJoint.weld_end && (weldCertIds.length || selectedJoint.cert_verdict)"
                    class="cert-result"
                  >
                    <span class="cert-result-label">{{ weldCertShortLabels || '—' }}</span>
                    <span v-if="selectedJoint.cert_verdict" class="cert-verdict-chip" :class="'is-' + certVerdictClass(selectedJoint.cert_verdict)">
                      {{ certVerdictMark(selectedJoint.cert_verdict) }}
                    </span>
                    <!-- WIT-FIX-CERT-5348-001 F2: the office's override on record
                         (the web canvas tooltip's reason / by / at). -->
                    <div v-if="certOverrideLines.length" class="cert-override-info">
                      <small v-for="(l, li) in certOverrideLines" :key="li" class="d-block">{{ l }}</small>
                    </div>
                  </div>
                  <div v-else class="fld-hint">
                    {{ weldCertShortLabels || (autoSelectEnabled === false
                      ? $t('message.weldMapping.assemblyPfNone')
                      : $t('message.weldMapping.certAutoHint')) }}
                  </div>
                </div>

                <!-- Padlock (customer choice 2026-09-28): the welder holds no
                     certificate usable on this weld, so it cannot be registered.
                     The server refuses it too; this just says so up front.
                     Only on a tenant that has set the policy to 'block'. -->
                <v-alert v-if="certLocked" type="error" text dense class="mt-2" icon="mdi-lock-outline">
                  <div class="font-weight-bold">{{ $t('message.weldMapping.certLockedTitle') }}</div>
                  <div>{{ $t('message.weldMapping.certLockedMsg') }}</div>
                  <div>{{ $t('message.weldMapping.certLockedOverride') }}</div>
                </v-alert>

                <!-- 5195 / F-4: same finding, no refusal. The welder may sign —
                     they did the work — but nothing they hold could be vouched
                     for on this weld, and the office needs to hear about it.
                     The server keeps the decision in the weld's audit trail. -->
                <!-- WIT-FIX-CERT-5348-001 F5a: the advisory fires when nothing the
                     welder holds is usable — a not_qualified finding (or no
                     certificate), never «could not be confirmed». F5b / D-CERT-9:
                     their OWN certificates, read-only, with the first failing
                     dimension, collapsed under the box. -->
                <v-alert v-else-if="certAdvisory" type="warning" text dense class="mt-2" icon="mdi-alert-outline">
                  <div class="font-weight-bold">{{ certAdvisoryText.title }}</div>
                  <div>{{ certAdvisoryText.msg }}</div>
                  <!-- WIT-FIX-CERT-5355-001 F5b (Erik 15.10, 3 c): no list of
                       failing certificates — one line says what none covers. -->
                </v-alert>

                <v-alert v-if="actError" type="error" text dense class="mt-2">{{ actError }}</v-alert>

                <button
                  class="primary-btn btn-gold"
                  :disabled="acting || patchSaving"
                  @click="editMode ? savePatch() : withGapCheck(registerWeld)"
                >
                  {{ (acting || patchSaving) ? $t('message.weldMapping.saving')
                     : (editMode
                        ? $t('message.weldMapping.saveChanges')
                        : (selectedJoint.status === 'REPAIR'
                           ? $t('message.weldMapping.actRepair')
                           : $t('message.weldMapping.actWeld'))) }}
                </button>
              </template>
              <template v-else-if="role === 'vt'">
                <!-- Reject reason picker. qa_fault_scope stores the NDT method
                     that found the fault, so the defect type goes into the
                     weld's comments — there is no defect-type column. -->
                <template v-if="rejectMode">
                  <div class="rejbox">
                    <div class="rej-t">{{ $t('message.weldMapping.rejectWhy') }}</div>
                    <div class="rej-opts">
                      <button
                        v-for="r in REJECT_REASONS" :key="r"
                        class="rej-opt" :class="{ on: rejectReason === r }"
                        @click="rejectReason = r"
                      >{{ $t('message.weldMapping.faults.' + r) }}</button>
                    </div>
                    <div class="fld mt-2">
                      <label>{{ $t('message.weldMapping.rejectNote') }}</label>
                      <input v-model.trim="rejectNote" type="text">
                    </div>
                  </div>
                  <v-alert v-if="actError" type="error" text dense class="mt-2">{{ actError }}</v-alert>
                  <button
                    class="primary-btn btn-red"
                    :disabled="acting || !rejectReason"
                    @click="vtVerdict('rej')"
                  >
                    {{ acting ? $t('message.weldMapping.saving') : $t('message.weldMapping.actVtReject') }}
                  </button>
                  <button class="primary-btn btn-outline" :disabled="acting" @click="cancelReject">
                    {{ $t('message.common.cancel') }}
                  </button>
                </template>

                <template v-else>
                  <!-- VT level (ISO 5817). Locked by default: it comes from the
                       weld row, and changing it is a deliberate act. Only
                       editable when the tenant runs the NDT matrix, the same
                       gate the PC canvas applies before writing vt_level. -->
                  <div class="vtlvl">
                    <div class="vtlvl-h">{{ $t('message.weldMapping.vtLevelIso') }}</div>
                    <div class="vtlvl-b">
                      <span class="vtlvl-badge">{{ vtLevel || '—' }}</span>
                      <div class="vtlvl-g">
                        <div class="vtlvl-n">{{ vtLevelLabel }}</div>
                        <div class="vtlvl-s">
                          <v-icon size="12" class="ic-inherit">
                            {{ levelUnlocked ? 'mdi-lock-open-variant-outline' : 'mdi-lock-outline' }}
                          </v-icon>
                          {{ levelUnlocked ? $t('message.weldMapping.vtLevelOverriding')
                                           : $t('message.weldMapping.vtLevelFromWeld') }}
                        </div>
                      </div>
                      <button
                        v-if="vtLevelEditable && !levelUnlocked"
                        class="wire-btn" @click="levelUnlocked = true"
                      >{{ $t('message.weldMapping.change') }}</button>
                    </div>
                    <div v-if="levelUnlocked" class="vtlvl-opts">
                      <button
                        v-for="l in ['B', 'C', 'D']" :key="l"
                        class="vtlvl-opt" :class="{ on: vtLevel === l }"
                        @click="vtLevel = l"
                      >
                        <b>{{ l }}</b>
                        <span>{{ $t('message.weldMapping.vtLevels.' + l) }}</span>
                      </button>
                    </div>
                  </div>

                  <v-alert v-if="actError" type="error" text dense class="mt-2">{{ actError }}</v-alert>

                  <!-- Approving or rejecting is an act of inspection, so those
                       stay out of a correction: editing VT changes the level it
                       was judged against, not the verdict. -->
                  <template v-if="!editMode">
                    <button class="primary-btn btn-green" :disabled="acting" @click="withGapCheck(() => vtVerdict('acc'))">
                      {{ acting ? $t('message.weldMapping.saving')
                         : $t('message.weldMapping.actVtOk', { level: vtLevel || '—' }) }}
                    </button>
                    <button class="primary-btn btn-red-outline" :disabled="acting" @click="startReject">
                      {{ $t('message.weldMapping.actVtReject') }}
                    </button>
                  </template>
                  <template v-else>
                    <button
                      class="primary-btn btn-green"
                      :disabled="patchSaving"
                      @click="savePatch"
                    >
                      {{ patchSaving ? $t('message.weldMapping.saving')
                                     : $t('message.weldMapping.saveChanges') }}
                    </button>
                    <button
                      class="primary-btn btn-red-outline"
                      :disabled="acting || patchSaving"
                      @click="startReject"
                    >
                      {{ $t('message.weldMapping.actVtReject') }}
                    </button>
                  </template>
                </template>
              </template>
              <div class="act-note">
                {{ editMode ? $t('message.weldMapping.editFieldHint')
                            : $t('message.weldMapping.signedAs') + (role === 'welder' && signer && signer.welder_id ? ' · ' + signer.welder_id : '') }}
              </div>
              <button v-if="editMode" class="primary-btn btn-outline" :disabled="patchSaving" @click="cancelEdit">
                {{ $t('message.weldMapping.cancel') }}
              </button>
            </template>
            <!-- Nothing for this role to do here. Two very different reasons —
                 their step is already behind the joint, or the joint has not
                 reached them yet — so the sentence carries a symbol that says
                 which: a check for done, a sand timer for waiting. Text alone
                 read as "done" in both cases. -->
            <div v-else class="noact" :class="noActionDone ? 'is-done' : 'is-wait'">
              <span class="noact-ic">
                <v-icon size="15">{{ noActionDone ? 'mdi-check' : 'mdi-timer-sand' }}</v-icon>
              </span>
              <span class="noact-tx">
                {{ $t('message.weldMapping.noAction', {
                     role: $t('message.weldMapping.roles.' + role),
                     status: statusLabel(selectedJoint.status) }) }}
              </span>
              <!-- Reopens THIS role's own registration form to correct what it
                   recorded — the same fields, laid out the same way, so editing
                   looks like registering. Only the changed values are saved, and
                   the signature/date/VT verdict are left alone. -->
              <button v-if="canPatchField" class="noact-edit" @click="startEdit">
                {{ $t('message.weldMapping.change') }}
              </button>
            </div>
            <!-- WIT-IMPL-CERT-001 P3: «Angre signering» — the welder's own
                 sign-off until VT/NDT is registered; the server decides
                 (can_undo) and checks again on the request. -->
            <template v-if="role === 'welder' && selectedJoint.weld_end && selectedJoint.can_undo && !editMode">
              <v-alert v-if="undoError" type="error" text dense class="mt-2">{{ undoError }}</v-alert>
              <button class="primary-btn btn-outline mt-2" :disabled="acting" @click="undoWeld">
                {{ acting ? $t('message.weldMapping.saving') : $t('message.weldMapping.undoSign') }}
              </button>
            </template>
          </div>

          <!-- Vises når et klikk utenfor ble avvist fordi noe er ulagret, så
               brukeren skjønner hvorfor arket ble stående. -->
          <div v-if="unsavedHint" class="unsaved-hint">{{ $t('message.weldMapping.unsavedHint') }}</div>
          <button class="sheet-close" :class="{ 'is-hinted': unsavedHint }" @click="closeWeld">{{ $t('message.weldMapping.close') }}</button>
          </template>
        </aside>
      </template>

      <!-- ── field-weld registration sheet (spec §2, prototype regSheet) ── -->
      <template v-if="fieldSheet">
        <div class="scrim" @click="closeFieldSheet"></div>
        <aside class="sheet fw-sheet" role="dialog" :aria-label="$t('message.weldMapping.field.newTitle')">
          <div class="sheet-handle"></div>
          <h2 class="fw-h2"><i class="sdot s-FIELD"></i>{{ fieldEditId ? $t('message.weldMapping.field.editTitle') : $t('message.weldMapping.field.newTitle') }}</h2>
          <p class="fw-sub">{{ $t('message.weldMapping.field.newSub') }}</p>

          <div v-if="optionsLoading" class="fld-hint">{{ $t('message.weldMapping.field.loadingOptions') }}</div>

          <div class="fw-two">
            <div class="fld" :class="{ err: fieldErr.after }">
              <label>{{ $t('message.weldMapping.field.after') }}</label>
              <select v-model="fieldForm.after" @change="onAfterChange">
                <option v-for="a in fieldOptions.after" :key="a" :value="a">{{ a }}</option>
              </select>
            </div>
            <div class="fld" :class="{ err: fieldErr.weld_nr }">
              <label>{{ $t('message.weldMapping.field.weldNr') }}</label>
              <input v-model.trim="fieldForm.weld_nr" type="text" autocomplete="off" class="mono" @input="fieldIdTouched = true">
            </div>
          </div>
          <div class="fld-hint fw-hint">{{ $t('message.weldMapping.field.idHint') }}</div>

          <div class="fld" :class="{ err: fieldErr.reason }">
            <label>{{ $t('message.weldMapping.field.reason') }}</label>
            <select v-model="fieldForm.reason">
              <option value="">{{ $t('message.weldMapping.field.pickReason') }}</option>
              <option v-for="r in fieldOptions.reasons" :key="r" :value="r">{{ reasonLabel(r) }}</option>
            </select>
          </div>

          <div class="fw-two">
            <div class="fld" :class="{ err: fieldErr.joint }">
              <label>{{ $t('message.weldMapping.field.joint') }}</label>
              <select v-model="fieldForm.joint" @change="onJointChange">
                <option value="">{{ $t('message.weldMapping.field.pick') }}</option>
                <option v-for="j in fieldOptions.joints" :key="j" :value="j">{{ jointLabel(j) }}</option>
              </select>
            </div>
            <div class="fld" :class="{ err: fieldErr.groove }">
              <label>{{ $t('message.weldMapping.field.groove') }}</label>
              <select v-model="fieldForm.groove" :disabled="!fieldForm.joint">
                <option value="">{{ fieldForm.joint ? $t('message.weldMapping.field.pickGroove') : $t('message.weldMapping.field.pickJointFirst') }}</option>
                <option v-for="g in grooveOptions" :key="g" :value="g">{{ grooveLabel(g) }}</option>
              </select>
            </div>
          </div>

          <div class="fld" :class="{ err: fieldErr.wps }">
            <label>{{ $t('message.weldMapping.wps') }}</label>
            <select v-model="fieldForm.wps" @change="runWpsCheck">
              <option value="">{{ $t('message.weldMapping.field.pickWps') }}</option>
              <option v-for="w in fieldOptions.wps" :key="w.id" :value="w.id">{{ wpsOptionLabel(w) }}</option>
            </select>
            <div v-if="selectedWps" class="fld-hint mono">{{ wpsHint(selectedWps) }}</div>
          </div>

          <div class="fw-two">
            <div class="fld" :class="{ err: fieldErr.material }">
              <label>{{ $t('message.weldMapping.field.material') }}</label>
              <select v-model="fieldForm.material" @change="runWpsCheck">
                <option value="">{{ $t('message.weldMapping.field.pick') }}</option>
                <option v-for="m in fieldOptions.materials" :key="m.type" :value="m.type">{{ m.type }}</option>
              </select>
              <!-- An empty dropdown used to be ambiguous: a register with
                   nothing in it looked exactly like one that failed to load
                   (5170/B2). The server now says which it is. -->
              <div v-if="!(fieldOptions.materials || []).length" class="fld-hint" :class="{ err: fieldOptions.materials_failed }">
                {{ fieldOptions.materials_failed
                   ? $t('message.weldMapping.field.materialsFailed')
                   : $t('message.weldMapping.noTypesInRegister') }}
              </div>
            </div>
            <div class="fld" :class="{ err: fieldErr.thickness }">
              <label>{{ $t('message.weldMapping.field.thickness') }}</label>
              <input v-model.trim="fieldForm.thickness" type="number" inputmode="decimal" min="0.5" step="0.5" placeholder="8" @input="runWpsCheck">
            </div>
          </div>

          <!-- Lengde mm: same column and rule as the length on ordinary welds
               (weld_length). Left empty, the server keeps inheriting it. -->
          <div class="fld" :class="{ err: fieldErr.weld_length }">
            <label>{{ $t('message.weldMapping.field.weldLength') }} <span class="fld-opt">({{ $t('message.weldMapping.field.optional') }})</span></label>
            <input v-model.trim="fieldForm.weld_length" type="number" inputmode="decimal" min="0" max="999999" step="0.1" placeholder="279">
          </div>

          <!-- Spool/del nr. (5170/B3): bare når tegningen har mer enn ett.
               Med ett setter serveren det selv, og et felt til i skjemaet ville
               vært et valg uten alternativer. -->
          <div v-if="spoolChoices.length > 1" class="fld" :class="{ err: fieldErr.spool_nr }">
            <label>{{ $t('message.weldMapping.field.spool') }}</label>
            <select v-model="fieldForm.spool_nr">
              <option value="">{{ $t('message.weldMapping.field.pickSpool') }}</option>
              <option v-for="sp in spoolChoices" :key="sp" :value="sp">{{ sp }}</option>
            </select>
            <div class="fld-hint">{{ $t('message.weldMapping.field.spoolHint') }}</div>
          </div>

          <!-- WPS deviation: shown live, never blocking (spec §2 step 4). -->
          <div v-if="wpsWarnings.length" class="fw-warn">
            <div v-for="(w, wi) in wpsWarnings" :key="wi">&#9888;&#65039; {{ w }}</div>
            <div class="fw-warn-sub">{{ $t('message.weldMapping.field.warnNotBlocking') }}</div>
          </div>
          <!-- WIT-IMPL-M101-PWA-001: hold welding on the chosen WPS / this
               welder, from field-weld/check. Shown live, never blocking. -->
          <div v-if="holdWarnings.length" class="fw-warn">
            <div v-for="(w, wi) in holdWarnings" :key="'hold' + wi">&#9888;&#65039; {{ w }}</div>
            <div class="fw-warn-sub">{{ $t('message.weldMapping.weldingHold.notBlocking') }}</div>
          </div>

          <div class="fld">
            <label>{{ $t('message.weldMapping.comment') }} <span class="fld-opt">({{ $t('message.weldMapping.field.optional') }})</span></label>
            <textarea v-model.trim="fieldForm.note" rows="2" :placeholder="$t('message.weldMapping.field.notePh')"></textarea>
          </div>

          <v-alert v-if="fieldSaveError" type="error" text dense class="mt-2">{{ fieldSaveError }}</v-alert>

          <div class="fw-btns fw-btns-save">
            <button class="primary-btn btn-outline" :disabled="fieldSaving" @click="closeFieldSheet">{{ $t('message.weldMapping.field.cancel') }}</button>
            <button class="primary-btn btn-violet" :disabled="fieldSaving" @click="saveField">
              {{ fieldSaving ? $t('message.weldMapping.saving') : (fieldEditId ? $t('message.weldMapping.saveChanges') : $t('message.weldMapping.field.save')) }}
            </button>
          </div>
          <!-- Production series (prototype v1.7): delete from the edit sheet,
               only while the weld carries no production data — the server
               decides (field.can_delete) and re-checks on the call. -->
          <template v-if="series && fieldEditJoint">
            <v-alert v-if="fieldActError" type="error" text dense class="mt-2">{{ fieldActError }}</v-alert>
            <button v-if="fieldEditJoint.field.can_delete" class="primary-btn btn-red-outline fw-editbtn" :disabled="fieldActing || fieldSaving" @click="deleteField">
              {{ $t('message.weldMapping.field.delete') }}
            </button>
            <div v-else class="fld-hint">{{ $t('message.weldMapping.field.deleteHasData', { status: jointStatusLabel(fieldEditJoint) }) }}</div>
          </template>
        </aside>
      </template>

      <!-- ── missing-details confirm ───────────────────────────── -->
      <!-- Shown before a single sign-off when the material traceability has
           holes. It warns, it does not block: the work was done, and the choice
           of signing now versus completing the record first belongs to the
           person holding the tablet. "No" leaves the sheet open on the very rows
           that need filling. -->
      <template v-if="gapWarn">
        <div class="scrim mat-scrim" @click="cancelGapWarn"></div>
        <aside class="sheet mat-sheet" role="dialog">
          <div class="sheet-handle"></div>
          <div class="sheet-head">
            <span class="mat-title">{{ $t('message.weldMapping.gapWarnTitle') }}</span>
            <button class="ib" @click="cancelGapWarn"><v-icon size="20">mdi-close</v-icon></button>
          </div>
          <div class="mat-sub">{{ $t('message.weldMapping.gapWarnHint') }}</div>

          <div class="gaps gaps-inline">
            <div
              v-for="row in gapWarn.rows" :key="row.index"
              class="gaps-row is-static"
            >
              <b>{{ $t('message.weldMapping.gapRow', { n: row.index + 1 }) }}</b>
              <span>{{ row.missing.map(k => $t('message.weldMapping.' + gapKey(k))).join(' · ') }}</span>
            </div>
          </div>

          <button class="primary-btn btn-gold" @click="confirmGapWarn">
            {{ $t('message.weldMapping.gapWarnYes') }}
          </button>
          <button class="primary-btn btn-outline" @click="cancelGapWarn">
            {{ $t('message.weldMapping.gapWarnNo') }}
          </button>
        </aside>
      </template>

      <!-- ── bulk sign-off sheet ───────────────────────────────── -->
      <!-- Batch and certificate are asked ONCE for the whole shift, and the
           server only writes them where the weld has none — a joint that already
           carries its own batch keeps it. -->
      <template v-if="bulkOpen">
        <div class="scrim mat-scrim" @click="bulkOpen = false"></div>
        <aside class="sheet mat-sheet" role="dialog">
          <div class="sheet-handle"></div>
          <div class="sheet-head">
            <span class="mat-title">{{ $t('message.weldMapping.bulkTitleVt') }}</span>
            <button class="ib" @click="bulkOpen = false"><v-icon size="20">mdi-close</v-icon></button>
          </div>
          <div class="mat-sub">{{ $t('message.weldMapping.bulkHint', { n: bulkReady.length }) }}</div>

          <!-- Which welds this signs, by number. Each row expands to the record
               being signed off — WPS, process, batch, certificate, materials —
               so the inspector can check what they are putting their name to
               without leaving the sheet and opening each joint. Collapsed by
               default: the list is the confirmation, the detail is the audit. -->
          <div class="bwl-h">{{ $t('message.weldMapping.bulkWeldList') }}</div>
          <div class="bwl">
            <button
              v-for="j in bulkReady" :key="j.id"
              type="button"
              class="bwl-row" :class="{ open: bulkShown === j.id }"
              :aria-expanded="String(bulkShown === j.id)"
              @click="bulkShown = bulkShown === j.id ? null : j.id"
            >
              <span class="bwl-head">
                <v-icon size="16" class="bwl-chev">
                  {{ bulkShown === j.id ? 'mdi-chevron-down' : 'mdi-chevron-right' }}
                </v-icon>
                <span class="bwl-id">{{ j.display_number || j.weld_nr }}</span>
                <span v-if="j.meta && j.meta.length" class="bwl-meta">{{ j.meta.join(' · ') }}</span>
                <span
                  class="bwl-st"
                  :style="{ color: STATUS[j.status] && STATUS[j.status].color }"
                >{{ statusLabel(j.status) }}</span>
              </span>

              <span v-if="bulkShown === j.id" class="bwl-body">
                <span v-for="d in bulkDetails(j)" :key="d.k" class="bwl-d">
                  <span class="bwl-dk">{{ d.k }}</span>
                  <span class="bwl-dv">{{ d.v }}</span>
                </span>
                <!-- Materials are rows, not a single value, so they list rather
                     than being flattened into one comma run. -->
                <span v-if="(j.materials || []).length" class="bwl-d">
                  <span class="bwl-dk">{{ $t('message.weldMapping.materials') }}</span>
                  <span class="bwl-dv">
                    <span v-for="(m, mi) in j.materials" :key="mi" class="bwl-m">
                      {{ bulkMaterialLine(m) }}
                    </span>
                  </span>
                </span>
              </span>
            </button>
          </div>

          <!-- Checked here, before anything is signed, so the inspector can back
               out and complete the record instead of finding out afterwards. -->
          <div v-if="bulkGapsPreview.length" class="gaps gaps-inline">
            <div class="gaps-h">
              <span class="gaps-t">{{ $t('message.weldMapping.bulkGapsTitle') }}</span>
            </div>
            <div class="gaps-s">{{ $t('message.weldMapping.bulkGapsHint') }}</div>
            <div
              v-for="g in bulkGapsPreview" :key="g.id"
              class="gaps-row is-static"
            >
              <b>{{ g.weld_nr }}</b>
              <span>{{ g.missing.map(k => $t('message.weldMapping.' + gapKey(k))).join(' · ') }}</span>
            </div>
          </div>

          <v-alert v-if="bulkError" type="error" text dense class="mt-2">{{ bulkError }}</v-alert>

          <button
            class="primary-btn btn-green"
            :disabled="bulkSaving"
            @click="runBulk"
          >
            {{ bulkSaving ? $t('message.weldMapping.saving')
                          : $t('message.weldMapping.bulkSignVt', { n: bulkReady.length }) }}
          </button>
          <button class="primary-btn btn-outline" :disabled="bulkSaving" @click="bulkOpen = false">
            {{ $t('message.weldMapping.cancel') }}
          </button>
        </aside>
      </template>

      <!-- ── material sheet: add / edit one base material ─────── -->
      <template v-if="matEdit">
        <div class="scrim mat-scrim" @click="matScrimClose"></div>
        <aside class="sheet mat-sheet" role="dialog">
          <div class="sheet-handle"></div>
          <div class="sheet-head">
            <span class="mat-title">
              {{ matEdit.index === -1
                 ? $t('message.weldMapping.newMaterial')
                 : $t('message.weldMapping.editMaterial') }}
            </span>
            <button class="ib" @click="closeMaterial"><v-icon size="20">mdi-close</v-icon></button>
          </div>
          <div class="mat-sub">{{ $t('message.weldMapping.materialHint') }}</div>

          <!-- Scanning is not wired: there is no QR/NFC bridge in the PWA yet,
               and a button that silently does nothing is worse than one that
               says so. Manual entry below writes the same record. -->
          <div class="scanrow">
            <button class="scanbtn primary" disabled>
              <v-icon size="26" color="#fff">mdi-qrcode-scan</v-icon>
              <span>{{ $t('message.weldMapping.scanQr') }}</span>
            </button>
            <button class="scanbtn outline" disabled>
              <v-icon size="26" color="#1d6fd6">mdi-nfc</v-icon>
              <span>{{ $t('message.weldMapping.scanNfc') }}</span>
            </button>
          </div>
          <div class="scan-note">{{ $t('message.weldMapping.scanComingNext') }}</div>

          <div class="ordiv">
            <span>{{ $t('message.weldMapping.orManual') }}</span>
          </div>

          <!-- Free text with suggestions, not a closed picker: the weld-mapping
               canvas lets engineering type a material the NDT register does not
               carry yet (attachMaterialTypeAutocomplete in
               project-advanced-weld-log.js), and a <select> here could not hold
               such a value — v-model found no matching <option>, silently reset
               the binding to '' and saved the blank back over the stored type.
               Suggestions are filtered as you type; focus with an empty field
               opens the whole register. -->
          <div class="fld mat-type-fld">
            <label>{{ $t('message.weldMapping.materialType') }}</label>
            <input
              ref="matTypeInput"
              v-model.trim="matEdit.type"
              type="text"
              autocomplete="off"
              maxlength="191"
              :placeholder="$t('message.weldMapping.selectType')"
              @focus="openTypeMenu"
              @input="openTypeMenu"
              @keydown.down.prevent="moveType(1)"
              @keydown.up.prevent="moveType(-1)"
              @keydown.enter="commitActiveType"
              @keydown.esc="closeTypeMenu"
              @beforeinput="onTypeBeforeInput"
              @click="openTypeMenu"
              @blur="onTypeBlur"
            >
            <div v-if="typeMenuOpen" class="mt-ac">
              <div v-if="!typeMatches.length" class="mt-ac-empty">
                {{ materialTypes.length
                   ? $t('message.weldMapping.noTypeMatches')
                   : $t('message.weldMapping.noTypesInRegister') }}
              </div>
              <div
                v-for="(t, i) in typeMatches"
                :key="t"
                class="mt-ac-item"
                :class="{ active: i === typeActive }"
                @mousedown.prevent="commitType(t)"
              >{{ t }}</div>
            </div>
          </div>
          <!-- Heat: always a dropdown with the drawing's Materialliste heats
               for this material type, numbers added before, and «Other…» to add
               a new one — also when the type has none yet, so adding is always
               offered. A typed number is added to the type's list
               (weld_mapping_custom_heats), shared with the web canvas. One
               registered heat is filled in when the type is set. -->
          <div class="fld">
            <label>{{ $t('message.weldMapping.heatNo') }}</label>
            <!-- :key rebuilds the select when its options change. Vue 2 sets a
                 select's `value` BEFORE patching its options, so when the list
                 arrived after the sheet opened (stored 90999 moving from the
                 unlisted option into the Materialliste group) the browser fell
                 back to «—» and the unchanged value was never re-applied. A new
                 element gets its options first, then the value. -->
            <select
              v-if="!heatTyping"
              :key="'heat|' + heatGroups.all.join('|')"
              :value="matEdit.heat"
              @change="pickHeat($event.target.value)"
            >
              <option value="">—</option>
              <optgroup v-if="heatGroups.registered.length" :label="$t('message.weldMapping.heatFromMaterialList')">
                <option v-for="h in heatGroups.registered" :key="'r' + h" :value="h">{{ h }}</option>
              </optgroup>
              <option v-for="h in heatGroups.custom" :key="'c' + h" :value="h">{{ h }}</option>
              <!-- A stored value not in the list stays selectable; ⚠ only when
                   the type has a list it is missing from. -->
              <option v-if="matEdit.heat && !heatGroups.all.includes(matEdit.heat)" :value="matEdit.heat">{{ matEdit.heat }}<template v-if="heatGroups.all.length"> &#9888;</template></option>
              <option :value="batchNew">{{ $t('message.weldMapping.heatTypeNew') }}</option>
            </select>
            <template v-else>
              <input
                v-model.trim="matEdit.heat"
                type="text"
                placeholder="H-3501"
                maxlength="191"
                @change="commitTypedHeat"
              >
              <button
                type="button"
                class="batch-back"
                @click="heatTyping = false"
              >{{ $t('message.weldMapping.batchPickFromList') }}</button>
            </template>
          </div>
          <div class="fld">
            <label>{{ $t('message.weldMapping.thickness') }}</label>
            <input v-model.trim="matEdit.thick" type="text" inputmode="decimal" placeholder="6">
          </div>
          <div class="fld">
            <label>{{ $t('message.weldMapping.posNo') }}</label>
            <input v-model.trim="matEdit.pos_nr" type="text" placeholder="SS-9, SB-1">
          </div>

          <!-- Samme gule varsel som «Ny sveis», og med samme regel: den
               advarer, den sperrer ikke. Lagre-knappen står urørt. -->
          <div v-if="matWpsWarn" class="fw-warn">
            <div>&#9888;&#65039; {{ matWpsWarn }}</div>
            <div class="fw-warn-sub">{{ $t('message.weldMapping.field.warnNotBlocking') }}</div>
          </div>

          <v-alert v-if="matError" type="error" text dense class="mt-2">{{ matError }}</v-alert>

          <button
            class="primary-btn btn-green"
            :disabled="!matValid || matSaving"
            @click="saveMaterial"
          >
            {{ matSaving ? $t('message.weldMapping.saving')
               : (matEdit.index === -1
                  ? $t('message.weldMapping.addMaterial')
                  : $t('message.weldMapping.saveChanges')) }}
          </button>
          <!-- Removing a row is a full-edit action. Opening the editor to fill a
               blank heat must not also hand out the ability to delete the row
               on a weld VT has signed off. -->
          <button
            v-if="matEdit.index !== -1 && canEditMaterials"
            class="primary-btn btn-red-outline"
            :disabled="matSaving"
            @click="deleteMaterial"
          >
            {{ $t('message.weldMapping.removeMaterial') }}
          </button>
          <!-- Samme som i skjøtearket: et avvist klikk utenfor må forklares,
               ellers ser arket bare ut til å henge fast. -->
          <div v-if="matUnsavedHint" class="unsaved-hint">{{ $t('message.weldMapping.unsavedHint') }}</div>
          <button
            class="primary-btn btn-outline"
            :class="{ 'is-hinted': matUnsavedHint }"
            :disabled="matSaving"
            @click="closeMaterial"
          >
            {{ $t('message.common.cancel') }}
          </button>
        </aside>
      </template>
    </template>
  </div>
</template>

<script>
/* eslint-disable */
import { mapGetters } from 'vuex'
import { hostAppApi } from '@/plugins/axios_settings'
import WeldMappingMixin, { readShowJoints, writeShowJoints, readUnit, writeUnit } from '@/mixins/weldMapping'
import DocumentGalleryMixin from '@/mixins/documentGallery'
import moment from 'moment'
import CameraUploadModal from '@/components/CameraUploadModal'
// The canvas is shared with the drawing overview cards; STATUS comes from it so
// the list, the sheet and the markers can never disagree on a colour.
import WeldMapCanvas, { STATUS, FIELD_COLOR, markerStyle } from '@/components/WeldMapping/WeldMapCanvas'
import '@/assets/css/weld-mapping-header.css'
// WIT-IMPL-M101-PWA-001: pure presenters for the server's NDT context and
// hold-welding warnings (src/utils/ndtContext.mjs, node-tested).
import { ndtContextRows, holdWarningLines, hasHoldWarning } from '@/utils/ndtContext.mjs'
// WIT-FIX-RETEST-1008-001: the welder's decisions over certificate-autoselect
// (blocked / auto / confirm / proceed) and the confirm options (src/utils/certSign.mjs).
import { welderCertDecision, welderReasonNeeded, confirmOptions, needsManualNote, officeReasonOutcome, advisoryCopy, dedupeRankedWhy, humaniseKey } from '@/utils/certSign.mjs'
// WIT-FIX-PWA-WELDERCERT-001: the welder's OWN picker — rows, count and empty
// state over the server's own list and its per-weld answer (src/utils/certOwnPicker.mjs).
import { ownPickerRows, ownRelevantCount, ownEmptyKey } from '@/utils/certOwnPicker.mjs'

// Offline outbox for field-weld registrations (spec §5 "Offline"). Kept in
// localStorage — the PWA has no other write queue — and drained on 'online'.
const OUTBOX_KEY = 'weldit-field-weld-outbox'
// Batch dropdown option that switches the field to free text («Annen…»).
// The heat dropdown in the material sheet uses the same sentinel.
const BATCH_NEW = '__new_batch__'
// Material types compare trimmed and case-insensitively (heat lookup).
const matTypeKey = (t) => String(t == null ? '' : t).trim().toLowerCase()
function readOutbox () {
  try { return JSON.parse(window.localStorage.getItem(OUTBOX_KEY) || '[]') } catch (e) { return [] }
}
function writeOutbox (list) {
  try { window.localStorage.setItem(OUTBOX_KEY, JSON.stringify(list)) } catch (e) {}
}
function uuid () {
  if (window.crypto && window.crypto.randomUUID) return window.crypto.randomUUID()
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
    const r = Math.random() * 16 | 0
    return (c === 'x' ? r : (r & 0x3 | 0x8)).toString(16)
  })
}

export default {
  mixins: [WeldMappingMixin, DocumentGalleryMixin],
  components: {
    'camera-upload-modal': CameraUploadModal,
    'weld-map-canvas': WeldMapCanvas,
    // Lazy, matching Documents.vue: vue-pdf pulls in the pdf.js worker, which
    // has no business in this route's bundle until a PDF is actually opened.
    'pdf-viewer': () => import('@/components/PdfViewer/PdfViewer.vue')
  },
  filters: {
    // Compact date/time for the traceability chain; the exact timestamp
    // matters for as-built documentation, so it is not abbreviated away.
    dt (v) {
      return v ? moment(v).format('DD.MM.YY HH:mm') : ''
    }
  },
  data () {
    return {
      loading: false,
      loadError: null,
      drawing: { id: null, number: '', name: '', parent: null, siblings: null },
      joints: [],
      // Production series (WIT-PROTO-PRODSERIES-PWA-001), from
      // weld-mapping-series. `series` stays null when the flag is off or the
      // drawing has no series, and the screen is then exactly today's.
      series: null,
      seriesUnits: [],
      seriesSummary: null,
      // The ONE unit the canvas and list show. The joints are loaded for it
      // server-side (?unit_id=), never filtered here.
      unitId: null,
      // Guards against an older joints response landing after a newer one
      // when the unit is switched quickly.
      jointsSeq: 0,
      // Whether the joints list is expanded. Seeded from the device preference
      // so the screen opens the way this tablet was left.
      showJoints: readShowJoints(),
      jointsLoading: false,
      jointsError: null,
      drawingFile: null,
      // Canvas overlay for this drawing; see fetchJoints().
      overlay: null,
      selected: null,
      showPdf: false,
      pdfLink: '',
      pdfDownloadable: true,
      pdfTitle: '',
      matEdit: null,
      // Utgangspunktet for materialskjemaet, som i skjøtearket.
      matBase: null,
      matUnsavedHint: false,
      materialTypes: [],
      // This drawing's Materialliste rows [{designation, heat, pos, group}] and
      // heat numbers added by hand per type — the heat choices of the sheet.
      materialListRows: [],
      customHeats: {},
      // «Other…» picked in the heat dropdown: free text until "Choose from list".
      heatTyping: false,
      // The type picked from the list (locked against typing), and the type
      // the heat was last matched to (a real change clears an unlisted heat).
      typePicked: null,
      typeOpened: '',
      typeBefore: '',
      // Pending sign-off held back by the missing-details warning: { rows, run }.
      gapWarn: null,
      // Fields acknowledged as missing on the sign-off in progress (5183 / R-3.2).
      // Cleared after each attempt so it can never ride along on the next weld.
      signedGaps: [],
      // Bulk sign-off: one action for every joint already at this role's step.
      bulkOpen: false,
      // Which weld's detail is expanded in the bulk sheet. One at a time: the
      // sheet is a confirmation, so the list has to stay scannable.
      bulkShown: null,
      bulkSaving: false,
      bulkError: null,
      // Traceability gaps reported by the last bulk sign-off. Sticky on purpose.
      // Correcting what is already recorded. The role's own registration form is
      // reused as-is, so there is no second set of inputs to keep in step — only
      // a flag, and the snapshot the form was opened with so that saving can
      // send just the fields that actually changed.
      editMode: false,
      patchOrig: null,
      patchSaving: false,
      // Material-type suggestion menu (free-text input + filtered list).
      typeMenuOpen: false,
      typeActive: -1,
      fillerBatch: '',
      batchEdit: false,
      acting: false,
      uploading: false,
      pfBatch: '',
      // Batch numbers already used on this project's welds ([{batch, wps}]),
      // offered in the Batch / Batch PF dropdowns next to the registered ones.
      projectBatches: [],
      // «Annen…» picked: the batch field is free text until the user goes back
      // to the list. One flag per field, reset whenever a joint is opened.
      pfBatchTyping: false,
      fillerBatchTyping: false,
      batchNew: BATCH_NEW,
      // Sammenstillingens sertifikat; lagres som CSV i assembly_pf, samme
      // kolonne som nettsidens «Sammenstilling (PF)» (pfNo).
      assemblyPfIds: [],
      // Sveiserens sertifikat; lagres som CSV i weldlog_expertise_id, samme
      // kolonne som nettsidens «Sveisesertifikat» (certificateRefs). Egen
      // kolonne fra sammenstillingens, så trinnene ikke overskriver hverandre.
      weldCertIds: [],
      // WIT-IMPL-CERT-MATCH-003: 'own' | 'project' (joints payload), whether
      // the server's automatic choice is available (null = not asked yet), the
      // welder's answers sent with the sign-off, and the dialog asking for them.
      certificatesScope: 'own',
      // WIT-FIX-PWA-WELDERCERT-001: own scope — 'ok' | 'no_certificates' (the
      // welder has no competence record at all, P5); the per-weld answer's
      // state and sentence from certificate-matches.
      certificatesOwnState: null,
      certOwnMatchState: null,
      // WIT-FIX-CERT-5348-001 F5b: the own-certificate list under the advisory box.
      certRankedOpen: false,
      certOwnMessage: '',
      autoSelectEnabled: null,
      // WIT-IMPL-CERT-001 P3: the viewer's profile welder id (joints payload),
      // and the «Angre signering» error.
      signer: null,
      undoError: null,
      // WIT-IMPL-CERT-001 P4: the office's certificate picker.
      certOpen: false,
      certSearch: '',
      certMatches: null,
      certMatchFor: null,
      certMatchLoading: false,
      certShowAll: false,
      tenantCerts: [],
      certSelection: null,
      certConfirmed: false,
      certOverrideReason: '',
      certDialog: { show: false, mode: 'confirm', message: '', note: '', options: [], pick: null, reason: '', required: false, error: '', resolve: null },
      // 5183 / S18: method code -> batch/heat, for a weld with more than one
      // welding process. Empty on every single-method weld, and then nothing
      // about the sign-off changes.
      methodBatches: {},
      // Valgbare sertifikater fra joints-endepunktet (samme liste som web).
      certificates: [],
      // Utgangspunktet for feltene i skjøtearket, satt når arket åpnes.
      // Brukes til å avgjøre om et klikk utenfor kan lukke uten å miste noe.
      sheetBase: null,
      unsavedHint: false,
      pfOpen: false,
      pfSearch: '',
      rejectMode: false,
      rejectReason: null,
      rejectNote: '',
      vtLevel: null,
      levelUnlocked: false,
      vtLevelEditable: false,
      // WIT-IMPL-WPS-5324-003 (O3): does this drawing's material list use
      // position numbers? true until the server says otherwise (older API).
      usesPosNr: true,
      REJECT_REASONS: ['undercut', 'porosity', 'spatter', 'fusion', 'geometry', 'other'],
      actError: null,
      matSaving: false,
      matError: null,
      // The trail is collapsed by default: it is evidence, not something the
      // field reads on every joint, and the sheet is already tall.
      auditOpen: false,
      // Status colours/badges for the strip, the joints list and the sheet. The
      // canvas owns the table; both sides read the one copy.
      STATUS,
      // The three-stage workshop relay, exactly as the prototype defines it:
      // fitter MAPPED→FITTED, welder FITTED/REPAIR→WELDED, VT WELDED→COMPLETED/REPAIR.
      // Assembly (Sammenstilling) can be switched off per drawing on the web,
      // and then there is no separate fit-up step at all: the welder does both
      // jobs and signs once. `compilation` carries that flag; ROLES/STAGES
      // below are the compilation-on relay, and the computed `roles` collapses
      // them when it is off.
      role: 'welder',
      // Satt så snart brukeren velger fane selv. Da slutter automatikken å
      // flytte fanen under føttene på hen ved neste henting.
      rolePicked: false,
      compilation: true,
      ROLES: {
        fitter: { emoji: '🔧', actsOn: ['MAPPED'] },
        welder: { emoji: '🔥', actsOn: ['FITTED', 'REPAIR'] },
        vt: { emoji: '🔍', actsOn: ['WELDED'] }
      },
      // Where each role sits in the joint's lifecycle, and where each status
      // sits. Comparing the two is what separates "your step is done" from
      // "not your turn yet" on a joint this role cannot act on.
      // REPAIR is at the welder's stage on purpose: a rejected weld goes back
      // to them, so for the fitter it still counts as past and for VT as
      // pending — which is exactly what actsOn already encodes.
      ROLE_STAGE: { fitter: 0, welder: 1, vt: 2 },
      STATUS_STAGE: { MAPPED: 0, FITTED: 1, REPAIR: 1, WELDED: 2, COMPLETED: 3 },
      // ── Field-registered welds (WeldMapping spec) ──────────────────
      FIELD_COLOR,
      // Server-side verdict on what THIS user may do; buttons follow it and
      // the server re-checks every call.
      fieldPerm: { can_register: false, can_review: false },
      // Placement mode: the next tap on the drawing is the new weld's spot.
      placing: false,
      pendingXY: null,          // { x, y, xPct, yPct, w, h } from the canvas
      ghost: null,              // temporary dashed marker
      fieldSheet: false,
      fieldEditId: null,        // weldlog id when correcting an existing field weld
      fieldForm: this.blankFieldForm(),
      fieldIdTouched: false,
      fieldErr: {},
      fieldSaving: false,
      fieldSaveError: null,
      fieldOptions: { after: [], reasons: [], joints: [], grooves: {}, wps: [], materials: [], spools: [], next_id: '' },
      optionsLoading: false,
      wpsWarnings: [],
      // WIT-IMPL-M101-PWA-001: hold-welding lines from field-weld/check
      // (`hold_warnings`, structured → text). A warning, never a block.
      holdWarnings: [],
      // The drawing's NDT profile {kind, class_axis, standard}; null on an
      // older API or a project without a profiled standard.
      ndtProfile: null,
      wpsCheckTimer: null,
      // Office gate
      reviewComment: '',
      fieldActing: false,
      fieldActError: null,
      // Offline outbox (localStorage). Each entry is a full registration
      // request with its client uuid; flushed when the connection returns.
      outbox: readOutbox(),
      syncing: false,
      // In-app notifications
      notifications: [],
      notifUnread: 0,
      notifOpen: false,
      notifTimer: null,
      notifNewestId: null,
      // Real connectivity, never hard-coded.
      isOnline: navigator.onLine,
      hostUserId: this.$cookie.get(process.env.VUE_APP_HOST_USER)
        ? parseInt(this.$cookie.get(process.env.VUE_APP_HOST_USER)) : 0
    }
  },
  computed: {
    ...mapGetters(['getHostRefApi']),
    // WIT-IMPL-M101-PWA-001: the sheet's «NDT» rows and hold lines for the
    // selected joint, straight from the server's keys.
    ndtRows () {
      const j = this.selectedJoint
      return ndtContextRows(j && j.ndt_context, (k, p) => this.$t(k, p), v => this.$options.filters.dt(v))
    },
    selectedHoldLines () {
      return this.holdLinesFor(this.selectedJoint)
    },
    // The relay as this drawing actually runs it. With assembly off the fitter
    // tab is gone and MAPPED is the welder's to act on — a weld never reaches
    // FITTED because nobody registers a fit-up, so leaving MAPPED out would
    // strand every weld with no role able to touch it.
    roles () {
      if (this.compilation) return this.ROLES
      const { fitter, ...rest } = this.ROLES
      return { ...rest, welder: { ...rest.welder, actsOn: ['MAPPED', 'FITTED', 'REPAIR'] } }
    },
    // Stage numbers matching `roles`. With assembly off MAPPED sits at the
    // welder's stage rather than a stage below it, so an untouched weld reads
    // as "waiting for you", not "someone else's step is still pending".
    roleStage () {
      return this.compilation ? this.ROLE_STAGE : { welder: 1, vt: 2 }
    },
    statusStage () {
      return this.compilation
        ? this.STATUS_STAGE
        : { ...this.STATUS_STAGE, MAPPED: 1 }
    },
    // Drives the symbol on the "no action" notice: true when this role's step
    // is already behind the open joint, false when the joint has not reached
    // them yet.
    noActionDone () {
      const j = this.selectedJoint
      if (!j) return false
      // VT's step is its signature: a weld it approved can still read WELDED
      // while the other NDT methods are signed on the web (see roleActsOn).
      if (this.role === 'vt' && j.vt) return true
      return this.isRoleStepDone(j.status, this.role)
    },
    // Sveisebildene og VT hører sammen: bildet sveiseren tar lagres som
    // 'weldlog', og VT-kontrollen jobber på det samme. De øvrige NDT-metodene
    // (MT, PT, RT, UT, PMI) lastes opp på nettsiden og skal ikke vises her —
    // uten dette filteret dukket MT-dokumenter opp i sveiserens bildestripe.
    visiblePhotos () {
      const photos = (this.selectedJoint && this.selectedJoint.photos) || []
      return photos.filter(ph => {
        const type = (ph && ph.type) || 'weldlog'
        return type === 'weldlog' || type === 'vt'
      })
    },
    // Markører på tegningen: sveiser som er erstattet av en reparasjon skjules,
    // ellers tegnes den gamle og reparasjonen oppå hverandre på samme punkt.
    // Joints-lista bruker fortsatt hele `joints`, så historikken er synlig der.
    /**
     * The unit the screen is scoped to, when the welder arrived from a
     * production-series card (5183 / S4). Null = the whole drawing, which is
     * what every caller before this got and still gets.
     */
    activeUnitId () {
      // Produksjonsserien lenker med ?unit=, 5128-plukkeren skriver ?unit_id=;
      // begge betyr det samme, og begge må treffe samme enhet her.
      const q = this.$route.query || {}
      const raw = q.unit_id || q.unit
      const id = parseInt(raw, 10)
      return Number.isFinite(id) && id > 0 ? id : null
    },
    /** The unit's own label, for the header strip. */
    activeUnitLabel () {
      if (!this.activeUnitId) return ''
      const hit = this.joints.find(j => Number(j.production_unit_id) === this.activeUnitId)
      return hit && hit.spool_nr ? hit.spool_nr : String(this.activeUnitId)
    },
    /**
     * Welds shown in the list and on the drawing.
     *
     * A production series repeats the drawing's whole weld set once per unit,
     * so without the filter every unit's markers were drawn on top of each
     * other and the status a welder saw was whichever row happened to come
     * first — R-3.2's "the drawing markers show the status of unit 01 only".
     * With a unit selected, only that unit's welds are shown; without one,
     * everything, exactly as before.
     */
    unitJoints () {
      if (!this.activeUnitId) return this.joints
      return this.joints.filter(j => Number(j.production_unit_id) === this.activeUnitId)
    },
    canvasJoints () {
      // weld_nr is only label text on the canvas, so a series row draws the
      // server's display number there (W-0001-2 in løpenummer mode).
      // Source is unitJoints, not joints: with a unit selected the drawing
      // shows only that unit's welds (5183 / R-3.2).
      return this.unitJoints
        .filter(j => !j.superseded_by)
        .map(j => {
          let m = j.display_number && j.display_number !== j.weld_nr ? { ...j, weld_nr: j.display_number } : j
          // Series unit view (web S13): each marker says THIS unit's status
          // under its number; Kartlagt needs no tag, the frame already says it.
          if (this.series && j.status && j.status !== 'MAPPED') m = { ...m, status_tag: this.statusLabel(j.status) }
          return m
        })
    },
    // Label of the unit picker: the identifier the series was created with.
    seriesPickLabel () {
      if (!this.series) return ''
      return this.series.id_mode === 'spool'
        ? this.$t('message.productionSeries.idModeSpool')
        : this.$t('message.productionSeries.idModeSeq')
    },
    // The field weld open in the edit sheet (series delete button).
    fieldEditJoint () {
      return this.fieldEditId ? (this.joints.find(j => j.id === this.fieldEditId) || null) : null
    },
    seriesSummaryCounts () {
      return (this.seriesSummary && this.seriesSummary.status_counts) || {}
    },
    // Welds the welder may not register for want of a certificate (cert_lock
    // from the server): the canvas draws a padlock on their symbol. Only on
    // the welder tab and only while the weld is still waiting for a welder.
    certLockedIds () {
      if (this.role !== 'welder') return []
      const actsOn = this.roles.welder.actsOn
      return this.canvasJoints.filter(j => j.cert_lock && actsOn.includes(j.status)).map(j => j.id)
    },
    /* WIT-IMPL-CERT-001 P1/P4 (D-CERT-1): the office — the server's project
       certificate scope (admin, welding coordinator, project leaders, granted
       users), never the dashboard's pwaIsAdmin flag. Only the office picks a
       certificate or answers a confirm / reason dialog; the server ignores
       those answers from anyone else. */
    certOffice () {
      return this.certificatesScope === 'project'
    },
    /* WIT-FIX-CERT-5355-001 D-5355-9 (Erik 15.10, 3 c): the sheet signs as
       the logged-in user, and one's own sign-off is a welder's act for every
       role — own certificates, the short dialog, the reason kept as a note.
       The office's dialogs apply only when signing for someone else. */
    certOwnSign () {
      return !this.signer || !this.signer.user_id || String(this.signer.user_id) === String(this.hostUserId)
    },
    certActsAsOffice () {
      return this.certOffice && !this.certOwnSign
    },
    /* WIT-FIX-PWA-WELDERCERT-001 (Erik 09.10.2026): the welder's OWN picker —
       the server's own list (joints `certificates`, owner or welder-id bridge),
       narrowed to what the matcher allows on THIS weld: the joint's
       cert_candidates at once, then certificate-matches with verdict and
       reason when the list opens. Shown whenever the server judged the weld
       (cert_candidates is an array); «Velges automatisk · n aktuelle» until
       the welder picks one themself. Nothing here can widen the list. */
    certOwnPickerVisible () {
      const j = this.selectedJoint
      return !this.certOffice && !this.editMode && !!j && !j.weld_end && Array.isArray(j.cert_candidates)
    },
    certOwnMatches () {
      const j = this.selectedJoint
      return j && this.certMatchFor === j.id ? this.certMatches : null
    },
    certOwnOptions () {
      const j = this.selectedJoint
      if (!j) return []
      return ownPickerRows({
        certificates: this.certificates,
        matches: this.certOwnMatches,
        candidateIds: j.cert_candidates,
        keepIds: this.weldCertIds,
        verdictText: {
          qualified: this.$t('message.weldMapping.certVerdictQualified'),
          cannot_verify: this.$t('message.weldMapping.certVerdictUnverified'),
          not_qualified: this.$t('message.weldMapping.certVerdictNotQualified')
        }
      })
    },
    certOwnCount () {
      const j = this.selectedJoint
      if (!j) return 0
      return ownRelevantCount({ certificates: this.certificates, matches: this.certOwnMatches, candidateIds: j.cert_candidates })
    },
    certOwnTriggerText () {
      if (this.weldCertIds.length) return this.certTriggerText
      return this.$t('message.weldMapping.certAutoPick', { n: this.certOwnCount })
    },
    // Own certificates the server left out for this weld (other welders' are never sent, so never counted).
    certOwnHiddenCount () {
      const shown = new Set(this.certOwnOptions.map(r => r.value))
      return (this.certificates || []).filter(c => !shown.has(String(c.value))).length
    },
    certOwnEmptyText () {
      if (this.certOwnMessage) return this.certOwnMessage
      const j = this.selectedJoint
      return ownEmptyKey({ ownState: this.certificatesOwnState, matchesState: this.certOwnMatchState }) === 'welder_id_missing'
        ? this.$t('message.weldMapping.certNoCertificates')
        : this.$t('message.weldMapping.certNoneRelevant', { weld: (j && j.weld_nr) || '' })
    },
    certOwnWelderIds () {
      return Array.from(new Set((this.certificates || []).map(c => c.welderId).filter(Boolean))).join(' / ')
    },
    certTriggerText () {
      const n = this.weldCertIds.length
      if (!n) return '—'
      if (n > 1) return this.$t('message.weldMapping.nSelected', { n })
      return this.weldCertShortLabels
    },
    /* WIT-IMPL-CERT-001 P4: the web dropdown's rule for the office
       (certMultiOptions, CERT_SCOPE 'project'): valid on the weld's day,
       grouped «Navn (sveiser-ID)», the verdict in the line, not_qualified only
       when already chosen. With «Vis alle sveisere» the company list joins,
       marked «ikke prosjektdeltaker». */
    certOfficeOptions () {
      const j = this.selectedJoint
      if (!j) return []
      const keep = this.weldCertIds.map(String)
      const base = this.certsValidOn(j.weld_end_date, keep)
      const seen = new Set(base.map(c => String(c.value)))
      const extra = this.certShowAll
        ? (this.tenantCerts || []).filter(c => !seen.has(String(c.value)))
        : []
      const m = this.certMatchFor === j.id ? this.certMatches : null
      const order = ['qualified', 'cannot_verify', 'not_qualified']
      const verdictText = {
        qualified: this.$t('message.weldMapping.certVerdictQualified'),
        cannot_verify: this.$t('message.weldMapping.certVerdictUnverified'),
        not_qualified: this.$t('message.weldMapping.certVerdictNotQualified')
      }
      const q = this.certSearch.trim().toLowerCase()
      const rows = base.concat(extra).map(c => {
        const id = String(c.value)
        const hit = m ? m[id] : null
        // P-B (WIT-FIX-RETEST-1008-001): the server answers only for the
        // certificates that can apply; one it left out is not offered unless
        // it is already on the weld. No verdicts (older server) = plain list.
        const hidden = !!m && !hit && !keep.includes(id)
        const verdict = m ? (hit && order.includes(hit.verdict) ? hit.verdict : 'cannot_verify') : null
        const welder = (c.welderName ? c.welderName + ' (' + (c.welderId || '—') + ')' : (c.welderId || '—')) +
          (c.participant === false ? ' — ' + this.$t('message.weldMapping.certNonParticipant') : '')
        const summary = (hit && hit.summary) || c.summary || ''
        return {
          value: id,
          welder,
          verdict,
          hidden,
          participant: c.participant !== false,
          text: (c.label || id) + (summary ? ' — ' + summary : '') + (verdict ? ' · ' + verdictText[verdict] : ''),
          why: (hit && (hit.why || (hit.reasons || []).join('; '))) || ''
        }
      })
        .filter(r => !r.hidden)
        .filter(r => r.verdict !== 'not_qualified' || keep.includes(r.value))
        .filter(r => !q || (r.welder + ' ' + r.text).toLowerCase().includes(q))
        .sort((a, b) => (a.welder < b.welder ? -1 : a.welder > b.welder ? 1 : order.indexOf(a.verdict) - order.indexOf(b.verdict)))
      return rows.map((r, i) => Object.assign(r, { groupHead: i === 0 || rows[i - 1].welder !== r.welder ? r.welder : '' }))
    },
    /* A company-wide pick (not a project participant) is signed only as the
       office's override with a reason — the web rule (3.7). */
    certPickNeedsOverride () {
      const listed = new Set(this.certsValidOn(null, []).map(c => String(c.value)))
      return this.weldCertIds.some(id => !listed.has(String(id)) &&
        (this.tenantCerts || []).some(c => String(c.value) === String(id)))
    },
    certLocked () {
      const j = this.selectedJoint
      return !!j && !this.editMode && this.role === 'welder' && !!j.cert_lock
    },
    /* 5195 / F-4: "nothing you hold could be vouched for on this weld", without
       the refusal. Shown in the same place as the padlock and never together
       with it — a locked weld is already saying the stronger thing. */
    certAdvisory () {
      const j = this.selectedJoint
      return !!j && !this.editMode && this.role === 'welder' && !j.cert_lock && !!j.cert_advisory
    },
    /* WIT-FIX-CERT-5348-001 F5a: the box's wording from the server's reason
       (cert_advisory_kind) — no certificate, none valid, or none qualifies
       («metode 138 mangler» from cert_uncovered_processes). */
    certAdvisoryText () {
      const j = this.selectedJoint || {}
      const c = advisoryCopy({ kind: j.cert_advisory_kind, uncovered: j.cert_uncovered_processes, ownState: this.certificatesOwnState, reason: j.cert_advisory_reason })
      const title = this.$t('message.weldMapping.certNotQualifiedTitle')
      // WIT-FIX-CERT-5355-001 F5a: the server's decisive line («Ingen av dine
      // sertifikater dekker 316L (materialgruppe 8).») + how to go on.
      if (c.key === 'reason') return { title, msg: c.reason + ' ' + this.$t('message.weldMapping.certSignWithReasonHint') }
      if (c.key === 'no_certificates') return { title, msg: this.$t('message.weldMapping.certNoCertificates') }
      if (c.key === 'none_valid') return { title, msg: this.$t('message.weldMapping.certNoValidMsg') }
      return {
        title,
        msg: this.$t('message.weldMapping.certNotQualifiedMsg', {
          codes: c.codes ? this.$t('message.weldMapping.certMissingProcess', { codes: c.codes }) : ''
        })
      }
    },
    // F2: «Begrunnelse / Av / Når» of a stored override, for the signed card.
    certOverrideLines () {
      const o = this.selectedJoint && this.selectedJoint.cert_override
      if (!o) return []
      return [
        // WIT-FIX-CERT-5355-001 D-5355-8: the welder's note, not an override.
        o.kind === 'note' ? this.$t('message.weldMapping.auditField.certUnqualifiedSign') : '',
        o.reason ? this.$t('message.weldMapping.certOverrideTipReason', { reason: o.reason }) : '',
        o.by ? this.$t('message.weldMapping.certOverrideTipBy', { by: o.by }) : '',
        o.at ? this.$t('message.weldMapping.certOverrideTipAt', { at: o.at }) : ''
      ].filter(Boolean)
    },
    // Fix 06: the canvas tags a symbol-less weld with "?" only on a drawing
    // that uses symbols; the legend explains the tag under the same rule.
    hasMissingSymbol () {
      const placed = this.canvasJoints.filter(j => j.x !== null && j.y !== null)
      const styles = placed.map(markerStyle)
      return styles.includes('symbol') && styles.includes('box')
    },
    // Batch-valgene for den valgte sveisens WPS, felles kilde for begge
    // batch-feltene — som nettsidens batchCellFor().
    wpsBatches () {
      return ((this.selectedJoint && this.selectedJoint.filler_batches) || [])
        .map(b => String(b))
        .filter(Boolean)
    },
    // Batch-valgene for den valgte sveisens WPS: registrerte tilsett-batcher +
    // batcher allerede brukt på samme WPS i prosjektet. Registrerte tilsett
    // mangler ofte eller står uten batchnummer, og da var nedtrekket tomt selv
    // om verkstedet har skrevet de samme batchene hele tiden. Batcher fra andre
    // WPS-er vises bevisst ikke — de hører til en annen prosedyre.
    /**
     * Per-method batches to send with a sign-off (5183 / S18), or null when the
     * weld has a single method and the plain `filler_batch` field is the whole
     * answer. Null rather than [] on purpose: an empty array would tell the
     * server to CLEAR a record the app was never showing.
     */
    methodBatchPayload () {
      if (this.weldMethods.length < 2) return null
      const out = this.weldMethods
        .map(m => ({ method: m, filler: '', batch: String(this.methodBatches[m] || '').trim() }))
        .filter(x => x.batch !== '')
      return out.length ? out : null
    },
    /** The welding methods on the open weld, as the server parsed them. */
    weldMethods () {
      const j = this.selectedJoint
      return (j && Array.isArray(j.methods)) ? j.methods : []
    },
    batchGroups () {
      const key = (this.selectedJoint && this.selectedJoint.wps_key) || ''
      const wps = this.wpsBatches.slice()
      if (key) {
        ;(this.projectBatches || []).forEach(p => {
          const b = String((p && p.batch) || '').trim()
          if (b && !wps.includes(b) && Array.isArray(p.wps) && p.wps.includes(key)) wps.push(b)
        })
      }
      return { wps, all: wps }
    },
    // Lukket felt viser første valgte, så «N valgt» — fSelect med
    // numDisplayed: 1 / overflowText: '{n} valgt' på nettsiden.
    pfTriggerText () {
      const n = this.assemblyPfIds.length
      if (!n) return '—'
      if (n > 1) return this.$t('message.weldMapping.nSelected', { n })
      return this.assemblyPfLabels
    },
    pfFiltered () {
      const list = this.certsValidOn(null, this.assemblyPfIds)
      const q = this.pfSearch.trim().toLowerCase()
      if (!q) return list
      return list.filter(c => String(c.label).toLowerCase().includes(q))
    },
    // Lesbar visning av valgte sertifikater når lista ikke kan hentes: uten
    // den ville feltet vist rå id-er (eller ingenting) selv om noe er valgt.
    assemblyPfLabels () {
      return this.certLabels(this.assemblyPfIds)
    },
    weldCertLabels () {
      return this.certLabels(this.weldCertIds)
    },
    /* WIT-IMPL-CERT-001 P3: «EV84 · ISO 9606-1 · 138/136 · BW · gr 8 · gyldig til …»
       — the server's short label (offer list shortLabel, or the sign
       response's cert_label), falling back to the plain label. */
    weldCertShortLabels () {
      const j = this.selectedJoint
      if (j && j.cert_label && this.csvIds(j.certificate_refs).join(',') === this.weldCertIds.join(',')) return j.cert_label
      return this.weldCertIds
        .map(id => {
          const hit = (this.certificates || []).find(c => String(c.value) === String(id))
          return hit ? (hit.shortLabel || hit.label) : id
        })
        .join('; ')
    },
    siblings () {
      return this.drawing.siblings || {}
    },
    hasPager () {
      return !!this.drawing.siblings && this.drawing.siblings.total > 1
    },
    prevLabel () {
      const n = this.siblings.prevNumber
      return n
        ? this.$t('message.weldMapping.prevDrawing', { number: n })
        : this.$t('message.weldMapping.firstDrawing')
    },
    nextLabel () {
      const n = this.siblings.nextNumber
      return n
        ? this.$t('message.weldMapping.nextDrawing', { number: n })
        : this.$t('message.weldMapping.lastDrawing')
    },
    vtLevelLabel () {
      return this.vtLevel
        ? this.$t('message.weldMapping.vtLevels.' + this.vtLevel)
        : this.$t('message.weldMapping.vtLevelNotSet')
    },
    selectedJoint () {
      return this.joints.find(j => j.id === this.selected) || null
    },
    /**
     * The open joint's change trail, newest first — material rows and weld-log
     * field corrections in one list.
     */
    weldAudit () {
      const j = this.selectedJoint
      // Server-ordered newest first; the `dt` filter formats each timestamp in
      // the template, the same way the traceability chain above does.
      return (j && j.material_audit) || []
    },
    /** Spool/del nr. i bruk på tegningen. Tom eller én: serveren setter den
     *  selv, og skjemaet spør ikke. Flere: sveiseren må velge (D-5170-3). */
    spoolChoices () {
      return (this.fieldOptions && this.fieldOptions.spools) || []
    },
    matValid () {
      const m = this.matEdit
      // 5324-003 O3: pos nr. only where the drawing's material list uses it.
      return !!(m && m.type && m.heat && m.thick && (m.pos_nr || !this.usesPosNr))
    },
    /** WPS-avvik for tykkelsen som skrives inn nå. Bevisst IKKE en del av
     *  matValid: et avvik skal registreres, ikke hindres (5170/B1). */
    matWpsWarn () {
      return this.wpsThicknessWarning(this.matEdit && this.matEdit.thick)
    },
    // Correcting a recorded value is not a sign-off, so it is not gated on
    // whose turn it is — but a superseded original is history and its rework
    // lives on the revision row, so that stays closed.
    canPatchField () {
      const j = this.selectedJoint
      if (!j || j.superseded_by) return false
      // Only offer an edit where this role has something of its own recorded —
      // the form it would reopen writes that step's fields, so there is nothing
      // to correct on a step that has not run.
      if (this.role === 'fitter') return !!(j.fitter || j.fit_up)
      if (this.role === 'welder') return !!(j.welder || j.weld_end)
      // VT can correct the LEVEL it judged against, not the verdict itself —
      // approve/reject stays an act of inspection. The level is only a field
      // where the project runs the NDT matrix, the same gate the canvas uses.
      if (this.role === 'vt') return !!j.vt && this.vtLevelEditable
      return false
    },
    // The joints this role may sign right now. Drives both the button's count
    // and what gets sent, so the two can never disagree.
    bulkReady () {
      return this.joints.filter(j => this.roleActsOn(this.role, j))
    },
    // Which of the reused form's fields the user actually touched. Saving sends
    // only these, so an untouched field is never rewritten with its own value.
    patchChanged () {
      if (!this.editMode || !this.patchOrig) return []
      const out = []
      const o = this.patchOrig
      // Only this role's own fields. The two forms bind different models —
      // the fitter's batch/certificate are pfBatch + assemblyPfIds, the
      // welder's are fillerBatch + weldCertIds — so comparing the other
      // role's pair would both miss real edits and send fields this form
      // never showed.
      if (this.role === 'fitter') {
        if (String(this.pfBatch || '') !== o.batch_pf) out.push('batch_pf')
        // Certificate ids compare as a set: reordering the picks is not a change.
        if (this.assemblyPfIds.slice().sort().join(',') !== o.assembly_pf) out.push('assembly_pf')
      } else if (this.role === 'welder') {
        if (String(this.fillerBatch || '') !== o.filler_batch) out.push('filler_batch')
        if (this.weldCertIds.slice().sort().join(',') !== o.certificate_refs) out.push('certificate_refs')
      } else if (this.role === 'vt') {
        if (String(this.vtLevel || '') !== o.vt_level) out.push('vt_level')
      }
      return out
    },
    // Suggestions for the material-type field. An empty field offers the whole
    // register; typing filters it by substring, the same rule the canvas' menu
    // uses. A value that is not in the register still stands — the field is
    // free text and the list only suggests.
    // The drawing's Materialliste designations come first (a row described
    // only by text is not in the register), then the register.
    // A picked (locked) type lists everything, so another can be chosen.
    typeMatches () {
      const all = []
      ;(this.materialListRows || []).forEach(r => {
        const d = String((r && r.designation) || '').trim()
        if (d && !all.includes(d)) all.push(d)
      })
      this.materialTypes.forEach(t => { if (t && !all.includes(t)) all.push(t) })
      const q = ((this.matEdit && this.matEdit.type) || '').trim().toLowerCase()
      if (!q || this.typeLocked) return all
      return all.filter(t => String(t).toLowerCase().includes(q))
    },
    typeLocked () {
      const t = this.matEdit && this.matEdit.type
      if (!t) return false
      if (t === this.typePicked) return true
      if (t !== this.typeOpened) return false
      return this.materialTypes.includes(t)
        || (this.materialListRows || []).some(r => String((r && r.designation) || '') === t)
    },
    // Heat choices for the material type in the sheet: the drawing's
    // Materialliste heats for that type, then numbers added by hand (canvas
    // «+» or «Other…» here). Types match trimmed and case-insensitively, the
    // same rule as the web canvas (allHeatOptionsFor).
    heatGroups () {
      const key = matTypeKey(this.matEdit && this.matEdit.type)
      const registered = []
      const custom = []
      if (key) {
        ;(this.materialListRows || []).forEach(r => {
          const h = String((r && r.heat) || '').trim()
          if (h && matTypeKey(r.designation) === key && !registered.includes(h)) registered.push(h)
        })
        const map = this.customHeats || {}
        Object.keys(map).forEach(k => {
          if (matTypeKey(k) !== key || !Array.isArray(map[k])) return
          map[k].forEach(v => {
            const h = String(v == null ? '' : v).trim()
            if (h && !registered.includes(h) && !custom.includes(h)) custom.push(h)
          })
        })
      }
      return { registered, custom, all: registered.concat(custom) }
    },
    // Any execution recorded at all — drives the "nothing yet" fallback.
    hasTrace () {
      const j = this.selectedJoint
      if (!j) return false
      return !!(j.fitter || j.filler_batch || j.fit_up || j.welder || j.weld_end || j.vt)
    },
    counts () {
      const c = {}
      Object.keys(this.STATUS).forEach(k => { c[k] = 0 })
      this.joints.forEach(j => { if (c[j.status] !== undefined) c[j.status]++ })
      return c
    },
    // What the progress strip shows. With assembly off, FITTED is a step this
    // drawing does not run — no weld can reach it — so its pill is dropped.
    // MAPPED stays: it is still real work, just the welder's rather than the
    // fitter's, and it is the only pill counting what is waiting for them.
    visibleCounts () {
      return this.compilation ? this.counts : { ...this.counts, FITTED: 0 }
    },
    // Which of the welds about to be approved still have traceability holes.
    // Computed from the joints already loaded so the check can happen in the
    // confirm sheet BEFORE anything is signed, rather than reporting it after.
    bulkGapsPreview () {
      return this.bulkReady
        .map(j => ({ id: j.id, weld_nr: j.weld_nr, rows: this.jointGaps(j) }))
        .filter(g => g.rows.length)
        // One line per weld: the union of what its rows lack, so a long drawing
        // does not turn the sheet into a per-row table.
        .map(g => ({
          id: g.id,
          weld_nr: g.weld_nr,
          missing: g.rows.reduce((acc, r) => {
            r.missing.forEach(k => { if (!acc.includes(k)) acc.push(k) })
            return acc
          }, [])
        }))
    },
    // Field-registered chip counts: pending review (violet) and rejected (red).
    fieldCounts () {
      const c = { pending: 0, rejected: 0 }
      this.joints.forEach(j => {
        if (!j.field) return
        if (j.field.review === 'rejected') c.rejected++
        else if (j.field.review !== 'approved') c.pending++
      })
      return c
    },
    grooveOptions () {
      return (this.fieldOptions.grooves && this.fieldOptions.grooves[this.fieldForm.joint]) || []
    },
    selectedWps () {
      return (this.fieldOptions.wps || []).find(w => w.id === this.fieldForm.wps) || null
    },
    // Whether the current role can act on the selected weld's status.
    canAct () {
      if (!this.selectedJoint) return false
      // A rejected field weld is dead on the drawing (spec §4).
      if (this.fieldRejected(this.selectedJoint)) return false
      // A superseded original still reads REPAIR, but the rework belongs to its
      // revision — it is history and nothing here may act on it.
      if (this.selectedJoint.superseded_by) return false
      return this.roleActsOn(this.role, this.selectedJoint)
    },
    // The weld this repair replaces, via the copy_reference_id FK the advanced
    // weld log sets — never parsed from the weld number.
    // Set when a revision replaced this weld (repair_of is the inverse).
    superseded () {
      return (this.selectedJoint && this.selectedJoint.superseded_by) || null
    },
    origin () {
      return (this.selectedJoint && this.selectedJoint.repair_of) || null
    },
    // Structured fault types first (what the web records), free text second.
    originReason () {
      const o = this.origin
      if (!o) return ''
      const faults = (o.faults || []).join(', ')
      return [faults, o.reason].filter(Boolean).join(' — ')
    },
    // Materials are traceability data, not a task step: a heat number is often
    // only found on the plate after welding, so gating this on canAct locked
    // the welder out of the very row that reads "Heat missing".
    // The welder may still correct materials on a weld they have welded; it
    // closes once VT has signed the weld off (or it is history).
    // This is the FULL-edit gate: add rows, retype a heat that is already
    // recorded, copy, delete. A missing heat is handled separately by
    // canEditMaterialRow — see there.
    canEditMaterials () {
      const j = this.selectedJoint
      if (!j) return false
      if (j.superseded_by) return false
      if (this.canAct) return true
      // Correcting a finished weld: the edit form is open, so the materials it
      // recorded are editable too. Without this the card stayed read-only on a
      // COMPLETED weld and an add/edit was impossible in the very mode meant
      // for fixing mistakes.
      if (this.editMode) return true
      // Welder keeps write access on their own welded joints until VT decides.
      return this.role === 'welder' && j.status === 'WELDED' && !j.vt
    },
    // A material row with no heat number is an incomplete record, not signed-off
    // data — leaving it unreachable means the gap can never be closed, which is
    // the opposite of what traceability is for. So any role may open such a row
    // at any status, including COMPLETED.
    // Deliberately narrow: it unlocks only rows where the heat is still blank.
    // A heat that has been entered stays under canEditMaterials, so nobody can
    // quietly rewrite or delete a number VT has already certified.
    canEditMaterialRow () {
      return (m) => {
        if (!this.selectedJoint || this.selectedJoint.superseded_by) return false
        return this.canEditMaterials || !(m && m.heat)
      }
    },
    // What this role should do next, from the real counts.
    taskHint () {
      const c = this.counts
      const t = k => this.$t('message.weldMapping.hints.' + k)
      // A tone name, not colours: inline styles would beat the stylesheet and
      // strand this banner in its light palette when the app is dark.
      const blue = 'hint-blue'
      const cyan = 'hint-cyan'
      const gold = 'hint-gold'
      const green = 'hint-green'
      if (this.role === 'fitter') {
        return c.MAPPED
          ? { tone: blue, text: this.$t('message.weldMapping.hints.fitter', { n: c.MAPPED }) }
          : { tone: green, text: t('fitterDone') }
      }
      if (this.role === 'welder') {
        // A superseded original still reads REPAIR, but its rework lives on the
        // revision — counting it promised work that cannot be done here.
        const supersededRepairs = this.joints
          .filter(j => j.status === 'REPAIR' && j.superseded_by).length
        // With assembly off there is no fit-up to wait for, so MAPPED welds are
        // the welder's queue too — matching what `roles` lets them act on.
        const n = c.FITTED + c.REPAIR - supersededRepairs +
          (this.compilation ? 0 : c.MAPPED)
        if (n) return { tone: cyan, text: this.$t('message.weldMapping.hints.welder', { n }) }
        // An empty queue has two very different causes and one message for both
        // was simply wrong: a drawing whose welds are all done was still told it
        // was "waiting for assembly". If nothing is left upstream of the weld
        // and welds are waiting on inspection, say that instead.
        const upstream = c.MAPPED + c.FITTED
        if (!upstream && c.WELDED) {
          return { tone: gold, text: this.$t('message.weldMapping.hints.welderNdt', { n: c.WELDED }) }
        }
        // Everything welded AND already inspected — the drawing is finished.
        if (!upstream && !c.WELDED && c.COMPLETED) {
          return { tone: green, text: t('welderDone') }
        }
        return { tone: gold, text: t('welderNone') }
      }
      // VT's queue is the signature, not the status (roleActsOn): a weld VT
      // already approved can sit at WELDED while the other NDT methods are
      // signed on the web, and that is not work for this screen.
      const vtLeft = this.joints.filter(j => this.roleActsOn('vt', j)).length
      if (vtLeft) return { tone: gold, text: this.$t('message.weldMapping.hints.vt', { n: vtLeft }) }
      const ndtOpen = this.joints.filter(j => !j.superseded_by && j.status === 'WELDED' && j.vt).length
      if (ndtOpen) return { tone: gold, text: this.$t('message.weldMapping.hints.vtNdtOpen', { n: ndtOpen }) }
      return { tone: green, text: t('vtDone') }
    }
  },
  watch: {
    '$route.params.drawing_id' () {
      // Ny tegning, nytt trinn: la fanevalget bli automatisk igjen.
      this.rolePicked = false
      this.load()
    },
    // A link to the same drawing with another ?unit_id= while the screen is
    // open. Our own replace() lands here too and is a no-op.
    '$route.query.unit_id' (v) {
      if (!this.series || v === undefined || v === null || String(v) === String(this.unitId)) return
      if (this.seriesUnits.some(u => String(u.id) === String(v))) this.onUnitChange(v)
    }
  },
  created () {
    window.addEventListener('online', this.setOnline)
    window.addEventListener('offline', this.setOnline)
    // Lukk sertifikat-nedtrekket ved klikk utenfor. Capture-fasen, ellers
    // rekker et klikk på en annen knapp å kjøre før nedtrekket lukkes.
    document.addEventListener('click', this.closePfOnOutside, true)
  },
  beforeDestroy () {
    clearInterval(this.notifTimer)
    document.removeEventListener('visibilitychange', this.onVisible)
    clearTimeout(this.wpsCheckTimer)
    window.removeEventListener('online', this.setOnline)
    window.removeEventListener('offline', this.setOnline)
    document.removeEventListener('click', this.closePfOnOutside, true)
  },
  mounted () {
    this.load()
    this.loadNotifications()
    this.notifTimer = setInterval(this.loadNotifications, 60000)
    document.addEventListener('visibilitychange', this.onVisible)
    if (this.isOnline && this.outbox.length) this.flushOutbox()
  },
  methods: {
    // WIT-IMPL-M101-PWA-001 — list flag and warning lines per joint.
    hasHold (j) {
      return hasHoldWarning(j)
    },
    holdLinesFor (j) {
      return holdWarningLines(j && j.hold_warnings, (k, p) => this.$t(k, p), v => this.$options.filters.dt(v))
    },
    /**
     * What the server actually said, or a sentence that is true when it said
     * nothing readable (5195 / F-4, F-6).
     *
     * `e?.response?.data?.message` alone was right for the common case and
     * silently wrong for the rest, and the fallback it fell through to —
     * "Noe gikk galt" — tells the welder nothing they can act on. Three shapes
     * this app really receives, none of which carried `message`:
     *
     *  - Laravel's validation response, `{message, errors:{field:[…]}}`, whose
     *    `message` is the untranslated "The given data was invalid." while the
     *    useful sentence sits one level down in `errors`;
     *  - this app's own handler returning a BARE message bag, `{field:[…]}`,
     *    with no `message` key at all (Handler::render);
     *  - no response object whatsoever — the request never completed, or the
     *    browser refused to hand us a cross-origin error body. That is the one
     *    that produced Erik's screenshots: the server answered 422 with a
     *    perfectly good Norwegian sentence and the browser threw it away for
     *    want of a CORS header on the error response.
     *
     * The last case must NOT read like a server error, or the office spends the
     * day looking for a bug in the weld log. It says the connection is the
     * problem, which is something a person on a shop floor can act on.
     */
    serverMessage (e) {
      const res = e && e.response
      if (!res) {
        // A cancelled request is not a failure worth shouting about.
        if (e && e.__CANCEL__) return ''
        return this.$t('message.weldMapping.noServerAnswer')
      }
      const data = res.data
      // An HTML error page (or anything non-JSON) is not a message for a user.
      if (!data || typeof data !== 'object') {
        return this.$t('message.common.somethingWentWrong')
      }
      if (typeof data.message === 'string' && data.message.trim() &&
          data.message !== 'The given data was invalid.') {
        return data.message
      }
      const firstOf = (bag) => {
        if (!bag || typeof bag !== 'object') return ''
        for (const k of Object.keys(bag)) {
          const v = bag[k]
          if (typeof v === 'string' && v.trim()) return v
          if (Array.isArray(v) && typeof v[0] === 'string' && v[0].trim()) return v[0]
        }
        return ''
      }
      // Laravel's `errors`, then the bare bag the response itself may be.
      return firstOf(data.errors) ||
        firstOf(data) ||
        (typeof data.error === 'string' ? data.error : '') ||
        (typeof data.message === 'string' && data.message.trim() ? data.message : '') ||
        this.$t('message.common.somethingWentWrong')
    },
    setOnline () {
      this.isOnline = navigator.onLine
      // Coming back online is what drains the outbox — and what fires the
      // office notification, server-side, on sync.
      if (this.isOnline && this.outbox.length) this.flushOutbox()
    },

    // ── Field-registered welds ──────────────────────────────────────────
    blankFieldForm () {
      return { after: '', weld_nr: '', reason: '', joint: '', groove: '', wps: '', material: '', thickness: '', weld_length: '', spool_nr: '', note: '' }
    },
    fieldPending (j) {
      return !!(j && j.field && j.field.review !== 'approved')
    },
    fieldRejected (j) {
      return !!(j && j.field && j.field.review === 'rejected')
    },
    // List / sheet presentation of a joint's state. A field weld is violet
    // until approved, whatever its status — same as its marker. Approved field
    // welds are indistinguishable from office welds here (spec key principle).
    jointDotKey (j) {
      if (this.fieldRejected(j)) return 'FIELD-REJ'
      if (this.fieldPending(j)) return 'FIELD'
      return j.status
    },
    jointColor (j) {
      if (this.fieldRejected(j)) return this.STATUS.REPAIR.color
      if (this.fieldPending(j)) return this.FIELD_COLOR
      return this.STATUS[j.status] ? this.STATUS[j.status].color : this.FIELD_COLOR
    },
    jointStatusLabel (j) {
      if (this.fieldRejected(j)) return this.$t('message.weldMapping.field.status.rejected')
      if (this.fieldPending(j) && j.status === 'MAPPED') return this.$t('message.weldMapping.field.status.pending')
      // Approved by VT, still waiting for the other NDT methods (signed on the
      // web) — without this the inspector's approval looked as if it failed.
      if (j.status === 'WELDED' && j.vt) return this.statusLabel(j.status) + ' · ' + this.$t('message.weldMapping.vtSignedNdtOpen')
      return this.statusLabel(j.status)
    },
    fieldSubLabel (j) {
      if (!j.field) return ''
      if (j.field.local) return this.$t('message.weldMapping.field.sub.local')
      if (j.field.review === 'rejected') return this.$t('message.weldMapping.field.sub.rejected')
      if (j.field.review === 'approved') return this.$t('message.weldMapping.field.sub.approved')
      return this.$t('message.weldMapping.field.sub.pending')
    },
    reasonLabel (r) {
      return r ? this.$t('message.weldMapping.field.reasons.' + r) : '—'
    },
    jointLabel (j) {
      return j ? this.$t('message.weldMapping.field.joints.' + j) : '—'
    },
    grooveLabel (g) {
      if (!g) return '—'
      const key = 'message.weldMapping.field.grooves.' + g
      const t = this.$t(key)
      return t === key ? g : t
    },
    wpsOptionLabel (w) {
      const parts = [w.id]
      if (w.process) parts.push(w.process)
      if (w.groups && w.groups.length) parts.push(w.groups.join('/'))
      if (w.tmin !== null || w.tmax !== null) parts.push((w.tmin ?? '?') + '–' + (w.tmax ?? '?') + ' mm')
      return parts.join(' · ')
    },
    wpsHint (w) {
      const parts = []
      if (w.process) parts.push(this.$t('message.weldMapping.process') + ' ' + w.process)
      if (w.tmin !== null || w.tmax !== null) parts.push(this.$t('message.weldMapping.field.validRange', { min: w.tmin ?? '?', max: w.tmax ?? '?' }))
      if (w.groups && w.groups.length) parts.push(w.groups.join(', '))
      return parts.join(' · ')
    },
    async loadFieldOptions (after) {
      this.optionsLoading = true
      try {
        const id = this.$route.params.drawing_id
        const { data } = await hostAppApi.get(
          `${this.getHostRefApi}project-drawing/${id}/field-weld/options`,
          { params: after ? { after } : {} }
        )
        this.fieldOptions = data
        this.fieldPerm = { can_register: !!data.can_register, can_review: !!data.can_review }
        return data
      } catch (e) {
        this.fieldSaveError = this.serverMessage(e)
        return null
      } finally {
        this.optionsLoading = false
      }
    },
    // Step 1: "Add weld in field" — the drawing enters placement mode.
    startPlacing () {
      this.placing = true
      this.selected = null
      this.pendingXY = null
      this.ghost = null
      if (!this.fieldOptions.after.length) this.loadFieldOptions()
      this.$nextTick(() => {
        const el = this.$refs.dwrap
        if (el && el.scrollIntoView) el.scrollIntoView({ behavior: 'smooth', block: 'center' })
      })
    },
    cancelPlacing () {
      this.placing = false
      this.pendingXY = null
      this.ghost = null
    },
    onCanvasSelect (id) {
      if (this.placing) return
      this.openWeld(id)
    },
    // Step 2: the tap. Percent of the drawing is what is stored.
    onPlace (pt) {
      if (!this.placing) return
      this.pendingXY = pt
      this.fieldEditId = null
      this.fieldForm = this.blankFieldForm()
      this.fieldIdTouched = false
      this.fieldErr = {}
      this.fieldSaveError = null
      this.wpsWarnings = []
      this.holdWarnings = []
      const after = this.fieldOptions.default_after || ''
      this.fieldForm.after = after
      this.fieldForm.weld_nr = this.localNextId(after)
      this.ghost = { x: pt.x, y: pt.y, label: this.fieldForm.weld_nr }
      this.fieldSheet = true
      this.autofillFieldMaterial()
      // The server owns the id; refresh the proposal once options are in.
      this.loadFieldOptions(after).then(d => {
        if (d && !this.fieldIdTouched && !this.fieldEditId) {
          if (!this.fieldForm.after) this.fieldForm.after = d.default_after || ''
          this.fieldForm.weld_nr = d.next_id || this.fieldForm.weld_nr
          if (this.ghost) this.ghost = { ...this.ghost, label: this.fieldForm.weld_nr }
        }
        if (d && !this.fieldEditId) this.autofillFieldMaterial()
      })
    },
    // The material list is the steel types already used on this drawing's
    // welds (FieldWeldService::drawingMaterials). With exactly one, the field
    // weld is almost certainly that steel too, so it is filled in — never over
    // a choice the welder already made, and the dropdown still lets them
    // change it.
    /** Back to the whole drawing from a unit-scoped view (5183 / S4). */
    clearUnitScope () {
      const query = { ...(this.$route.query || {}) }
      delete query.unit
      this.$router.replace({ query }).catch(() => {})
    },
    autofillFieldMaterial () {
      const list = (this.fieldOptions && this.fieldOptions.materials) || []
      if (list.length !== 1 || this.fieldForm.material) return
      this.fieldForm.material = list[0].type
      this.runWpsCheck()
    },
    // Client-side twin of FieldWeldService::nextFieldId, for offline use and
    // for an instant proposal before the server answers. Letters already used
    // on the loaded joints (and the outbox) are skipped.
    localNextId (after) {
      const m = /^[A-Za-z]*([-_ ]?)(\d+)$/.exec(String(after || '').trim())
      const used = {}
      const all = this.joints.map(j => j.weld_nr).concat(this.outbox.map(o => o.weld_nr))
      const re = m
        ? new RegExp('^([A-Z])' + m[1].replace(/[-]/g, '\\-') + m[2] + '$')
        : new RegExp('^([A-Z])-' + String(after).replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '$')
      all.forEach(nr => { const h = re.exec(String(nr || '')); if (h) used[h[1]] = true })
      for (const L of 'ABCDEFGHIJKLMNOPQRSTUVWXYZ') {
        if (!used[L]) return m ? L + m[1] + m[2] : L + '-' + after
      }
      return 'A-' + after
    },
    async onAfterChange () {
      if (this.fieldIdTouched) return
      const after = this.fieldForm.after
      this.fieldForm.weld_nr = this.localNextId(after)
      if (this.ghost) this.ghost = { ...this.ghost, label: this.fieldForm.weld_nr }
      if (!this.isOnline) return
      try {
        const id = this.$route.params.drawing_id
        const { data } = await hostAppApi.get(
          `${this.getHostRefApi}project-drawing/${id}/field-weld/options`, { params: { after } })
        if (!this.fieldIdTouched && this.fieldForm.after === after && data.next_id) {
          this.fieldForm.weld_nr = data.next_id
          if (this.ghost) this.ghost = { ...this.ghost, label: data.next_id }
        }
      } catch (e) { /* keep the local proposal */ }
    },
    onJointChange () {
      const g = this.grooveOptions
      this.fieldForm.groove = g.length === 1 ? g[0] : ''
    },
    // Live WPS check: local (from the options' WPS data) so it works offline,
    // confirmed by the server when online. Advisory only.
    runWpsCheck () {
      const w = this.selectedWps
      const warns = []
      if (w) {
        const mat = (this.fieldOptions.materials || []).find(m => m.type === this.fieldForm.material)
        if (mat && mat.group && w.groups && w.groups.length && !w.groups.includes(mat.group)) {
          warns.push(this.$t('message.weldMapping.field.warnMaterial', { material: mat.type, wps: w.id }))
        }
        const t = parseFloat(this.fieldForm.thickness)
        if (!isNaN(t) && (w.tmin !== null || w.tmax !== null)) {
          if ((w.tmin !== null && t < w.tmin) || (w.tmax !== null && t > w.tmax)) {
            warns.push(this.$t('message.weldMapping.field.warnThickness', { t, min: w.tmin ?? '?', max: w.tmax ?? '?' }))
          }
        }
        // WIT-IMPL-WPS-5324-003 (F4): the WPS states no welding process the
        // certificate match can read. [] only — null/absent = unknown.
        if (Array.isArray(w.process_codes) && !w.process_codes.length) {
          warns.push(this.$t('message.weldMapping.field.warnNoProcess', { wps: w.id }))
        }
      }
      this.wpsWarnings = warns
      // Hold status is the server's to say; cleared until /check answers.
      this.holdWarnings = []
      clearTimeout(this.wpsCheckTimer)
      if (!this.isOnline || !this.fieldForm.wps) return
      this.wpsCheckTimer = setTimeout(async () => {
        try {
          const id = this.$route.params.drawing_id
          const { data } = await hostAppApi.post(`${this.getHostRefApi}project-drawing/${id}/field-weld/check`, {
            wps: this.fieldForm.wps, material: this.fieldForm.material, thickness: this.fieldForm.thickness
          })
          if (data && Array.isArray(data.warnings)) this.wpsWarnings = data.warnings
          // WIT-IMPL-M101-PWA-001: structured hold entries → lines. An older
          // API sends no key and the list simply stays empty.
          this.holdWarnings = holdWarningLines(data && data.hold_warnings, (k, p) => this.$t(k, p), v => this.$options.filters.dt(v))
        } catch (e) { /* local result stands */ }
      }, 350)
    },
    closeFieldSheet () {
      this.fieldSheet = false
      this.fieldEditId = null
      this.fieldSaveError = null
      this.cancelPlacing()
    },
    validateField () {
      const f = this.fieldForm
      const err = {}
      const required = ['after', 'weld_nr', 'reason', 'joint', 'groove', 'wps', 'material', 'thickness']
      // Spool/del nr. is only asked for when the drawing holds more than one
      // (D-5170-3). With a single spool the server assigns it and the sheet
      // never shows the field, so requiring it would block a valid save.
      if (this.spoolChoices.length > 1) required.push('spool_nr')
      required.forEach(k => {
        if (!String(f[k] || '').trim()) err[k] = true
      })
      // Optional, but a number from 0 to 999999 when given (server: weld_length).
      const len = String(f.weld_length == null ? '' : f.weld_length).trim().replace(',', '.')
      if (len !== '' && !(isFinite(Number(len)) && Number(len) >= 0 && Number(len) <= 999999)) err.weld_length = true
      if (!this.fieldEditId && !this.pendingXY) err.pos = true
      this.fieldErr = err
      return !Object.keys(err).length
    },
    fieldPayload () {
      const f = this.fieldForm
      const w = this.selectedWps
      const p = {
        after: f.after, weld_nr: f.weld_nr, reason: f.reason, joint: f.joint, groove: f.groove,
        wps: f.wps, process: w ? w.process : '', material: f.material, thickness: f.thickness, note: f.note,
        // Empty clears it on edit; on registration the server then inherits it.
        weld_length: String(f.weld_length == null ? '' : f.weld_length).trim().replace(',', '.') || null
      }
      // Sent only when the welder actually chose one; with a single spool on
      // the drawing the server's own answer is the whole truth and a client
      // value is ignored anyway.
      if (this.spoolChoices.length > 1 && f.spool_nr) p.spool_nr = f.spool_nr
      if (this.pendingXY) {
        p.x_pct = this.pendingXY.xPct
        p.y_pct = this.pendingXY.yPct
        p.drawing_w = this.pendingXY.w
        p.drawing_h = this.pendingXY.h
      }
      return p
    },
    // Step 5: save. Online → server; offline or network failure → outbox.
    async saveField () {
      if (this.fieldSaving || !this.validateField()) return
      this.fieldSaving = true
      this.fieldSaveError = null
      const id = this.$route.params.drawing_id
      try {
        if (this.fieldEditId) {
          const { data } = await hostAppApi.post(
            `${this.getHostRefApi}weld-mapping/weld/${this.fieldEditId}/field-weld/update`, this.fieldPayload())
          const j = this.joints.find(x => x.id === this.fieldEditId)
          if (j) {
            this.$set(j, 'weld_nr', data.weld_nr)
            this.$set(j, 'wps', data.wps)
            this.$set(j, 'process', data.process)
            this.$set(j, 'field', data.field)
          }
          this.fieldSheet = false
          this.fieldEditId = null
          this.toast(this.$t('message.weldMapping.field.updated', { weld: data.weld_nr }))
          await this.loadJoints()
          return
        }
        const payload = {
          ...this.fieldPayload(),
          client_uuid: uuid(),
          // Production series: the weld is added to the unit on screen only.
          // Also kept in the outbox entry, so a later sync lands it there too.
          ...(this.series && this.unitId ? { unit_id: this.unitId } : {})
        }
        if (!this.isOnline) {
          this.queueOffline(payload)
          return
        }
        try {
          const { data } = await hostAppApi.post(`${this.getHostRefApi}project-drawing/${id}/field-weld`, payload)
          this.fieldSheet = false
          this.cancelPlacing()
          this.toast(data.message || this.$t('message.weldMapping.field.registeredToast', { weld: data.weld_nr }))
          await this.loadJoints()
          this.refreshSeriesCounts()
        } catch (e) {
          // No response at all = the network, not the server: queue it.
          if (!e.response) { this.queueOffline(payload); return }
          throw e
        }
      } catch (e) {
        const errs = e?.response?.data?.errors
        this.fieldSaveError = errs ? Object.values(errs).flat().join(' ')
          : (this.serverMessage(e))
      } finally {
        this.fieldSaving = false
      }
    },
    queueOffline (payload) {
      const entry = {
        uuid: payload.client_uuid, drawingId: this.$route.params.drawing_id,
        weld_nr: payload.weld_nr, payload, createdAt: new Date().toISOString(),
        x: this.pendingXY ? this.pendingXY.x : null, y: this.pendingXY ? this.pendingXY.y : null
      }
      this.outbox = this.outbox.concat([entry])
      writeOutbox(this.outbox)
      // Show it on the drawing right away: dashed, local, pending.
      this.joints = this.joints.concat([this.localJoint(entry)])
      this.fieldSheet = false
      this.cancelPlacing()
      this.toast(this.$t('message.weldMapping.field.queuedToast', { weld: payload.weld_nr }))
    },
    localJoint (entry) {
      const p = entry.payload
      return {
        id: 'local-' + entry.uuid, weld_nr: p.weld_nr,
        // Its unit, when queued on a series drawing; null for legacy entries.
        unit_id: p.unit_id !== undefined ? p.unit_id : null,
        meta: [p.process, p.wps, p.material, p.thickness + ' mm'].filter(Boolean),
        status: 'MAPPED', x: entry.x, y: entry.y, label_x: null, label_y: null,
        materials: [], photos: [], faults: [], field_weld: true,
        // WIT-IMPL-M101-PWA-001: no server context yet for a queued row.
        ndt_context: null, hold_warnings: [],
        field: {
          origin: 'field', review: 'pending', local: true, reason: p.reason, note: p.note, after: p.after,
          joint: p.joint, groove: p.groove, material: p.material, thickness: p.thickness,
          warnings: this.wpsWarnings.slice(), registered_at: entry.createdAt,
          can_review: false, can_edit: false
        }
      }
    },
    async flushOutbox () {
      if (this.syncing || !this.outbox.length || !navigator.onLine) return
      this.syncing = true
      let changed = false
      for (const entry of this.outbox.slice()) {
        try {
          await hostAppApi.post(`${this.getHostRefApi}project-drawing/${entry.drawingId}/field-weld`, entry.payload)
          this.outbox = this.outbox.filter(o => o.uuid !== entry.uuid)
          changed = true
        } catch (e) {
          if (e.response && e.response.status >= 400 && e.response.status < 500) {
            // Rejected by the server (validation, permission): drop it and say so.
            this.outbox = this.outbox.filter(o => o.uuid !== entry.uuid)
            changed = true
            const errs = e?.response?.data?.errors
            this.toast((entry.weld_nr + ': ') + (errs ? Object.values(errs).flat().join(' ') : (e.response.data?.message || '')))
          }
          // Network again: keep it for the next attempt.
        }
      }
      writeOutbox(this.outbox)
      this.syncing = false
      if (changed && String(this.$route.params.drawing_id)) {
        await this.loadJoints()
        this.refreshSeriesCounts()
      }
    },
    // Office can edit a field weld during approval; the welder their own
    // pending one. Same form, prefilled.
    async openFieldEdit () {
      const j = this.selectedJoint
      if (!j || !j.field) return
      this.fieldEditId = j.id
      this.fieldIdTouched = true
      this.fieldErr = {}
      this.fieldSaveError = null
      this.pendingXY = null
      this.fieldForm = {
        after: j.field.after || '', weld_nr: j.weld_nr, reason: j.field.reason || '',
        joint: j.field.joint || '', groove: j.field.groove || '', wps: j.wps || '',
        material: j.field.material || '', thickness: j.field.thickness || '',
        weld_length: j.field.weld_length || '',
        spool_nr: j.spool_nr || '', note: j.field.note || ''
      }
      this.wpsWarnings = (j.field.warnings || []).slice()
      this.holdWarnings = []
      this.fieldSheet = true
      if (!this.fieldOptions.wps.length) await this.loadFieldOptions()
      if (this.fieldForm.after && !this.fieldOptions.after.includes(this.fieldForm.after)) {
        this.fieldOptions.after = [this.fieldForm.after].concat(this.fieldOptions.after)
      }
    },
    // Welder removes their own registration (or the office any) before it
    // is reviewed. A queued offline registration is simply dropped locally.
    async deleteField () {
      // From the edit sheet (series drawings) or from the weld sheet.
      const fromEdit = this.fieldSheet && !!this.fieldEditJoint
      const j = fromEdit ? this.fieldEditJoint : this.selectedJoint
      if (!j || !j.field || this.fieldActing) return
      if (!confirm(this.$t('message.weldMapping.field.deleteConfirm', { weld: j.weld_nr }))) return
      if (j.field.local) {
        const uuid = String(j.id).replace(/^local-/, '')
        this.outbox = this.outbox.filter(o => o.uuid !== uuid)
        writeOutbox(this.outbox)
        this.joints = this.joints.filter(x => x.id !== j.id)
        this.selected = null
        return
      }
      this.fieldActing = true
      this.fieldActError = null
      try {
        const { data } = await hostAppApi.post(`${this.getHostRefApi}weld-mapping/weld/${j.id}/field-weld/delete`, {})
        this.joints = this.joints.filter(x => x.id !== j.id)
        this.selected = null
        if (fromEdit) this.closeFieldSheet()
        this.toast(data.message || '')
        this.refreshSeriesCounts()
      } catch (e) {
        this.fieldActError = this.serverMessage(e)
      } finally {
        this.fieldActing = false
      }
    },
    async reviewField (action) {
      const j = this.selectedJoint
      if (!j || this.fieldActing) return
      this.fieldActing = true
      this.fieldActError = null
      try {
        const { data } = await hostAppApi.post(
          `${this.getHostRefApi}weld-mapping/weld/${j.id}/field-weld/${action}`,
          { comment: this.reviewComment || null })
        this.reviewComment = ''
        if (action === 'delete') {
          this.joints = this.joints.filter(x => x.id !== j.id)
          this.selected = null
        } else {
          this.$set(j, 'field', data.field)
          this.selected = null
        }
        this.toast(data.message || '')
        this.loadNotifications()
      } catch (e) {
        this.fieldActError = this.serverMessage(e)
      } finally {
        this.fieldActing = false
      }
    },
    // In-app notifications (spec §4/§5). Polled; a socket can replace this.
    async loadNotifications () {
      if (!navigator.onLine) return
      try {
        const { data } = await hostAppApi.get(`${this.getHostRefApi}weld-mapping/notifications`, {
          params: { project_id: this.$route.params.project_id }
        })
        const items = data.items || []
        // A new approve/reject on THIS drawing means the office changed a weld
        // under us: refresh the joints so the marker turns solid (or red)
        // without the welder having to reload.
        const newest = items.length ? Math.max(...items.map(i => Number(i.id) || 0)) : 0
        if (this.notifNewestId !== null && newest > this.notifNewestId) {
          const fresh = items.filter(i => Number(i.id) > this.notifNewestId)
          const mine = fresh.some(i => String(i.drawing_id) === String(this.$route.params.drawing_id))
          if (mine) this.loadJoints()
          // Tell the welder right away: a decision on their weld pops up as a
          // toast even while they are looking at the drawing.
          fresh.filter(i => i.type !== 'field_weld_registered').forEach(i => {
            const who = i.payload.actor ? ' · ' + i.payload.actor : ''
            const cmt = i.payload.comment ? ' — ' + i.payload.comment : ''
            this.toast(this.notifText(i) + who + cmt, i.type === 'field_weld_rejected')
          })
        }
        this.notifNewestId = newest
        this.notifications = items
        this.notifUnread = data.unread || 0
      } catch (e) { /* quiet: the bell simply shows nothing new */ }
    },
    // Coming back to the tab: the office may have reviewed meanwhile.
    onVisible () {
      if (document.visibilityState === 'visible' && navigator.onLine && !this.selected) {
        this.loadJoints()
        this.loadNotifications()
      }
    },
    notifText (n) {
      return this.$t('message.weldMapping.field.notif.' + n.type, { weld: n.payload.weld_nr || '' })
    },
    async openNotification (n) {
      this.notifOpen = false
      try {
        await hostAppApi.post(`${this.getHostRefApi}weld-mapping/notifications/read`, { ids: [n.id] })
        n.read = true
        this.notifUnread = Math.max(0, this.notifUnread - 1)
      } catch (e) { /* read state is a convenience */ }
      if (n.drawing_id && String(n.drawing_id) !== String(this.$route.params.drawing_id)) {
        this.$router.push({ name: 'weld-mapping-drawing', params: { project_id: this.$route.params.project_id, drawing_id: n.drawing_id } })
        return
      }
      await this.loadJoints()
      const j = this.joints.find(x => x.id === n.weldlog_id)
      if (j) this.openWeld(j.id)
    },
    // App-wide snackbar (App.vue listens on the root bus). Violet toasts are
    // not available there; colour is the app's success/warning.
    toast (msg, warn) {
      if (!msg) return
      this.$root.$emit('snackbar', { snackbar: true, color: warn ? 'warning' : 'success', text: msg })
    },
    statusLabel (k) {
      return this.$t('message.weldMapping.status.' + k)
    },
    // Both certificate columns store a CSV of expertise ids, never free text.
    csvIds (v) {
      return String(v || '').split(',').map(s => s.trim()).filter(Boolean)
    },
    certsValidOn (weldEnd, keepIds = []) {
      const day = /^\d{4}-\d{2}-\d{2}$/.test(String(weldEnd || '')) ? String(weldEnd) : moment().format('YYYY-MM-DD')
      const keep = keepIds.map(String)
      // WIT-IMPL-CERT-001 W1: no end date = not valid (the server's
      // CertificateReferenceGuard::validOn); a stored pick is kept. A
      // continuous (Type C / Armering) certificate is valid on any day.
      return (this.certificates || []).filter(c =>
        c.continuous || (c.endDate && c.endDate >= day) || keep.includes(String(c.value)))
    },
    certLabels (ids) {
      return ids
        .map(id => {
          const hit = this.certificates.find(c => String(c.value) === String(id))
          return hit ? hit.label : id
        })
        .join(', ')
    },
    // Wired as a method rather than a computed so an unknown status/role pair
    // (a status this build does not know yet) falls back to "waiting" instead
    // of claiming work is finished.
    isRoleStepDone (status, role) {
      const roleStage = this.roleStage[role]
      const statusStage = this.statusStage[status]
      if (roleStage === undefined || statusStage === undefined) return false
      return statusStage > roleStage
    },
    // Whether a role still has its step to do on a joint. The status says which
    // step the joint is at; for VT the signature decides as well. On a project
    // where the other NDT methods count (NDT matrix or ordering on), a weld the
    // inspector approved stays WELDED until MT/RT/UT/PT/PMI are signed on the
    // web — the server derives COMPLETED from every scoped method, the same
    // rule as the PC canvas — and this screen must neither queue it for VT
    // again nor let it be approved twice. The server mirrors the guard
    // (bulk-sign skips a signed weld as already_signed).
    roleActsOn (roleKey, j) {
      const r = this.roles[roleKey]
      if (!r || !j || j.superseded_by) return false
      if (!r.actsOn.includes(j.status)) return false
      if (roleKey === 'vt' && j.vt) return false
      return true
    },
    // Switching role changes which welds are actionable, so the open sheet's
    // action area would no longer apply to the weld it was opened for.
    setRole (r) {
      this.role = r
      this.rolePicked = true
      this.selected = null
    },
    /**
     * Fanen tegningen skal åpne på.
     *
     * Relèet går platearbeider → sveiser → VT, og en tegning står som regel
     * på ett av trinnene: er ingen skjøter sammenstilt ennå, er det
     * platearbeideren som står for tur; er alt sammenstilt, sveiseren; er alt
     * sveist, VT-kontrollen. Vi velger derfor den første rollen som faktisk
     * har noe å gjøre — samme actsOn-liste som avgjør om knappene er aktive,
     * så fanen og handlingene aldri er uenige.
     *
     * Har ingen roller noe utestående (alt fullført, eller tegningen er tom),
     * blir stående fane beholdt.
     */
    /**
     * Flytt fanen videre når trinnet er gjort unna.
     *
     * Etter siste skjøt en rolle hadde igjen, blir fanen stående tom — brukeren
     * ser «ingen skjøter» og må selv finne ut at neste trinn er neste fane.
     * Har rollen ingenting igjen å gjøre, hopper vi derfor til den første som
     * har det. Dette overstyrer rolePicked med vilje: fanen brukeren valgte er
     * ferdig, så det er ikke lenger et valg vi tråkker på.
     *
     * Har rollen fortsatt skjøter igjen, står fanen — midt i en serie skal den
     * ikke rykke.
     */
    advanceRoleIfDone () {
      if (!this.roles[this.role]) return
      const left = this.joints.some(j => this.roleActsOn(this.role, j))
      if (left) return
      const next = this.defaultRole()
      if (next && next !== this.role) this.role = next
    },
    defaultRole () {
      const order = Object.keys(this.roles)
      const hit = order.find(k => this.joints.some(j => this.roleActsOn(k, j)))
      return hit || null
    },
    // Collapse/expand the joints list, remembering the choice on this device.
    toggleJoints () {
      this.showJoints = !this.showJoints
      writeShowJoints(this.showJoints)
    },
    openWeld (id) {
      this.selected = id
      this.matEdit = null
      this.matBase = null
      this.matUnsavedHint = false
      this.actError = null
      this.undoError = null
      this.certOpen = false
      this.certSearch = ''
      // The history belongs to the joint it was opened on, so it collapses with
      // it rather than staying expanded over the next joint's rows.
      this.auditOpen = false
      // An edit belongs to the joint it was started on, never to the next one.
      this.editMode = false
      this.patchOrig = null
      // Pre-fill the consumable batch from what the weld already carries, the
      // way the canvas suggests it from the WPS.
      const j = this.joints.find(x => x.id === id)
      this.fillerBatch = (j && j.batch) || ''
      // Nothing recorded and nothing to inherit: open the field rather than
      // showing an em dash the welder has to discover is editable.
      this.batchEdit = !this.fillerBatch
      this.refreshBatchOptions()
      this.rejectMode = false
      // Hvert trinn fyller ut fra sin egen kolonne: sammenstillingen fra
      // Batch PF / Sammenstilling (PF), sveiseren fra Batch / Sveisesertifikat.
      this.pfBatch = (j && j.batch_pf) || ''
      // A new joint opens on the list again, not on the previous one's «Annen…».
      this.pfBatchTyping = false
      this.fillerBatchTyping = false
      this.pfOpen = false
      this.pfSearch = ''
      // Begge er lagret som CSV av expertise-id-er — vis det som allerede er
      // valgt på web.
      this.assemblyPfIds = this.csvIds(j && j.assembly_pf)
      this.weldCertIds = this.csvIds(j && j.certificate_refs)
      // WIT-FIX-PWA-WELDERCERT-001: the own picker's per-weld answer belongs to the weld it was fetched for.
      this.certOwnMatchState = null
      this.certOwnMessage = ''
      // WIT-IMPL-CERT-MATCH-003: no client-side pre-pick any more. The first
      // qualifying id (cert_qualified) is not the one the server's rule picks
      // when several qualify (narrowest), so the app and the canvas could sign
      // the same weld differently (C7). The server picks at «Ferdig sveist».
      /* 5183 / S18: seed one field per welding method from what is already
         recorded. The server answers with a single row carrying charge_nr when
         nothing per-method has been stored yet, so the first method starts
         pre-filled with today's value rather than blank — the welder completes
         the record instead of re-entering it. */
      this.methodBatches = {}
      const stored = (j && Array.isArray(j.method_batches)) ? j.method_batches : []
      ;((j && j.methods) || []).forEach((m, i) => {
        const hit = stored.find(x => String(x.method) === String(m))
        this.$set(this.methodBatches, m, hit ? (hit.batch || '') : (i === 0 ? ((j && j.batch) || '') : ''))
      })
      this.rejectReason = null
      this.rejectNote = ''
      this.levelUnlocked = false
      this.vtLevel = (j && j.vt_level) || null
      this.sheetBase = this.sheetSnapshot()
      this.unsavedHint = false
    },
    // Images go to the shared lightGallery mixin (zoom/rotate/fullscreen/
    // thumbnails). openGallery() is called directly rather than
    // previewDocuments(), because the photos are already loaded with the joint —
    // there is no /{id}/edit endpoint to refetch them from.
    //
    // PDFs deliberately do NOT go through lightGallery. document.download serves
    // them with `Content-Disposition: attachment`, and no browser will render an
    // attachment inside an iframe — Chrome saves the file instead and the slide
    // spins forever waiting for a load event that never comes. (The mixin's
    // Google-viewer workaround is also out: it makes Google fetch the URL, which
    // fails for any host not reachable from the public internet, and puts weld
    // documentation on an external service.) Instead reuse PdfViewer, the same
    // component Documents.vue previews with: vue-pdf pulls the bytes over XHR
    // and paints to canvas, so the attachment header is irrelevant.
    openPhotos (startIndex = 0) {
      // Samme liste som stripa viser — startIndex peker inn i den, ikke i det
      // ufiltrerte settet fra API-et.
      const photos = this.visiblePhotos
      if (!photos.length) return
      const clicked = photos[startIndex]
      if (clicked && !this.isImageDoc(clicked)) {
        this.openPdf(clicked)
        return
      }
      const images = photos.filter(ph => this.isImageDoc(ph))
      if (!images.length) return
      const items = images.map(ph => {
        const tag = ph.type && ph.type !== 'weldlog' ? ` — ${ph.type.toUpperCase()}` : ''
        const sub = `<h4>${ph.name || ''}${tag}</h4><p>${ph.addedAt || ''}</p>`
        return { src: ph.downloadUrl, thumb: ph.downloadUrl, subHtml: sub }
      })
      // startIndex counts the whole strip; map it onto the images-only list.
      const imageIndex = Math.max(0, images.indexOf(clicked))
      this.openGallery(items, imageIndex)
    },
    openPdf (doc) {
      this.pdfTitle = doc.name || ''
      this.pdfLink = doc.downloadUrl
      this.pdfDownloadable = true
      this.showPdf = true
    },
    // The API is not consistent about extension case ("PDF" / "JPG" come back
    // from some upload paths), so normalise before every comparison — an
    // uppercase extension used to fall through to the iframe branch and open
    // a photo in a blank frame instead of the zoomable image view.
    docExt (d) {
      return String((d && d.extention) || '').toLowerCase().replace(/^\./, '')
    },
    isImageDoc (d) {
      return ['png', 'jpg', 'jpeg', 'gif', 'bmp', 'webp'].includes(this.docExt(d))
    },
    // MAPPED -> FITTED. The server stamps fit_up and resolves the fitter
    // signature from the user, the way the canvas prefills that cell.
    // Valg i et batch-nedtrekk. «Annen…» bytter feltet til fritekst (tomt, klart
    // for skanning/skriving) i stedet for å lagre sentinel-verdien.
    pickBatch (field, value) {
      const model = field === 'pf' ? 'pfBatch' : 'fillerBatch'
      const typing = field === 'pf' ? 'pfBatchTyping' : 'fillerBatchTyping'
      if (value === BATCH_NEW) {
        this[model] = ''
        this[typing] = true
        return
      }
      this[model] = value
    },
    // Batch-listene ble hentet med sveisene. Et nummer lagt til med «+» på
    // nettsiden etterpå ville ellers mangle her til siden lastes på nytt, så
    // lista hentes igjen hver gang en sveis åpnes. Feiler det (f.eks. uten
    // nett), beholdes lista fra innlastingen.
    async refreshBatchOptions () {
      try {
        const { data } = await hostAppApi.get(
          `${this.getHostRefApi}project-drawing/${this.$route.params.drawing_id}/weld-mapping-batches`,
          { params: { user_id: this.hostUserId } }
        )
        const map = (data && data.batches) || {}
        this.joints.forEach(x => {
          if (!x.wps) return
          const fresh = (map[x.wps] || []).map(String)
          // Et nummer lagt til her som ikke har nådd serveren ennå beholdes.
          const pending = (x.filler_batches || []).map(String).filter(b => !fresh.includes(b))
          const next = fresh.concat(pending)
          if (JSON.stringify(next) !== JSON.stringify((x.filler_batches || []).map(String))) {
            this.$set(x, 'filler_batches', next)
          }
        })
      } catch (e) {
        // Ikke kritisk: lista fra weld-mapping-joints brukes.
      }
    },
    // En batch som skrives inn i fritekstfeltet registreres på sveisens WPS,
    // slik at neste sveis på samme WPS kan velge den fra lista i stedet for å
    // skrive den på nytt. @change, altså ved Enter eller når feltet forlates.
    commitTypedBatch (field) {
      const val = String((field === 'pf' ? this.pfBatch : this.fillerBatch) || '').trim()
      if (!val) return
      this.saveNewBatch(field, val)
    },
    // Legger nummeret til WPS-ens liste (samme liste som «+» på nettsiden) og
    // velger det. Lista oppdateres lokalt med én gang for alle sveiser med
    // samme WPS; feiler lagringen (f.eks. uten nett) følger nummeret likevel
    // med sveisen når den lagres.
    async saveNewBatch (field, val) {
      const wps = this.selectedJoint && this.selectedJoint.wps
      if (!val || !field) return
      if (wps) {
        this.joints.forEach(x => {
          if (x.wps !== wps) return
          const list = (x.filler_batches || []).map(String)
          if (!list.includes(val)) this.$set(x, 'filler_batches', list.concat([val]))
        })
      }
      if (field === 'pf') this.pfBatch = val
      else this.fillerBatch = val
      if (!wps) return
      try {
        await hostAppApi.post(
          `${this.getHostRefApi}project-drawing/${this.$route.params.drawing_id}/weld-mapping-custom-batch`,
          { user_id: this.hostUserId, wps_nr: wps, batch_nr: val }
        )
      } catch (e) {
        // Ikke kritisk: batchen lagres uansett på sveisen ved Lagre.
      }
    },
    closePfOnOutside (e) {
      if (this.pfOpen) {
        const el = this.$refs.pfsel
        if (el && !el.contains(e.target)) this.pfOpen = false
      }
      if (this.certOpen) {
        const el = this.$refs.certsel
        if (el && !el.contains(e.target)) this.certOpen = false
      }
    },
    togglePfOpen () {
      this.pfOpen = !this.pfOpen
      if (this.pfOpen) this.pfSearch = ''
    },
    // Vanlig av/på-avkryssing for alle sertifikater, også de som allerede var
    // lagret fra nettsiden — feltbrukeren kan rette et feil valg.
    pickPfCert (value) {
      this.assemblyPfIds = this.assemblyPfIds.includes(value)
        ? this.assemblyPfIds.filter(v => v !== value)
        : this.assemblyPfIds.concat([value])
    },
    /**
     * Persist a label dragged on the canvas.
     *
     * Writes the same label_x/label_y the PC canvas drags, so a callout moved
     * on the tablet opens in that position on the web. Only the label moves —
     * the marker keeps pointing at the joint.
     *
     * The canvas already shows the new position optimistically; on failure it
     * is told to drop that override, so the label snaps back rather than
     * sitting where nothing was saved.
     */
    async saveLabelPosition ({ id, x, y }) {
      try {
        await hostAppApi.post(
          `${this.getHostRefApi}weld-mapping/weld/${id}/label-position`,
          { label_x: x, label_y: y }
        )
        // Fold the saved position into the row the canvas renders from, so the
        // optimistic override can be released without the label jumping back.
        const j = this.joints.find(w => w.id === id)
        if (j) {
          this.$set(j, 'label_x', x)
          this.$set(j, 'label_y', y)
        }
        if (this.$refs.canvas) this.$refs.canvas.clearLabelMove(id)
      } catch (e) {
        if (this.$refs.canvas) this.$refs.canvas.clearLabelMove(id)
        this.actError = this.serverMessage(e)
      }
    },
    async registerFitUp () {
      if (this.acting || !this.pfBatch) return
      this.acting = true
      this.actError = null
      try {
        const { data } = await hostAppApi.post(
          `${this.getHostRefApi}weld-mapping/weld/${this.selected}/register-fitup`,
          {
            user_id: this.hostUserId,
            filler_batch: this.pfBatch,
            // Samme CSV-format som nettsidens flervalg skriver.
            certificate_refs: this.assemblyPfIds.join(', ') || null
          }
        )
        const j = this.joints.find(x => x.id === this.selected)
        if (j) {
          this.$set(j, 'fit_up', data.fit_up)
          this.$set(j, 'fitter', data.fitter)
          // Sammenstillingens egne kolonner. Sveiserens batch/sertifikat røres
          // ikke her — det er sveiserens eget trinn som skriver dem.
          this.$set(j, 'batch_pf', data.batch_pf)
          this.$set(j, 'assembly_pf', data.assembly_pf)
          this.$set(j, 'filler_batch', data.batch_pf)
          this.$set(j, 'status', data.weld_status)
        }
        this.selected = null
        this.advanceRoleIfDone()
        this.refreshSeriesCounts()
      } catch (e) {
        this.actError = this.serverMessage(e)
      } finally {
        this.acting = false
      }
    },
    startReject () {
      this.rejectMode = true
      this.actError = null
    },
    // Backing out of the picker leaves whatever brought us here intact: from a
    // correction it drops back into the edit form, otherwise into the verdict
    // buttons. The reason itself is cleared so a reopened picker starts blank.
    cancelReject () {
      this.rejectMode = false
      this.rejectReason = null
      this.rejectNote = ''
      this.actError = null
    },
    // Approve or reject. The server writes the same columns the PC canvas
    // writes, so deriveStatus() agrees on both sides.
    async vtVerdict (verdict) {
      if (this.acting) return
      this.acting = true
      this.actError = null
      try {
        const reason = verdict === 'rej'
          ? [this.rejectReason ? this.$t('message.weldMapping.faults.' + this.rejectReason) : '', this.rejectNote]
            .filter(Boolean).join(' — ')
          : null
        const { data } = await hostAppApi.post(
          `${this.getHostRefApi}weld-mapping/weld/${this.selected}/vt-verdict`,
          {
            user_id: this.hostUserId,
            verdict,
            vt_level: this.vtLevel || null,
            reason
          }
        )
        const j = this.joints.find(x => x.id === this.selected)
        if (j) {
          this.$set(j, 'vt', data.vt)
          this.$set(j, 'vt_level', data.vt_level)
          this.$set(j, 'qa_verdict', data.verdict)
          // Trust the server's derivation rather than recomputing it here.
          this.$set(j, 'status', data.weld_status)
          // The reject created the repair weld; mark this one superseded so it
          // stops offering an action immediately.
          if (data.revision) this.$set(j, 'superseded_by', data.revision)
        }
        // A rejection adds a new weld row (the repair). Patching in place
        // cannot show it, so refetch to bring it onto the map and list.
        if (data.revision) await this.loadJoints()
        this.selected = null
        this.advanceRoleIfDone()
        this.refreshSeriesCounts()
      } catch (e) {
        this.actError = this.serverMessage(e)
      } finally {
        this.acting = false
      }
    },
    // The camera modal hands back a File; post it to the weld row. VT files
    // its photo as the VT report, other roles attach it to the weld log.
    // The shared camera modal hands back a full-resolution PNG, which for a
    // phone camera is several MB — past PHP's upload_max_filesize, so the file
    // is discarded before Laravel sees it. Re-encode to a bounded JPEG first.
    downscalePhoto (file, maxEdge = 1600, quality = 0.82) {
      return new Promise(resolve => {
        const url = URL.createObjectURL(file)
        const img = new Image()
        img.onload = () => {
          const scale = Math.min(1, maxEdge / Math.max(img.width, img.height))
          const cv = document.createElement('canvas')
          cv.width = Math.round(img.width * scale)
          cv.height = Math.round(img.height * scale)
          cv.getContext('2d').drawImage(img, 0, 0, cv.width, cv.height)
          URL.revokeObjectURL(url)
          cv.toBlob(
            blob => resolve(blob
              ? new File([blob], file.name.replace(/\.png$/i, '') + '.jpg', { type: 'image/jpeg' })
              : file),
            'image/jpeg', quality
          )
        }
        // If it cannot be decoded, send the original and let the server judge.
        img.onerror = () => { URL.revokeObjectURL(url); resolve(file) }
        img.src = url
      })
    },
    // Picking from the gallery/files. The input is cleared afterwards so
    // choosing the same file twice still fires a change event.
    pickPhotos (event) {
      const files = Array.from(event.target.files || [])
      event.target.value = ''
      if (files.length) this.uploadPhoto(files)
    },
    // Accepts one File (camera) or several (file picker) — the endpoint takes
    // document[] as an array either way.
    async uploadPhoto (raw) {
      if (this.uploading || !this.selected) return
      const rawFiles = Array.isArray(raw) ? raw : [raw]
      if (!rawFiles.length) return
      this.uploading = true
      this.actError = null
      try {
        const files = await Promise.all(rawFiles.map(f => this.downscalePhoto(f)))
        const fd = new FormData()
        files.forEach(f => fd.append('document[]', f))
        fd.append('type', this.role === 'vt' ? 'vt' : 'weldlog')
        const { data } = await hostAppApi.post(
          `${this.getHostRefApi}weld-mapping/weld/${this.selected}/photo`,
          fd, { headers: { 'Content-Type': 'multipart/form-data' } }
        )
        const j = this.joints.find(x => x.id === this.selected)
        if (j) this.$set(j, 'photos', data.documents || [])
        this.$root.$emit('snackbar', {
          snackbar: true, color: 'success', text: this.$t('message.weldMapping.photoSaved')
        })
      } catch (e) {
        this.actError = this.serverMessage(e)
      } finally {
        this.uploading = false
      }
    },
    // Marks the weld finished: weld_end + signature + consumable batch. The
    // signature is resolved server-side from the logged-in user.
    /* WIT-IMPL-CERT-MATCH-003 — the dialog behind «Bekreft sertifikat» and the
       override reason. Resolves with the picked id / the reason, or null. */
    askCert (mode, message, options, required, note) {
      return new Promise(resolve => {
        this.certDialog = {
          show: true,
          mode,
          message: message || '',
          // P-C: the «Bekreftet manuelt» line (confirm mode only).
          note: note || '',
          options: options || [],
          pick: options && options.length ? options[0].value : null,
          reason: '',
          required: !!required,
          error: '',
          resolve
        }
      })
    },
    certDialogDone (ok) {
      const d = this.certDialog
      if (ok && d.mode === 'reason' && d.required && !String(d.reason || '').trim()) {
        d.error = this.$t('message.weldMapping.certReasonRequired')
        return
      }
      const value = !ok ? null : (d.mode === 'confirm' ? d.pick : String(d.reason || '').trim())
      this.certDialog = Object.assign({}, d, { show: false, resolve: null })
      if (d.resolve) d.resolve(value)
    },
    certVerdictClass (v) {
      return v === 'qualified' ? 'ok'
        : (v === 'cannot_verify' || v === 'cannot_verify_confirmed') ? 'warn'
          : v === 'override' ? 'override' : 'bad'
    },
    /* WIT-FIX-WELDMAP-002 3.4: the server's `ranked` list (best first) as
       dialog rows — ✓ / ? / ✗, the certificate's label, range and reason. */
    certRankedOptions (ranked) {
      return (Array.isArray(ranked) ? ranked : []).map(r => {
        const k = this.certVerdictClass(r.verdict)
        return {
          value: String(r.id),
          label: this.certLabels([String(r.id)]) || String(r.id),
          mark: k === 'ok' ? '✓' : k === 'warn' ? '?' : '✗',
          summary: r.summary || '',
          why: r.why || ''
        }
      })
    },
    certVerdictMark (v) {
      const k = this.certVerdictClass(v)
      // WIT-FIX-RETEST-1008-001 P-C: the welder's own confirmation reads as such.
      if (v === 'cannot_verify_confirmed') return '? ' + this.$t('message.weldMapping.certVerdictConfirmed')
      return (k === 'ok' ? '✓ ' + this.$t('message.weldMapping.certVerdictQualified')
        : k === 'warn' ? '? ' + this.$t('message.weldMapping.certVerdictUnverified')
          // WIT-IMPL-CERT-001 P3: the coordinator's decision, not a failed check.
          : k === 'override' ? '! ' + this.$t('message.weldMapping.certVerdictOverride')
            : '✗ ' + this.$t('message.weldMapping.certVerdictNotQualified'))
    },
    /* Spec §2.3 steps 2–5: before «Ferdig sveist» is posted, ask the server
       which of the welder's own certificates this weld is welded under — the
       same CertificateAutoSelector the web canvas uses, so both pick alike
       (C7). A certificate already chosen is respected. Returns false when the
       sign-off should not go ahead (the office cancelled a required reason).

       WIT-IMPL-CERT-001 P2: a welder is never asked anything — not to confirm
       a certificate, not for a reason. The pick is posted as it is and the
       server decides: «Varsle» signs and flags (toast), «Blokker» refuses and
       says why, «Av» signs. Only the office (project certificate scope, from
       the server) still answers the confirm / reason dialogs, because only the
       office's answer counts on the server. */
    async prepareCertificate () {
      this.certConfirmed = false
      this.certOverrideReason = ''
      this.certSelection = this.weldCertIds.length ? 'manual' : null
      // P4: a pick from «Vis alle sveisere i firmaet» needs the office's reason.
      if (this.certOffice && !this.editMode && this.certPickNeedsOverride) {
        const reason = await this.askCert('reason', this.$t('message.weldMapping.certNonParticipantReason'), [], true)
        if (reason === null) return false
        this.certOverrideReason = reason
        this.certSelection = 'override'
        return true
      }
      if (this.weldCertIds.length || this.editMode) return true
      let data = null
      try {
        ({ data } = await hostAppApi.get(
          `${this.getHostRefApi}weld-mapping/weld/${this.selected}/certificate-autoselect`,
          { params: { user_id: this.hostUserId } }
        ))
      } catch (e) {
        // The server still judges (and records) the sign-off; under «Blokker»
        // it refuses a welder whose check cannot run (D-CERT-8).
        return true
      }
      if (!data || !data.enabled) {
        this.autoSelectEnabled = false
        return true
      }
      this.autoSelectEnabled = true
      const ids = (data.ids || []).map(String)
      const candidates = (data.candidates || []).map(String)
      const mode = data.enforcement || 'warn'
      if (data.result === 'auto' && ids.length) {
        this.weldCertIds = ids
        this.certSelection = 'auto'
        return true
      }
      if (!this.certActsAsOffice) {
        /* WIT-FIX-RETEST-1008-001 (Erik, retest 08.10.2026):
           P-D «Blokker» + nothing usable: the server's padlock stops here —
               told, never asked, nothing posted (register-weld refuses too).
           P-C nothing verifies but the server still offers candidates (own,
               relevant, never not_qualified — P-B): the «Bekreft sertifikat»
               dialog, figure 1, with the «Bekreftet manuelt» line; the pick
               is posted as cert_confirmed and stored as the manual marker.
           Otherwise the server judges the sign-off as before. */
        const decision = welderCertDecision(data)
        if (decision.action === 'blocked') {
          this.actError = decision.message || this.$t('message.weldMapping.certLockedMsg')
          const j = this.selectedJoint
          if (j) this.$set(j, 'cert_lock', true)
          return false
        }
        if (decision.action === 'confirm') {
          const options = confirmOptions(decision.candidates, data.verdicts, id => this.certLabels([id]))
          if (options.length) {
            const note = needsManualNote(options) ? this.$t('message.weldMapping.certConfirmManualNote') : ''
            const pick = await this.askCert('confirm', decision.message, options, false, note)
            if (pick === null) return false
            this.weldCertIds = Array.from(new Set(decision.ids.concat([String(pick)])))
            this.certConfirmed = true
            this.certSelection = 'confirmed'
            return true
          }
        }
        /* WIT-FIX-CERT-5355-001 F1 / F2 / F5b (Erik 15.10, section 3): nothing
           the welder holds covers the weld → the short dialog: the one-line
           reason and a Begrunnelse field, no list of failing certificates.
           AVBRYT closes it and nothing is posted; SIGNER posts the reason,
           which the server keeps as the welder's note on the weld (the
           coordinator reads it when the certificate arrives). Nothing is
           persisted before this point. */
        if (welderReasonNeeded(data, decision)) {
          const reason = await this.askCert(
            'reason',
            data.message || this.$t('message.weldMapping.certNotQualifiedLead'),
            [],
            true,
            this.$t('message.weldMapping.certReasonWelder')
          )
          const outcome = officeReasonOutcome(reason)
          if (!outcome.proceed) return false
          this.certOverrideReason = outcome.reason
        }
        if (decision.ids.length) {
          this.weldCertIds = decision.ids
          this.certSelection = 'auto'
        }
        return true
      }
      if (data.result === 'needs_confirmation' && candidates.length) {
        const options = candidates.map(id => ({
          value: id,
          label: this.certLabels([id]) || id,
          why: ((data.verdicts || {})[id] || {}).why || ''
        }))
        const pick = await this.askCert('confirm', data.message, options)
        // WIT-FIX-CERT-5348-001 F1: AVBRYT cancels the sign-off — it used to
        // fall through to the reason dialog.
        if (pick === null) return false
        if (pick) {
          this.weldCertIds = Array.from(new Set(ids.concat([String(pick)])))
          this.certConfirmed = true
          this.certSelection = 'confirmed'
          return true
        }
      }
      if (mode === 'off') return true
      // WIT-FIX-WELDMAP-002 3.4: list the certificates best first, each with
      // its own reason, instead of one generic line.
      // WIT-FIX-CERT-5348-001 F5a: one sentence — a row's reason the message
      // already gives is not repeated under it.
      const reason = await this.askCert('reason', data.message, dedupeRankedWhy(data.message, this.certRankedOptions(data.ranked)), mode === 'block')
      // F1 / D-CERT-5: AVBRYT never signs, in any mode (it used to return
      // true under «Varsle», so cancel signed the weld). SIGNER with text is
      // the office's override; SIGNER without text under «Varsle» is a plain
      // sign-off the server flags (not an override).
      const outcome = officeReasonOutcome(reason)
      if (!outcome.proceed) return false
      if (ids.length) this.weldCertIds = ids
      this.certOverrideReason = outcome.reason
      if (outcome.selection) this.certSelection = outcome.selection
      return true
    },
    toggleCertOpen () {
      this.certOpen = !this.certOpen
      if (this.certOpen) {
        this.certSearch = ''
        // Verdicts for the weld's CURRENT saved values, fetched each time the
        // picker opens (the web does the same).
        this.loadCertMatches()
      }
    },
    async loadCertMatches () {
      const j = this.selectedJoint
      // A weld not on the server yet (queued offline) has no row to match.
      if (!j || !/^\d+$/.test(String(j.id)) || !navigator.onLine) return
      const id = j.id
      this.certMatchLoading = true
      try {
        const ids = this.certShowAll ? (this.tenantCerts || []).map(c => c.value).join(',') : ''
        const { data } = await hostAppApi.get(
          `${this.getHostRefApi}weld-mapping/weld/${id}/certificate-matches`,
          { params: { user_id: this.hostUserId, ids } }
        )
        if (!this.selectedJoint || this.selectedJoint.id !== id) return
        this.certMatches = data && data.enabled && data.matches ? data.matches : null
        this.certMatchFor = id
        // WIT-FIX-PWA-WELDERCERT-001 step 4: the own branch's state and its
        // sentence (welder_id_missing / unqualified_row) — never a wider list.
        this.certOwnMatchState = (data && data.state) || null
        this.certOwnMessage = (data && data.message) || ''
      } catch (e) {
        // The plain list (no verdicts) rather than an empty picker.
        if (this.selectedJoint && this.selectedJoint.id === id) {
          this.certMatches = null
          this.certMatchFor = id
        }
      } finally {
        if (!this.selectedJoint || this.selectedJoint.id === id) this.certMatchLoading = false
      }
    },
    async onShowAllWelders () {
      const j = this.selectedJoint
      if (!this.certShowAll || !j) return this.loadCertMatches()
      try {
        const { data } = await hostAppApi.get(
          `${this.getHostRefApi}weld-mapping/weld/${j.id}/certificate-tenant-list`,
          { params: { user_id: this.hostUserId } }
        )
        this.tenantCerts = (data && data.certificates) || []
      } catch (e) {
        this.certShowAll = false
        this.tenantCerts = []
        this.actError = this.serverMessage(e)
      }
      this.loadCertMatches()
    },
    pickWeldCert (value) {
      this.weldCertIds = this.weldCertIds.includes(value)
        ? this.weldCertIds.filter(v => v !== value)
        : this.weldCertIds.concat([value])
    },
    /* WIT-IMPL-CERT-001 P3: a signed joint's certificates as short labels —
       the sign response's cert_label while it matches, else the offer list. */
    certShortLabelsFor (j) {
      if (!j) return ''
      const ids = this.csvIds(j.certificate_refs)
      if (j.cert_label && ids.length) return j.cert_label
      return ids
        .map(id => {
          const hit = (this.certificates || []).find(c => String(c.value) === String(id))
          return hit ? (hit.shortLabel || hit.label) : id
        })
        .join('; ')
    },
    /* WIT-IMPL-CERT-001 P3: «Angre signering». The server clears the weld end,
       the signature and an automatically chosen certificate, and refuses once
       VT/NDT is registered or the sign-off is someone else's. */
    async undoWeld () {
      const j = this.selectedJoint
      if (!j || this.acting) return
      this.acting = true
      this.undoError = null
      try {
        const { data } = await hostAppApi.post(
          `${this.getHostRefApi}weld-mapping/weld/${j.id}/undo-weld`,
          { user_id: this.hostUserId }
        )
        this.$set(j, 'weld_end', null)
        this.$set(j, 'weld_end_date', null)
        this.$set(j, 'welder', '')
        this.$set(j, 'cert_verdict', data.cert_verdict || null)
        this.$set(j, 'certificate_refs', data.certificate_refs || '')
        this.$set(j, 'cert_label', '')
        this.$set(j, 'welder_id', data.welder_ids || null)
        this.$set(j, 'welder_id_missing', false)
        this.$set(j, 'can_undo', false)
        this.$set(j, 'status', data.weld_status)
        this.weldCertIds = this.csvIds(data.certificate_refs)
        this.toast(this.$t('message.weldMapping.undoSignDone', { weld: j.weld_nr || '' }))
        this.refreshSeriesCounts()
      } catch (e) {
        this.undoError = this.serverMessage(e)
      } finally {
        this.acting = false
      }
    },
    async registerWeld (retried = false) {
      // Guarded here too: a superseded weld must not be re-welded even if the
      // handler is reached another way. The padlock does not stop the attempt:
      // the server is the one that refuses under «Blokker» and says why
      // (WIT-IMPL-CERT-001 P2) — only the office may answer with a reason.
      if (this.acting || this.superseded) return
      // Strictly true: a click handler would pass the event object here.
      const isRetry = retried === true
      if (!isRetry && !(await this.prepareCertificate())) {
        // F1 side bug: the finally below is skipped on this return, so the
        // «signer likevel» gaps would otherwise ride on the next weld.
        this.signedGaps = []
        return
      }
      this.acting = true
      this.actError = null
      let retry = null
      let retryWhy = ''
      try {
        const { data } = await hostAppApi.post(
          `${this.getHostRefApi}weld-mapping/weld/${this.selected}/register-weld`,
          {
            user_id: this.hostUserId,
            filler_batch: this.fillerBatch || null,
            // Sveiserens eget sertifikat, samme CSV-format som sammenstillingens.
            certificate_refs: this.weldCertIds.join(', ') || null,
            /* 5183 / R-3.2: hvilke sporbarhetsfelt sveiseren signerte UTEN, når
               «Manglende opplysninger» ble besvart med «Ja, signer likevel».
               Advarselen var — og forblir — rådgivende, men valget sto ingen
               steder etterpå: ingen nedstrøms kunne skille en sveis der
               sporbarheten aldri ble fanget fra en der den bare ikke er
               gjennomgått ennå. Serveren fører det som en egen hendelse i
               revisjonsloggen. */
            traceability_gaps: this.signedGaps,
            /* 5183 / S18: one entry per welding method, sent only when the weld
               HAS more than one — a single-method weld keeps sending
               filler_batch alone and the server behaves exactly as before.
               The server keeps charge_nr equal to the first entry, so every
               reader that knows only that column still sees a true value. */
            method_batches: this.methodBatchPayload,
            // WIT-IMPL-CERT-MATCH-003: the welder's answers, judged and stored
            // by the server (cert_verdict).
            cert_confirmed: this.certConfirmed,
            cert_override_reason: this.certOverrideReason || null,
            cert_selection: this.certSelection
          }
        )
        const j = this.joints.find(x => x.id === this.selected)
        if (j) {
          this.$set(j, 'cert_verdict', data.cert_verdict || null)
          // WIT-FIX-CERT-5348-001 F2: the office's override, shown at once.
          this.$set(j, 'cert_override', data.cert_override || null)
          this.$set(j, 'weld_end', data.weld_end)
          this.$set(j, 'weld_end_date', data.weld_end_date)
          this.$set(j, 'welder', data.welder)
          // WIT-FIX-WELDMAP-002 3.5: the welder id the server set on sign
          // (the signer's profile id), and «Sveiser-ID mangler» when none.
          if (data.welder_ids !== undefined) this.$set(j, 'welder_id', data.welder_ids || null)
          this.$set(j, 'welder_id_missing', !!data.welder_id_missing)
          this.$set(j, 'batch', data.batch)
          this.$set(j, 'certificate_refs', data.certificate_refs)
          // WIT-IMPL-CERT-001 P3: the chosen certificate as one read-only line.
          this.$set(j, 'cert_label', data.cert_label || '')
          // Serveren avleder statusen: en reparasjon nullstiller vt_sign her, så
          // en lokal «vt ? COMPLETED : WELDED»-gjetning kan bomme på raden.
          this.$set(j, 'status', data.weld_status)
          // WIT-IMPL-M101-PWA-001: the server's NDT context after the sign
          // (earliest NDT now follows weld_end) and the hold carrier.
          if (data.ndt_context !== undefined) this.$set(j, 'ndt_context', data.ndt_context)
          if (Array.isArray(data.hold_warnings)) this.$set(j, 'hold_warnings', data.hold_warnings)
          // WIT-FIX-CERT-5355-001 F2-c: the Endringslogg line of this sign-off, no reload.
          if (Array.isArray(data.audit)) this.$set(j, 'audit', data.audit)
        }
        this.selected = null
        // WIT-IMPL-CERT-001 P2: «Varsle» — signed and flagged for the welding
        // coordinator. One line, the server's own wording and reason; no dialog.
        if (data.flagged) {
          this.toast(data.flag_message || this.$t('message.weldMapping.certFlaggedToast'), true)
        }
        this.advanceRoleIfDone()
        this.refreshSeriesCounts()
      } catch (e) {
        const body = (e && e.response && e.response.data) || {}
        // The server found the weld locked (stale list, or a WPS/material edit
        // changed what qualifies): show the padlock now rather than on reload.
        if (body.cert_lock) {
          const j = this.joints.find(x => x.id === this.selected)
          if (j) this.$set(j, 'cert_lock', true)
        }
        // Refused under «Blokker». The office is asked once and the request
        // sent again; a welder is told why and nothing else (WIT-IMPL-CERT-001
        // P2) — the weld stays as it was. The server answers reason_required /
        // confirm_required only for the office.
        if (!isRetry && body.reason_required && !this.certOverrideReason) {
          retry = 'reason'
          retryWhy = String(body.why || body.message || '').trim()
        } else if (!isRetry && body.confirm_required && !this.certConfirmed) {
          retry = 'confirm'
        } else {
          const why = typeof body.why === 'string' ? body.why.trim() : ''
          const msg = this.serverMessage(e)
          this.actError = why && !msg.includes(why) ? msg + ' ' + why : msg
        }
      } finally {
        this.acting = false
        if (!retry) {
          // One sign-off, one acknowledgement — never carried to the next weld.
          this.signedGaps = []
        }
      }
      if (retry === 'reason') {
        // WIT-FIX-CERT-5355-001 D-5355-1: the server asks everyone for a reason
        // on a sign-off no certificate covers — the same short dialog.
        const reason = await this.askCert(
          'reason', retryWhy, [], true,
          this.certActsAsOffice ? '' : this.$t('message.weldMapping.certReasonWelder')
        )
        const outcome = officeReasonOutcome(reason)
        if (outcome.proceed && outcome.reason) {
          this.certOverrideReason = outcome.reason
          if (this.certActsAsOffice) this.certSelection = 'override'
          return this.registerWeld(true)
        }
        this.signedGaps = []
      } else if (retry === 'confirm') {
        const options = this.weldCertIds.map(id => ({ value: String(id), label: this.certLabels([String(id)]) || String(id) }))
        const pick = await this.askCert('confirm', '', options, false, this.$t('message.weldMapping.certConfirmManualNote'))
        if (pick) {
          this.certConfirmed = true
          this.certSelection = 'confirmed'
          return this.registerWeld(true)
        }
        this.signedGaps = []
      }
    },
    // Material list for the picker. Fetched once per screen; a failure leaves
    // the array empty and the field degrades to free text rather than blocking.
    // With drawing_id the drawing's Materialliste rows and the hand-added heats
    // come along (5237). Fetched each time the sheet opens, so a heat added on
    // the web meanwhile is offered; a failure keeps what was loaded before.
    async loadMaterialTypes () {
      try {
        const { data } = await hostAppApi.get(
          `${this.getHostRefApi}weld-mapping/material-types`,
          { params: { drawing_id: this.$route.params.drawing_id } }
        )
        this.materialTypes = (data && data.types) || []
        this.materialListRows = (data && data.material_list_rows) || []
        this.customHeats = (data && data.custom_heats) || {}
      } catch (e) {
        // Keep the lists from an earlier load; the field degrades to free text.
      }
      // The sheet opened before the lists arrived: fill a blank heat now.
      if (this.matEdit && this.matEdit.type && !this.matEdit.heat) this.applyTypeHeat(false)
    },
    // Material Type -> Heat, like WPS -> Batch (web: autoFillMaterialHeat).
    // `changed`: the type really differs from the last one — a heat not listed
    // for the new type (or any heat once the type is cleared) is dropped.
    // Then exactly one registered heat for the type is filled in.
    // Pos nr is never filled: it is entered by the user.
    applyTypeHeat (changed) {
      const m = this.matEdit
      if (!m) return
      const groups = this.heatGroups
      if (changed) {
        this.heatTyping = false
        if (m.heat && (!m.type || !groups.all.includes(m.heat))) this.$set(m, 'heat', '')
      }
      if (!m.heat && groups.registered.length === 1) this.$set(m, 'heat', groups.registered[0])
    },
    // Called when the type is committed (picked, or the field is left).
    onTypeCommitted () {
      const m = this.matEdit
      if (!m) return
      const changed = matTypeKey(this.typeBefore) !== matTypeKey(m.type)
      this.typeBefore = m.type || ''
      this.applyTypeHeat(changed)
    },
    // A type picked from the list is locked: typing cannot append to it,
    // Backspace/Delete clears it whole (and with it the heat); tapping the
    // field opens the list to pick another. Free text only while nothing is
    // picked — the same rule as the web canvas.
    onTypeBeforeInput (e) {
      if (!this.typeLocked) return
      e.preventDefault()
      if (String(e.inputType || '').startsWith('delete')) {
        this.$set(this.matEdit, 'type', '')
        this.typePicked = null
        this.onTypeCommitted()
        this.openTypeMenu()
      }
    },
    pickHeat (value) {
      if (value === BATCH_NEW) {
        this.$set(this.matEdit, 'heat', '')
        this.heatTyping = true
        return
      }
      this.$set(this.matEdit, 'heat', value)
    },
    // A heat typed in the free-text field is added to the type's list, so the
    // next weld can pick it (same list as the canvas «+»). @change: Enter or
    // leaving the field. Without a type there is no list to add it to.
    async commitTypedHeat () {
      const m = this.matEdit
      const val = String((m && m.heat) || '').trim()
      const type = String((m && m.type) || '').trim()
      if (!val) return
      // No type (no list to add to) or already listed: just show it selected.
      if (!type || this.heatGroups.all.includes(val)) {
        this.heatTyping = false
        return
      }
      const map = Object.assign({}, this.customHeats || {})
      map[type] = (Array.isArray(map[type]) ? map[type] : []).concat([val])
      this.customHeats = map
      // Back to the dropdown, now showing the new number selected.
      this.heatTyping = false
      try {
        const { data } = await hostAppApi.post(
          `${this.getHostRefApi}project-drawing/${this.$route.params.drawing_id}/weld-mapping-custom-heat`,
          { user_id: this.hostUserId, material_type: type, heat_nr: val }
        )
        if (data && data.custom_heats) this.customHeats = data.custom_heats
      } catch (e) {
        // Not critical: the heat is saved on the material row anyway.
      }
    },
    // index -1 = new row; otherwise edit the existing one in place.
    openMaterial (index) {
      // Guarded here too, not only on the buttons: the editor must not open
      // for a mode/status that may not change materials.
      // Adding a row (-1) is a full-edit action; opening an existing row goes
      // through the per-row gate, which also lets a blank heat be filled in.
      const rows = this.selectedJoint.materials || []
      const src = index >= 0 ? rows[index] : null
      if (index < 0 ? !this.canEditMaterials : !this.canEditMaterialRow(src)) return
      this.matError = null
      this.closeTypeMenu()
      this.loadMaterialTypes()

      /* 5183 / S6: a second material on the same joint is almost always the
         same steel at the same thickness — Erik asked for it to be copied from
         the first so it does not have to be typed again.

         TYPE AND THICKNESS ONLY. Heat/charge and pos. nr. identify one physical
         piece of steel; pre-filling those would let a welder sign a traceability
         record for material they never looked at, which is a falsification risk,
         not a convenience. They stay blank on purpose. */
      const seed = index < 0 ? (rows[0] || null) : null

      this.matEdit = {
        index,
        type: (src && src.type) || (seed && seed.type) || '',
        heat: (src && src.heat) || '',
        thick: src && src.thick !== null && src.thick !== undefined
          ? String(src.thick)
          : (seed && seed.thick !== null && seed.thick !== undefined ? String(seed.thick) : ''),
        pos_nr: (src && src.pos_nr) || ''
      }
      this.heatTyping = false
      this.typeBefore = this.matEdit.type
      this.typePicked = null
      // A stored type that is a list option counts as picked (typeLocked).
      this.typeOpened = this.matEdit.type
      this.matBase = this.matSnapshot()
      // Heat from the Materialliste for a type without one (lists from an
      // earlier open; loadMaterialTypes() repeats this when it returns). After
      // the snapshot on purpose: the filled heat is an unsaved change.
      if (this.matEdit.type && !this.matEdit.heat) this.applyTypeHeat(false)
      this.matUnsavedHint = false
    },
    // Materialskjemaets felter som én sammenlignbar streng.
    matSnapshot () {
      const m = this.matEdit
      return m ? JSON.stringify([m.type, m.heat, m.thick, m.pos_nr]) : null
    },
    matDirty () {
      return this.matBase !== null && this.matSnapshot() !== this.matBase
    },
    /**
     * Klikk på bakgrunnen ved siden av materialarket. Samme trapp som
     * scrimClose: åpen materialtype-liste lukkes alene, ulagret utfylling
     * blir stående til Avbryt trykkes.
     */
    matScrimClose () {
      if (this.typeMenuOpen) {
        this.closeTypeMenu()
        return
      }
      if (this.matDirty()) {
        this.matUnsavedHint = true
        return
      }
      this.closeMaterial()
    },
    closeMaterial () {
      this.matEdit = null
      this.matBase = null
      this.matUnsavedHint = false
      this.matError = null
      this.closeTypeMenu()
    },
    // True while a correction session is open on this joint. Materials are saved
    // by their own sheet, so editMode has to outlive a material save — otherwise
    // adding one row re-locks the card and the "+" needed for the next one is
    // gone.
    // What one joint's material traceability is still missing, per row. All four
    // fields the material sheet writes are checked — a row with no thickness or
    // no type is as unusable for the as-built as one with no heat.
    jointGaps (joint) {
      const rows = []
      ;((joint && joint.materials) || []).forEach((m, index) => {
        const missing = []
        if (!String(m.type || '').trim()) missing.push('type')
        if (!String(m.heat || '').trim()) missing.push('heat')
        if (!String(m.thick === 0 ? '0' : (m.thick || '')).trim()) missing.push('thickness')
        // WIT-IMPL-WPS-5324-003 (O3, D-5324-8): a gap only where the drawing's
        // material list uses position numbers (server: uses_pos_nr).
        if (this.usesPosNr && !String(m.pos_nr || '').trim()) missing.push('pos_nr')
        if (missing.length) rows.push({ index, missing })
      })
      return rows
    },
    // The open joint's gaps — what the single sign-off warning shows.
    materialGaps () {
      return this.jointGaps(this.selectedJoint)
    },
    /**
     * Run a sign-off, warning first if the material record has holes.
     *
     * The warning is advisory: `run` is kept and replayed verbatim when the user
     * confirms, so the sign-off path itself is unchanged and there is no second
     * copy of it to drift. A joint with no material rows at all is NOT held up —
     * there is nothing to complete, and the drawing may simply not track
     * materials on that weld.
     */
    withGapCheck (run) {
      const rows = this.materialGaps()
      if (!rows.length) return run()
      this.gapWarn = { rows, run }
    },
    confirmGapWarn () {
      const w = this.gapWarn
      // The distinct field names the welder is signing past, flattened from the
      // per-material rows — that is what the audit note records (5183 / R-3.2).
      this.signedGaps = w
        ? Array.from(new Set((w.rows || []).flatMap(r => r.missing || [])))
        : []
      this.gapWarn = null
      if (w) w.run()
    },
    cancelGapWarn () {
      this.gapWarn = null
      this.signedGaps = []
    },
    /**
     * The record being signed off on one weld, as label/value pairs.
     *
     * Only fields that carry a value: a bulk confirmation listing eight empty
     * rows per weld would hide the ones that matter. Certificates resolve to
     * names through the same helper the pickers use, so this reads as the field
     * that set it does.
     */
    bulkDetails (j) {
      const t = k => this.$t('message.weldMapping.' + k)
      // Reuses the short audit labels for the batch/certificate fields: the form
      // labels are full sentences ("Batch welding filler material") and would
      // swamp a detail row.
      const rows = [
        ['wps', j.wps],
        ['process', j.process],
        ['auditField.filler', j.filler],
        ['auditField.fillerBatch', j.filler_batch || j.batch],
        ['auditField.batchPf', j.batch_pf],
        ['auditField.vtLevel', j.vt_level],
        ['auditField.certificateRefs', this.certLabels(this.csvIds(j.certificate_refs))],
        ['auditField.assemblyPf', this.certLabels(this.csvIds(j.assembly_pf))],
        // Timestamps arrive as ISO strings; formatted here the same way the
        // traceability chain formats them, not printed raw.
        ['bulkFitUp', this.$options.filters.dt(j.fit_up)],
        ['bulkWeldEnd', this.$options.filters.dt(j.weld_end)]
      ]
      return rows
        .filter(([, v]) => v !== null && v !== undefined && String(v).trim() !== '')
        .map(([k, v]) => ({ k: t(k), v: String(v) }))
    },
    // One material row as the sheet shows it elsewhere: grade, thickness, heat.
    bulkMaterialLine (m) {
      const parts = [
        m.type || '—',
        (m.thick ? m.thick + ' mm' : null),
        (m.heat ? this.$t('message.weldMapping.heat') + ' ' + m.heat : null),
        (m.pos_nr ? this.$t('message.weldMapping.posLabel') + ' ' + m.pos_nr : null)
      ]
      return parts.filter(Boolean).join(' · ')
    },
    // Maps a server gap code to its label key.
    gapKey (k) {
      // WIT-FIX-CERT-5355-001: type / thickness were shown as raw keys.
      return { heat: 'gapHeat', pos_nr: 'gapPosNr', no_materials: 'gapNoMaterials', type: 'gapType', thickness: 'gapThickness' }[k] || k
    },
    /** An audit action as its label — never the key path (WIT-FIX-CERT-5355-001). */
    auditActionLabel (action) {
      const key = 'message.weldMapping.auditAction.' + action
      return this.$te(key) ? this.$t(key) : humaniseKey(action)
    },
    /**
     * Label for an audited material field. The server sends the field name, not
     * a translated string, so the same entry reads in whatever language the
     * viewer is in — not the one the editor happened to use.
     */
    auditFieldLabel (f) {
      const keys = {
        // Material row fields.
        type: 'materialType',
        heat: 'heatNo',
        thickness: 'thickness',
        pos_nr: 'posLabel',
        // Weld-log fields, patched one at a time from the sheet. These use
        // their own short audit labels: the form labels are full sentences
        // ("Batch welding filler material") and would swamp a history line.
        filler_batch: 'auditField.fillerBatch',
        batch_pf: 'auditField.batchPf',
        filler: 'auditField.filler',
        certificate_refs: 'auditField.certificateRefs',
        assembly_pf: 'auditField.assemblyPf',
        vt_level: 'auditField.vtLevel',
        // WIT-FIX-CERT-5348-001 F2: the certificate decision.
        cert_verdict: 'auditField.certVerdict',
        cert_override_reason: 'auditField.certOverrideReason',
        cert_unqualified_sign: 'auditField.certUnqualifiedSign',
        traceability_gaps: 'auditField.traceabilityGaps',
        // WIT-FIX-CERT-5355-001 3 d: a certificate registered afterwards.
        cert_registered_later: 'auditField.certRegisteredLater'
      }
      return keys[f] ? this.$t('message.weldMapping.' + keys[f]) : humaniseKey(f)
    },
    /**
     * What an audit entry was about: a material row is named by its pos, a
     * weld-log field by the field's own label.
     */
    auditSubjectLabel (e) {
      if (e.subject === 'field' || e.subject === 'event') return this.auditFieldLabel(e.ref)
      return e.pos_nr
        ? this.$t('message.weldMapping.posLabel') + ' ' + e.pos_nr
        : this.$t('message.weldMapping.materials')
    },
    /**
     * Whether a change renders stacked rather than inline.
     *
     * Driven by the rendered length of the two values, not by which field it is:
     * a single short certificate reads fine inline, and a long free-text batch
     * would wrap just as badly as a certificate list.
     */
    auditStacked (c) {
      if (c.from === null || c.to === null) return false
      // More than one value on either side always stacks: the parts render as
      // their own lines, which cannot sit inside an inline "old → new".
      const parts = v => this.auditValueParts(c.field, v)
      if (parts(c.from).length > 1 || parts(c.to).length > 1) return true
      const len = v => parts(v).join(', ').length
      return len(c.from) + len(c.to) > 40
    },
    /**
     * Display form of one audited value.
     *
     * Certificates are stored as raw expertise ids — the trail has to resolve
     * them against the same list the pickers use, or the history would read
     * "12 → 15" and mean nothing to the person checking it. Ids with no match
     * (a certificate since removed) fall back to the id, which is still better
     * evidence than blanking them.
     */
    auditValue (field, v) {
      return this.auditValueParts(field, v).join(', ')
    },
    /**
     * One audited value as the parts to render.
     *
     * A certificate field holds a CSV of ids, and the names themselves contain
     * numbers and spaces ("014 Type C ISO 9606-1 1 1 1 1 1 s1 D1 1 1"), so
     * comma-joining several of them produces one unreadable run. Returning the
     * parts lets the template put each certificate on its own line.
     */
    auditValueParts (field, v) {
      const raw = (v === null || v === undefined || v === '') ? '' : String(v)
      if (raw === '') return ['—']
      // F2: a verdict reads as its label; the flagged sign-off's «id:verdict»
      // pairs (or the bare verdict) as «certificate — verdict».
      if (field === 'cert_verdict') return [this.auditVerdictWord(raw)]
      if (field === 'cert_unqualified_sign') {
        return raw.split(',').map(p => p.trim()).filter(Boolean).map(p => {
          const m = p.match(/^(\d+):(.+)$/)
          return m ? (this.certLabels([m[1]]) || m[1]) + ' — ' + this.auditVerdictWord(m[2]) : this.auditVerdictWord(p)
        })
      }
      // WIT-FIX-CERT-5355-001: the gap codes («heat, pos_nr») as their labels.
      if (field === 'traceability_gaps') {
        return [raw.split(',').map(p => p.trim()).filter(Boolean).map(p => {
          const k = this.gapKey(p)
          return k === p ? humaniseKey(p) : this.$t('message.weldMapping.' + k)
        }).join(', ')]
      }
      if (field !== 'certificate_refs' && field !== 'assembly_pf') return [raw]
      // Reuses the same id -> label resolution the pickers use, so the history
      // names a certificate exactly as the field that set it does.
      const ids = this.csvIds(raw)
      const parts = ids.map(id => this.certLabels([id]))
      return parts.length ? parts : [raw]
    },
    /* WIT-FIX-CERT-5348-001 F2: a stored verdict value as the sheet's own word. */
    auditVerdictWord (v) {
      const known = ['qualified', 'cannot_verify', 'cannot_verify_confirmed', 'not_qualified', 'override']
      if (v === 'not_checked') return this.$t('message.weldMapping.certVerdictNotChecked')
      return known.includes(v) ? this.certVerdictMark(v) : humaniseKey(v)
    },
    openBulk () {
      this.bulkError = null
      // A previous open's expansion must not carry over into this one.
      this.bulkShown = null
      this.bulkOpen = true
    },
    async runBulk () {
      if (this.bulkSaving || !this.bulkReady.length) return
      // Guarded here as well as on the button: a bulk sign-off must never be
      // reachable for a role whose steps are signed one weld at a time.
      if (this.role !== 'vt') return
      this.bulkSaving = true
      this.bulkError = null
      try {
        const { data } = await hostAppApi.post(
          `${this.getHostRefApi}weld-mapping/bulk-sign`,
          {
            user_id: this.hostUserId,
            // VT only — assembly and welding stay per weld, see the button.
            action: 'vt',
            weld_ids: this.bulkReady.map(j => j.id),
            vt_level: this.vtLevel || null,
            // Decision D: the server refuses the whole batch if any row is not
            // in this unit. Only sent on a series drawing.
            ...(this.series && this.unitId ? { unit_id: this.unitId } : {})
          }
        )
        // Statuses come back per weld — the server derives them, so the list is
        // updated from the response rather than guessed here.
        ;(data.signed || []).forEach(sg => {
          const j = this.joints.find(x => x.id === sg.id)
          if (!j) return
          this.$set(j, 'status', sg.status)
          // The signature too: VT's step is keyed on it, not on the status.
          if (sg.vt !== undefined) this.$set(j, 'vt', sg.vt)
        })
        // Refetch so signatures, dates and batches on the touched rows are the
        // stored ones, not a local reconstruction.
        await this.loadJoints()
        this.bulkOpen = false
        this.advanceRoleIfDone()
        this.refreshSeriesCounts()
      } catch (e) {
        this.bulkError = this.serverMessage(e)
      } finally {
        this.bulkSaving = false
      }
    },
    // Reopen this role's registration form to correct what it recorded. The form
    // is already bound to fillerBatch / pfBatch / weldCertIds and seeded from the
    // joint by openWeld(), so all this has to do is unlock it and remember the
    // starting values.
    startEdit () {
      if (!this.canPatchField) return
      this.actError = null
      this.patchOrig = {
        batch_pf: String(this.pfBatch || ''),
        assembly_pf: this.assemblyPfIds.slice().sort().join(','),
        filler_batch: String(this.fillerBatch || ''),
        certificate_refs: this.weldCertIds.slice().sort().join(','),
        vt_level: String(this.vtLevel || '')
      }
      // Both the batch and the VT level sit behind their own "Change" button in
      // the normal forms; an edit is explicitly about changing them, so open
      // them rather than making the user tap twice.
      this.batchEdit = true
      if (this.role === 'vt') this.levelUnlocked = true
      this.editMode = true
    },
    cancelEdit () {
      // Re-seed from the joint so a cancelled edit leaves no typed value behind.
      const j = this.joints.find(x => x.id === this.selected)
      if (j) {
        this.fillerBatch = j.batch || ''
        this.pfBatch = j.batch_pf || ''
        this.weldCertIds = this.csvIds(j.certificate_refs)
        this.assemblyPfIds = this.csvIds(j.assembly_pf)
        this.vtLevel = j.vt_level || null
      }
      this.pfBatchTyping = false
      this.fillerBatchTyping = false
      this.levelUnlocked = false
      this.editMode = false
      this.patchOrig = null
      this.actError = null
      // The fields are back to what the joint holds, so nothing is pending any
      // more — re-baseline rather than leaving a stale "unsaved" flag behind.
      this.sheetBase = this.sheetSnapshot()
      this.unsavedHint = false
      // The material sheet is only reachable while editing here, so it must not
      // outlive the mode that opened it.
      this.matEdit = null
      this.matBase = null
    },
    // Saves each changed field through the patch endpoint, which writes that one
    // column and nothing else — so the signature, the weld date and the VT
    // verdict all survive a correction. Sequential on purpose: they touch the
    // same row, and a failure part-way must leave the earlier fields saved
    // rather than racing them.
    async savePatch () {
      if (!this.editMode || this.patchSaving) return
      const changed = this.patchChanged
      if (!changed.length) { this.cancelEdit(); return }
      this.patchSaving = true
      this.actError = null
      const j = this.joints.find(x => x.id === this.selected)
      try {
        for (const field of changed) {
          const value = {
            batch_pf: this.pfBatch,
            filler_batch: this.fillerBatch,
            assembly_pf: this.assemblyPfIds.join(', '),
            certificate_refs: this.weldCertIds.join(', '),
            vt_level: this.vtLevel
          }[field]
          const { data } = await hostAppApi.post(
            `${this.getHostRefApi}weld-mapping/weld/${this.selected}/patch-field`,
            { user_id: this.hostUserId, field, value: value || '' }
          )
          if (j) {
            // The joints list calls the weld batch `batch`; map it back rather
            // than writing a key the sheet does not read.
            this.$set(j, { filler_batch: 'batch' }[data.field] || data.field, data.value)
            if (data.field === 'batch_pf') this.$set(j, 'filler_batch', data.value)
            this.$set(j, 'status', data.weld_status)
            // Each patch returns the refreshed trail, so the history keeps up
            // with a multi-field correction without a reload.
            if (data.audit !== undefined) this.$set(j, 'material_audit', data.audit || [])
          }
        }
        this.editMode = false
        this.patchOrig = null
        // The level goes back behind its lock once the correction is stored, so
        // it reads the same as it does on a freshly opened joint.
        this.levelUnlocked = false
        // What was just stored is the new baseline. Without this the sheet keeps
        // comparing against the values it opened with, so a saved edit still
        // counts as unsaved: the scrim refuses to close and the "unsaved
        // changes" warning stays up for work that is already persisted.
        this.sheetBase = this.sheetSnapshot()
        this.unsavedHint = false
      } catch (e) {
        this.actError = this.serverMessage(e)
      } finally {
        this.patchSaving = false
      }
    },
    openTypeMenu () {
      this.typeMenuOpen = true
      this.typeActive = -1
    },
    closeTypeMenu () {
      this.typeMenuOpen = false
      this.typeActive = -1
    },
    // mousedown on an item fires before blur and closes the menu itself, so
    // blur only has to dismiss it when focus really left the field.
    onTypeBlur () {
      this.closeTypeMenu()
      // A typed (free-text) type is committed when the field is left.
      this.onTypeCommitted()
    },
    moveType (step) {
      if (!this.typeMenuOpen) { this.openTypeMenu(); return }
      const n = this.typeMatches.length
      if (!n) return
      const i = this.typeActive + step
      this.typeActive = i < 0 ? n - 1 : (i >= n ? 0 : i)
    },
    // Enter picks the highlighted suggestion; with none highlighted it leaves
    // the typed text alone so an off-register value can be entered.
    commitActiveType (e) {
      if (!this.typeMenuOpen || this.typeActive < 0) return
      e.preventDefault()
      this.commitType(this.typeMatches[this.typeActive])
    },
    commitType (val) {
      if (this.matEdit) {
        this.$set(this.matEdit, 'type', val)
        this.typePicked = val
        this.onTypeCommitted()
      }
      this.closeTypeMenu()
    },
    // Writes material_types on the weld row in the same shape the PC canvas
    // uses, so both views keep agreeing.
    // Duplicate a material row exactly as stored, blanks included.
    // WPS sheet: open in the PDF viewer, no download offered.
    previewWps () {
      const url = this.selectedJoint && this.selectedJoint.wps_url
      if (!url) return
      this.pdfTitle = `${this.$t('message.weldMapping.wps')} ${this.selectedJoint.wps || ''}`.trim()
      this.pdfLink = url
      this.pdfDownloadable = false
      this.showPdf = true
    },
    async copyMaterial (index) {
      if (this.matSaving || !this.canEditMaterials) return
      this.matSaving = true
      this.matError = null
      try {
        // Dedicated endpoint: it duplicates the STORED row, so a material with
        // no heat yet copies fine. Posting through the Add/Edit endpoint would
        // hit its required-field rules and refuse.
        const { data } = await hostAppApi.post(
          `${this.getHostRefApi}weld-mapping/weld/${this.selected}/material-copy`,
          { index, user_id: this.hostUserId }
        )
        this.applyMaterials(data.materials, data.audit, data.wps_warnings)
      } catch (e) {
        this.matError = this.serverMessage(e)
      } finally {
        this.matSaving = false
      }
    },
    async saveMaterial () {
      if (!this.matValid || this.matSaving) return
      this.matSaving = true
      this.matError = null
      try {
        const { data } = await hostAppApi.post(
          `${this.getHostRefApi}weld-mapping/weld/${this.selected}/material`,
          {
            index: this.matEdit.index === -1 ? null : this.matEdit.index,
            type: this.matEdit.type,
            heat: this.matEdit.heat,
            thickness: this.matEdit.thick,
            pos_nr: this.matEdit.pos_nr,
            // Attributes the audit entry; the server records it unattributed
            // rather than refusing the save if this is ever missing.
            user_id: this.hostUserId
          }
        )
        this.applyMaterials(data.materials, data.audit, data.wps_warnings)
        this.closeMaterial()
      } catch (e) {
        this.matError = this.serverMessage(e)
      } finally {
        this.matSaving = false
      }
    },
    async deleteMaterial () {
      if (this.matSaving || this.matEdit.index === -1) return
      // Mirrors the button's v-if: deletion stays a full-edit action.
      if (!this.canEditMaterials) return
      this.matSaving = true
      this.matError = null
      try {
        const { data } = await hostAppApi.post(
          `${this.getHostRefApi}weld-mapping/weld/${this.selected}/material-delete`,
          { index: this.matEdit.index, user_id: this.hostUserId }
        )
        this.applyMaterials(data.materials, data.audit, data.wps_warnings)
        this.closeMaterial()
      } catch (e) {
        this.matError = this.serverMessage(e)
      } finally {
        this.matSaving = false
      }
    },
    // Server response is authoritative; patch the row rather than re-fetching
    // the whole drawing.
    /**
     * Er tykkelsen utenfor WPS-ens gyldighetsområde? (5170/B1 — Erik: «Here we
     * can choose 12mm, when the WPS only states 40 mm».)
     *
     * Samme regel som serveren (FieldWeldService::wpsWarningsForWeldRow) og
     * «Ny sveis»: sveisen kan ha flere WPS-er, og er tykkelsen dekket av ÉN av
     * dem, er det ikke et avvik. Regnes lokalt fra wps_limits i skjøtelista,
     * så den svarer mens man skriver — også uten nett. Serveren regner den ut
     * på nytt ved lagring, og det er den som flagger for kontoret.
     */
    wpsThicknessWarning (thickness, joint) {
      const j = joint || this.selectedJoint
      const limits = (j && j.wps_limits) || []
      const t = parseFloat(thickness)
      if (!limits.length || isNaN(t)) return null
      const num = v => (v === null || v === undefined || v === '') ? null : Number(v)
      const covered = limits.some(l => {
        const lo = num(l.tmin); const hi = num(l.tmax)
        return !((lo !== null && t < lo) || (hi !== null && t > hi))
      })
      if (covered) return null
      const l = limits[0]
      return this.$t('message.weldMapping.field.warnThickness', {
        t,
        min: num(l.tmin) === null ? '?' : l.tmin,
        max: num(l.tmax) === null ? '?' : l.tmax
      })
    },
    applyMaterials (materials, audit, wpsWarnings) {
      const j = this.joints.find(x => x.id === this.selected)
      if (!j) return
      this.$set(j, 'materials', materials || [])
      // Serverens flagg for kontoret (samme kolonne som weben skriver), så
      // arket viser det samme som lerretet uten å laste tegningen på nytt.
      if (wpsWarnings !== undefined) this.$set(j, 'wps_warnings', wpsWarnings || [])
      // Every write returns the refreshed trail, so the history stays in step
      // with the rows without a second round trip. Undefined means the response
      // carried none (older server) — the existing trail is then left alone
      // rather than blanked.
      if (audit !== undefined) this.$set(j, 'material_audit', audit || [])
    },
    // Feltene brukeren kan endre i skjøtearket, som én sammenlignbar streng.
    sheetSnapshot () {
      return JSON.stringify([
        this.fillerBatch, this.pfBatch,
        this.assemblyPfIds.slice().sort(), this.weldCertIds.slice().sort(),
        this.rejectReason, this.rejectNote, this.vtLevel
      ])
    },
    // Har brukeren skrevet eller valgt noe som ikke er lagret ennå?
    sheetDirty () {
      return this.sheetBase !== null && this.sheetSnapshot() !== this.sheetBase
    },
    /**
     * Klikk på bakgrunnen ved siden av arket.
     *
     * Et åpent sertifikat-nedtrekk skal bare lukkes — ikke ta hele arket med
     * seg; ellers mister feltbrukeren valget sitt ved å trykke «utenfor»
     * lista. Og har hen fylt ut noe usanset, blir arket stående: da må
     * Lukk-knappen brukes, så et bomklikk på tegningen ikke sletter arbeidet.
     */
    scrimClose () {
      if (this.pfOpen || this.certOpen) {
        this.pfOpen = false
        this.certOpen = false
        return
      }
      if (this.sheetDirty()) {
        this.unsavedHint = true
        return
      }
      this.closeWeld()
    },
    closeWeld () {
      this.selected = null
      this.sheetBase = null
      this.unsavedHint = false
      this.editMode = false
      this.patchOrig = null
    },
    // ── Production series ───────────────────────────────────────────────
    // The drawing's series + unit list. Fail-soft: any error, or mode 'none',
    // leaves `series` null and the screen is today's screen.
    async loadSeries () {
      const id = this.$route.params.drawing_id
      let data = null
      try {
        ({ data } = await hostAppApi.get(`${this.getHostRefApi}project-drawing/${id}/weld-mapping-series`))
      } catch (e) {
        data = null
      }
      // Paged to another drawing while this was in flight.
      if (String(id) !== String(this.$route.params.drawing_id)) return
      if (!data || data.mode !== 'unit' || !data.series || !(data.units || []).length) {
        this.series = null
        this.seriesUnits = []
        this.seriesSummary = null
        this.unitId = null
        this.syncUnitQuery()
        return
      }
      this.series = data.series
      this.seriesUnits = data.units
      this.seriesSummary = data.summary || null
      this.unitId = this.pickUnitId(id)
      writeUnit(id, this.unitId)
      this.syncUnitQuery()
    },
    // Decision C: the URL wins, then this device's last unit for the drawing,
    // then the unit at position 1 (the server lists units by position).
    pickUnitId (drawingId) {
      const ids = this.seriesUnits.map(u => String(u.id))
      const fromUrl = this.$route.query.unit_id || this.$route.query.unit
      if (fromUrl !== undefined && fromUrl !== null && ids.includes(String(fromUrl))) return Number(fromUrl)
      const stored = readUnit(drawingId)
      if (stored !== null && ids.includes(String(stored))) return Number(stored)
      return this.seriesUnits[0].id
    },
    // Mirror the selection into ?unit_id= without adding a history entry.
    syncUnitQuery () {
      if (String(this.$route.query.unit_id || '') === String(this.unitId || '')) return
      const query = { ...this.$route.query }
      if (this.unitId) query.unit_id = String(this.unitId)
      else delete query.unit_id
      // Kom vi hit fra seriekortets ?unit=, speiles valget til én skrivemåte.
      delete query.unit
      this.$router.replace({ query }).catch(() => {})
    },
    onUnitChange (value) {
      const id = Number(value)
      if (!id || id === this.unitId) return
      this.unitId = id
      writeUnit(this.$route.params.drawing_id, id)
      this.closeWeld()
      this.syncUnitQuery()
      this.loadJoints()
    },
    // After a registration the summary strip and the picker's counts are
    // stale; refresh them without touching the selection.
    async refreshSeriesCounts () {
      if (!this.series) return
      const id = this.$route.params.drawing_id
      try {
        const { data } = await hostAppApi.get(`${this.getHostRefApi}project-drawing/${id}/weld-mapping-series`)
        if (String(id) !== String(this.$route.params.drawing_id) || !data || data.mode !== 'unit') return
        this.series = data.series
        this.seriesUnits = data.units || []
        this.seriesSummary = data.summary || null
      } catch (e) {
        // Counts stay as they were; the registration itself already succeeded.
      }
    },
    unitOptionText (u) {
      const c = u.status_counts || {}
      const done = (u.weld_count || 0) - (c.MAPPED || 0)
      return (u.label || '') +
        (u.retained ? this.$t('message.productionSeries.unitRetainedSuffix') : '') +
        ' — ' + this.$t('message.productionSeries.markerDone', { done, total: u.weld_count || 0 })
    },
    async loadJoints () {
      this.jointsError = null
      this.jointsLoading = true
      const seq = ++this.jointsSeq
      try {
        const id = this.$route.params.drawing_id
        // user_id is what the document download route authenticates on, so the
        // photo URLs it builds are only usable when it is sent.
        const params = { user_id: this.hostUserId }
        // One unit only (Decision A): the server filters, never this screen.
        if (this.series && this.unitId) params.unit_id = this.unitId
        const { data } = await hostAppApi.get(
          `${this.getHostRefApi}project-drawing/${id}/weld-mapping-joints`,
          { params }
        )
        if (seq !== this.jointsSeq) return
        // WIT-IMPL-M101-PWA-001: drawing-level NDT profile (null when absent).
        this.ndtProfile = (data && data.ndt_profile) || null
        this.joints = ((data && data.joints) || []).concat(
          // Registrations still queued offline for this drawing stay on the map.
          this.outbox
            .filter(o => String(o.drawingId) === String(id))
            // An entry queued on another unit belongs there, not here. Entries
            // queued before units existed carry none and show everywhere.
            .filter(o => !this.series || !o.payload || o.payload.unit_id === undefined || String(o.payload.unit_id) === String(this.unitId))
            .map(o => this.localJoint(o))
        )
        this.fieldPerm = (data && data.field_weld) ? {
          can_register: !!data.field_weld.can_register, can_review: !!data.field_weld.can_review
        } : this.fieldPerm
        this.vtLevelEditable = !!(data && data.vt_level_editable)
        this.usesPosNr = !(data && data.uses_pos_nr === false)
        this.certificates = (data && data.certificates) || []
        this.certificatesScope = (data && data.certificates_scope) === 'project' ? 'project' : 'own'
        // WIT-FIX-PWA-WELDERCERT-001: 'no_certificates' = the welder holds no competence record at all (P5).
        this.certificatesOwnState = (data && data.certificates_own_state) || null
        this.signer = (data && data.signer) || null
        this.projectBatches = (data && data.project_batches) || []
        this.drawingFile = (data && data.drawing_file) || null
        // Measurements, notes, QR and the as-built stamp the planner drew on
        // the PC canvas. Null on older API builds, which the canvas treats as
        // "nothing placed" and draws the weld markers alone, as before.
        this.overlay = (data && data.overlay) || null
        // Older API builds do not send this; assume assembly is on there, which
        // is the behaviour this screen has always had.
        this.compilation = !data || data.compilation === undefined
          ? true
          : !!data.compilation
        // Paging to a drawing that does not run assembly while the fitter tab
        // was open would leave `role` pointing at a tab that no longer exists.
        if (!this.compilation && this.role === 'fitter') this.role = 'welder'
        // Åpne på trinnet tegningen faktisk står på. Bare før brukeren har
        // valgt fane selv — ellers ville en henting kastet hen ut av fanen
        // hen nettopp trykket på.
        if (!this.rolePicked) {
          const r = this.defaultRole()
          if (r) this.role = r
        }
        this.selected = null
        // The canvas refits and clears its own error state when either input
        // changes, so nothing here has to reach into it.
      } catch (e) {
        if (seq !== this.jointsSeq) return
        this.joints = []
        this.drawingFile = null
        this.overlay = null
        this.jointsError = this.serverMessage(e)
      } finally {
        if (seq === this.jointsSeq) this.jointsLoading = false
      }
    },
    async load () {
      this.loadError = null
      this.loading = true
      // The series decides which unit the joints are loaded for, so it goes
      // first; with no series it resolves to null and the joints load as today.
      this.jointsLoading = true
      this.loadSeries().then(() => this.loadJoints())
      try {
        const id = this.$route.params.drawing_id
        const { data } = await hostAppApi.get(`${this.getHostRefApi}project-drawing/${id}/weld-mapping-header`)
        this.drawing = {
          id: data.id,
          number: data.number || '',
          name: data.name || '',
          parent: data.parent || null,
          siblings: data.siblings || null
        }
      } catch (e) {
        this.loadError = this.serverMessage(e)
      } finally {
        this.loading = false
      }
    },
    step (id) {
      if (!id) return
      this.$router.push({
        name: 'weld-mapping-drawing',
        params: { project_id: this.$route.params.project_id, drawing_id: id }
      })
    },
    goBack () {
      this.$router.push({
        name: 'advanced-weld-log_weld-mapping',
        params: { project_id: this.$route.params.project_id }
      })
    }
  }
}
</script>
